import { toKML } from '@placemarkio/tokml';
import { setDefaultOptions, loadModules } from 'esri-loader';
import JSZip from 'jszip';
import proj4 from 'proj4';
import shpwrite from 'shp-write-update';
import * as toGeoJSON from '@tmcw/togeojson';

import api from '../api';
import VueInst from '../main';
import Vue from 'vue';
import i18n from '@/plugins/i18n.plugin';

import constants from '@/helpers/constants';
import DateService, { FMT, DayjsUnit } from '@/helpers/DateService';
import * as symbology from './GraphicsSimbology';
import { buildTickPaths } from './PerimetroHelper';
import { sanitizeFileName, handleBlobDownload, downloadBlob } from './Utils';

import store from '@/store/index';
import { resolveIconoUrl } from './IconoHelper';
import { buildPerimetroLegendItem, buildPlanOperacionesLegendItem } from './CaptureLegendHelper';
import { buildLayers, updateEmergenciesLayer, deleteLayer } from '@/helpers/map/arcgis-layers';
import {
  buildGetFeatureInfoUrl,
  parseFeatureInfoResponse,
  isPointGeometryType,
  geoJsonToEsriJson,
} from '@/helpers/popup/WmsFeatureInfo';

setDefaultOptions({ css: true });

/**
 * Global variables
 */
let map;
let mapView;
let mapViewPlanOperaciones;
let overviewMap;
let mapViewOverview;

// #region MAP
let ExaggeratedElevationLayerSubClass;
let elevationLayer;
export const createMap = async () => {
  try {
    const [Map, WebMap] = await loadModules(['esri/Map', 'esri/WebMap']);

    await initExaggeratedElevationLayerSubClass();
    elevationLayer = new ExaggeratedElevationLayerSubClass();
    map = new WebMap({
      ground: {
        layers: [elevationLayer],
      },
      basemap: 'satellite',
    });
    await addHybridReferenceLayer();

    overviewMap = new Map({
      ground: {
        layers: [elevationLayer],
      },
      basemap: 'gray-vector',
    });

    await registerToken();
    await use2D();

    await setSearchWidget();
    await compassWidget();
    await legendWidget();

    redrawInfraestructuras();

    mapView.when(() => {
      store.dispatch('map/setMapLoading', false);
      if (gotoRecurso) {
        setCenterMap(gotoRecurso);
      }
    });
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
};

let draw;
export const drawPolygonAreaToTrackMedios = async () => {
  try {
    const [Draw] = await loadModules(['esri/views/draw/Draw']);
    const [Point] = await loadModules(['esri/geometry/Point']);
    const [projection] = await loadModules(['esri/geometry/projection']);
    const [Polygon] = await loadModules(['esri/geometry/Polygon']);

    let poligono = null;

    const layer = map.findLayerById('layer-drawPolygon');

    if (!draw) {
      // Se inicializa si no lo está ya
      draw = new Draw({
        view: mapView,
      });
    }
    const action = draw.create('polygon');

    return new Promise((resolve, _reject) => {
      // PolygonDrawAction.vertex-add
      // Fires when user clicks, or presses the "F" key.
      // Can also be triggered when the "R" key is pressed to redo.
      action.on('vertex-add', async (evt) => {
        const lastVertex = evt.vertices[evt.vertexIndex];
        const point = new Point({
          x: lastVertex[0],
          y: lastVertex[1],
        });

        if (!poligono) {
          poligono = new Polygon({
            spatialReference: {
              wkid: 3857,
            },
          });
          poligono.addRing([point, point]);
        } else {
          const ringLength = poligono.rings[0].length;
          poligono.insertPoint(0, ringLength - 1, point);
        }
        const poligonoGrafico = await createPolygonGraphic(evt.vertices);
        layer.graphics.removeAll();
        layer.graphics.push(poligonoGrafico);
      });

      // Fires when the pointer moves over the view
      action.on('cursor-update', async (evt) => {
        const poligonoGrafico = await createPolygonGraphic(evt.vertices);
        layer.graphics.removeAll();
        layer.graphics.push(poligonoGrafico);
      });

      // Add a graphic representing the completed polygon
      // when user double-clicks on the view or presses the "C" key
      action.on('draw-complete', async () => {
        const geometry4326 = projection.project(poligono, { wkid: 4326 });
        let coordenadasPoligono = '(';

        for (let i = 0; i < geometry4326.rings[0].length; i++) {
          const coordenadas = String(geometry4326.rings[0][i]).split(',');
          if (i === geometry4326.rings[0].length - 1) {
            coordenadasPoligono += `${coordenadas[0]} ${coordenadas[1]})`;
          } else {
            coordenadasPoligono += `${coordenadas[0]} ${coordenadas[1]}, `;
          }
        }

        mapView.goTo(poligono, {
          duration: 3000,
          easing: 'in-expo',
        });
        layer.graphics.removeAll();
        resolve(coordenadasPoligono);
      });
    });
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
};
export function cancelDrawingPolygon() {
  const layer = map.findLayerById('layer-drawPolygon');
  if (draw) {
    draw.reset();
    layer.graphics.removeAll();
  }
}

async function createPolygonGraphic(vertices) {
  try {
    const [Polygon, Graphic] = await loadModules(['esri/geometry/Polygon', 'esri/Graphic']);
    const geometry = new Polygon({
      hasZ: false,
      hasM: true,
      rings: vertices,
      spatialReference: { wkid: 3857 },
    });

    const poligonoGraphic = new Graphic({
      geometry,
      symbol: {
        type: 'simple-fill', // autocasts as SimpleFillSymbol
        color: 'purple',
        style: 'solid',
        outline: {
          // autocasts as SimpleLineSymbol
          color: 'white',
          width: 1,
        },
      },
    });

    return poligonoGraphic;
  } catch (err) {
    VueInst.$log.error(err);
  }
}
async function initExaggeratedElevationLayerSubClass() {
  try {
    const [ElevationLayer, BaseElevationLayer] = await loadModules([
      'esri/layers/ElevationLayer',
      'esri/layers/BaseElevationLayer',
    ]);

    ExaggeratedElevationLayerSubClass = BaseElevationLayer.createSubclass({
      properties: {
        id: 'elevation-layer',
        exaggeration: 1,
      },

      // The load() method is called when the layer is added to the map prior to it being rendered in the view.
      load() {
        this._elevation = new ElevationLayer({
          id: 'elevation-layer',
          url: 'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
        });

        // wait for the elevation layer to load before resolving load()
        this.addResolvingPromise(
          this._elevation.load().then(() => {
            // get tileInfo, spatialReference and fullExtent from the elevation service
            // this is required for elevation services with a custom spatialReference
            this.tileInfo = this._elevation.tileInfo;
            this.spatialReference = this._elevation.spatialReference;
            this.fullExtent = this._elevation.fullExtent;
          })
        );

        return this;
      },

      // Fetches the tile(s) visible in the view
      fetchTile(level, row, col, options) {
        // calls fetchTile() on the elevationlayer for the tiles
        // visible in the view
        return this._elevation.fetchTile(level, row, col, options).then((data) => {
          const exaggeration = this.exaggeration;
          // `data` is an object that contains the
          // the width and the height of the tile in pixels,
          // and the values of each pixel
          for (let i = 0; i < data.values.length; i++) {
            // Multiply the given pixel value
            // by the exaggeration value
            data.values[i] = data.values[i] * exaggeration;
          }

          return data;
        });
      },
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
}

let viewSwitchInFlight = Promise.resolve();

function enqueueViewSwitch(switchView) {
  viewSwitchInFlight = viewSwitchInFlight.then(switchView);
  return viewSwitchInFlight;
}

export const use2D = () => enqueueViewSwitch(switchTo2DView);

async function switchTo2DView() {
  // Crea el viewMap 2d
  try {
    const [MapView] = await loadModules(['esri/views/MapView']);

    store.dispatch('map/setMap3D', false);
    const viewSwitch = releaseCurrentMapViewPerimeters();
    await updatePerimetroSymbology();

    const newMapView = new MapView({
      map,
      container: 'mapNode',
      constraints: {
        rotationEnabled: false,
      },
      popupEnabled: true,
    });

    newMapView.ui.remove('zoom');

    newMapView.ui.padding = { top: 70, left: 0, right: 15, bottom: 24 };

    mapViewOverview = new MapView({
      container: 'overviewDiv',
      map: overviewMap,
      constraints: {
        rotationEnabled: false,
        maxScale: 2311162.2171545, // 4622324.434309
      },
    });

    mapViewOverview.ui.components = [];

    mapViewOverview.when(() => {
      mapView.when(() => {
        setup();
      });
    });

    await onMapViewChange(newMapView, viewSwitch);
  } catch (err) {
    VueInst.$log.error(err);
  }
}

async function setup() {
  const [Graphic, reactiveUtils, promiseUtils] = await loadModules([
    'esri/Graphic',
    'esri/core/reactiveUtils',
    'esri/core/promiseUtils',
  ]);

  const extent3Dgraphic = new Graphic({
    geometry: null,
    symbol: {
      type: 'simple-fill',
      color: [0, 0, 0, 0.5],
      outline: null,
    },
  });
  mapViewOverview.graphics.add(extent3Dgraphic);

  const extentDebouncer = promiseUtils.debounce(async () => {
    if (mapView.stationary) {
      await mapViewOverview.goTo({
        center: mapView.center,
        scale:
          mapView.scale * 2 * Math.max(mapView.width / mapViewOverview.width, mapView.height / mapViewOverview.height),
      });
    }
  });

  reactiveUtils.watch(
    () => mapView.extent,
    (extent) => {
      // Sync the overview map location
      // whenever the 3d view is stationary
      extent3Dgraphic.geometry = extent;
    },
    {
      initial: true,
    }
  );

  mapView.watch('stationary', (isStationary) => {
    if (isStationary) {
      extentDebouncer();
    }
  });
}

export const getCaptureMapOverview = async (layer) => {
  return await getCaptureMapFeatures(mapViewOverview.graphics, layer);
};

export const measureOverview = async () => {
  return await measurePerimetros(mapViewOverview.graphics);
};

export const use3D = () => enqueueViewSwitch(switchTo3DView);

async function switchTo3DView() {
  try {
    const [SceneView] = await loadModules(['esri/views/SceneView']);

    closeMapPopups(); // Si no se deseleccionan los graficos antes de cambiar de mapview, da error

    store.dispatch('map/setMap3D', true);
    const viewSwitch = releaseCurrentMapViewPerimeters();
    await updatePerimetroSymbology();

    const newMapView = new SceneView({
      map,
      container: 'mapNode',
      constraints: {
        rotationEnabled: false,
      },
    });

    // newMapView.ui.components = (['attribution'])
    const navigation = newMapView.ui.find('navigation-toggle');
    newMapView.ui.remove('zoom');

    newMapView.ui.move(navigation, 'top-right');
    // newMapView.ui.move(compass, 'top-right')
    newMapView.ui.remove('compass');
    newMapView.ui.remove('legend');

    // newMapView.ui.padding = { top: 378, left: 0, right: 60, bottom: 24 }

    await onMapViewChange(newMapView, viewSwitch);
  } catch (err) {
    VueInst.$log.error(err);
  }
}

function releasePreviousMapView(previous) {
  if (!previous) return;

  try {
    previous.popup?.close();
    previous.container = null;
  } catch (err) {
    VueInst.$log.error(err);
  }

  draw = null;
}

function releaseCurrentMapViewPerimeters() {
  const previous = mapView;
  const extent = previous?.extent ? previous.extent : store.getters['map/paramsComunidad'].EXTENT;

  const hadActivePerimeterSVM = perimeterSVM !== null;
  if (hadActivePerimeterSVM) {
    destroyPerimeterSVM();
  }

  releasePreviousMapView(previous);

  return { extent, hadActivePerimeterSVM };
}

async function onMapViewChange(newMapView, { extent, hadActivePerimeterSVM }) {
  mapView = newMapView;

  await setExtentMap(extent);

  clearMeasurement();

  await setSearchWidget();

  await compassWidget();
  updateWidgetView();

  await updateRendererViento();

  assignMapViewEvents();

  if (hadActivePerimeterSVM) {
    await createPerimetroSketchViewModel();
  }
}

export const getMapView = () => {
  return mapView;
};

export const getMapViewOverviewGraphics = () => {
  return mapViewOverview.graphics;
};

export const createMarcoCapturaGraphic = async (params) => {
  const [Graphic, Extent] = await loadModules([
    'esri/Graphic',
    'esri/geometry/Extent',
    'esri/views/MapView',
    'esri/core/reactiveUtils',
  ]);

  const extent = new Extent({
    xmin: params.xmin,
    ymin: params.ymin,
    xmax: params.xmax,
    ymax: params.ymax,
    spatialReference: { wkid: 4326 },
  });

  const graphic = new Graphic({
    geometry: extent,
    symbol: {
      type: 'simple-fill',
      color: [0, 0, 0, 0.5],
      outline: null,
    },
  });

  return graphic;
};

export const establecerEscala = (nuevaEscala) => {
  mapView.constraints = {
    snapToZoom: false,
  };
  mapView
    .goTo(
      {
        scale: nuevaEscala,
      },
      {
        duration: 1000, // Ajusta la duración a 2 segundos (2000 ms)
        easing: 'ease-in-out', // Puedes experimentar con diferentes curvas de aceleración
      }
    )
    .catch((error) => {
      // console.error('Error ajustando la escala:', error)
      VueInst.$log.error('Error ajustando la escala:', error);
    });
};

export const capturarPantalla = async (frameBounds) => {
  const options = {
    width: frameBounds.width * 5,
    height: frameBounds.height * 5,
    area: {
      x: frameBounds.x,
      y: frameBounds.y,
      width: frameBounds.width,
      height: frameBounds.height,
    },
  };

  try {
    const screenshot = await mapView.takeScreenshot(options);
    return screenshot.dataUrl;
  } catch (error) {
    console.error('Error al capturar la pantalla:', error);
    VueInst.$log.error('Error al capturar la pantalla:', error);
    throw error;
  }
};

export const getMap = () => {
  return map;
};
// #endregion

// #region MAPVIEW EVENTS
// --- Spiderfy recursos (cuando se solapan) ---
const SPIDERFY_ICONS_LAYER_ID = 'layer-recursos-spiderfy';
const SPIDERFY_LINKS_LAYER_ID = 'layer-recursos-spiderfy-links';

let spiderfyIconsLayer = null;
let spiderfyLinksLayer = null;
let dataExpresion = null;
async function ensureSpiderfyLayers() {
  if (spiderfyIconsLayer && spiderfyLinksLayer) return;

  const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);

  spiderfyLinksLayer = new GraphicsLayer({
    id: SPIDERFY_LINKS_LAYER_ID,
    title: 'Links recursos (spiderfy)',
    visible: true,
    opacity: 1,
    listMode: 'hide',
  });

  spiderfyIconsLayer = new GraphicsLayer({
    id: SPIDERFY_ICONS_LAYER_ID,
    title: 'Recursos (spiderfy)',
    visible: true,
    opacity: 1,
    listMode: 'hide',
  });

  map.addMany([spiderfyLinksLayer, spiderfyIconsLayer]);

  // Asegurarlas arriba del todo (links debajo de iconos)
  map.reorder(spiderfyLinksLayer, map.layers.length - 2);
  map.reorder(spiderfyIconsLayer, map.layers.length - 1);
}

function clearSpiderfy() {
  spiderfyIconsLayer?.removeAll();
  spiderfyLinksLayer?.removeAll();
  if (!dataExpresion) return;
  store.dispatch('layer/setDefinitionExpression', {
    id: 'layer-recursos',
    definitionExpression: dataExpresion,
    reset: false,
  });
  dataExpresion = null;
}

async function spiderfyRecursos(clickEvent, recursoGraphics) {
  await ensureSpiderfyLayers();
  clearSpiderfy();

  const [Graphic, Polyline] = await loadModules(['esri/Graphic', 'esri/geometry/Polyline']);

  const center = clickEvent.mapPoint;
  const n = recursoGraphics.length;

  const radius = Math.max(44, Math.min(110, 28 + n * 7));
  const startAngle = -Math.PI / 2; // empieza arriba

  const mainGraphic = new Graphic({
    symbol: {
      type: 'simple-marker',
      style: 'circle',
      color: [255, 255, 255, 0.5],
      size: '20px',
    },
    geometry: center,
  });

  spiderfyLinksLayer.add(mainGraphic);

  for (let i = 0; i < n; i++) {
    const angle = startAngle + (2 * Math.PI * i) / n;
    const dx = radius * Math.cos(angle);
    const dy = radius * Math.sin(angle);

    const dest = mapView.toMap({ x: clickEvent.x + dx, y: clickEvent.y + dy });

    // Línea centro -> icono
    const link = new Graphic({
      geometry: new Polyline({
        paths: [
          [
            [center.x, center.y],
            [dest.x, dest.y],
          ],
        ],
        spatialReference: center.spatialReference,
      }),
      symbol: {
        type: 'simple-line',
        width: 1,
        color: [80, 80, 80, 160],
      },
    });
    spiderfyLinksLayer.add(link);

    const original = recursoGraphics[i];
    const attrs = JSON.parse(JSON.stringify(original.attributes || {}));

    const haloSymbol = {
      type: 'simple-marker',
      style: 'circle',
      color: original.attributes.COLOR,
      size: '35px',
      outline: {
        color: [0, 0, 0, 0],
        width: 0,
      },
    };

    const haloGraphic = new Graphic({
      geometry: dest,
      symbol: haloSymbol,
    });

    const iconGraphic = new Graphic({
      geometry: dest,
      symbol: {
        type: 'picture-marker',
        url: resolveIconoUrl(original.attributes.ICON),
        width: '38.5px',
        height: '38.5px',
      },
      attributes: attrs,
    });

    spiderfyIconsLayer.addMany([haloGraphic, iconGraphic]);
  }

  // dataExpresion =
  //   " AND ID_GEO_DISPOSITIVO NOT IN (" +
  //   recursoGraphics.map((r) => `'${r.attributes.ID_GEO_DISPOSITIVO}'`) +
  //   ") ";
  // store.dispatch("layer/setDefinitionExpression", {
  //   id: "layer-recursos",
  //   definitionExpression: dataExpresion,
  //   reset: false,
  // });
}

let graphicHighlight = null;
const trackHighlight = [];

function assignMapViewEvents() {
  // Evento Emit ChangeExtent (Compartir mapa)
  let pointerDown = false;

  mapView.on('layerview-create-error', revertBasemapOnLayerViewError);

  mapView.watch('extent', (event) => {
    if (event) {
      store.dispatch('shareMap/changeMapExtent', {
        xmax: event.xmax,
        xmin: event.xmin,
        ymax: event.ymax,
        ymin: event.ymin,
        wkid: event.spatialReference.wkid,
        viewpoint: mapView.viewpoint,
      });
    }
  });

  // Scale
  mapView.watch('scale', (event) => {
    VueInst.$eventHub.$emit('escalaMap', event);
    // clearSpiderfy()
    // closeMapPopups()
  });

  // Move mouse -> update coordinates
  mapView.on('pointer-move', (event) => {
    if (!pointerDown) {
      sendCoordinates(mapView.toMap(event));
    }
  });

  mapView.on('click', (event) => {
    // Un nuevo click invalida cualquier GetFeatureInfo WMS pendiente (evita popups obsoletos)
    const wmsClickId = cancelPendingWmsFeatureInfo();

    // Enviar coordenadas para representarlas en el MapaWeb
    sendCoordinates(mapView.toMap(event));

    // Right click -> Context Menu
    if (event.button !== 0) {
      const position = {
        x: event.x,
        y: event.y,
      };
      const attributes = {};
      attributes.X = event.mapPoint.x;
      attributes.Y = event.mapPoint.y;
      attributes.LATITUDE = event.mapPoint.latitude;
      attributes.LONGITUDE = event.mapPoint.longitude;
      VueInst.$eventHub.$emit('showContextMenu', {
        attributes,
        position,
      });
    }

    // Click -> tooltip layer
    mapView
      .hitTest(event)
      .then(async (response) => {
        if (event.button === 0) {
          const spiderHit = response.results.find((r) => r.graphic?.layer?.id === SPIDERFY_ICONS_LAYER_ID);

          if (spiderHit?.graphic) {
            const graphic = spiderHit.graphic;

            // Highlight del icono spiderfy
            mapView.whenLayerView(graphic.layer).then((layerView) => {
              if (graphicHighlight) {
                graphicHighlight.remove();
                graphicHighlight = null;
              }
              graphicHighlight = layerView.highlight(graphic);
            });

            const position = { x: event.x, y: event.y + 3 };
            const attributes = JSON.parse(JSON.stringify(graphic.attributes || {}));

            // Enviamos el popup como si fuera layer-recursos para reutilizar tu UI
            VueInst.$eventHub.$emit('showPopup', {
              layer: 'layer-recursos',
              attributes,
              position,
            });
            return;
          }

          const spiderLinkHit = response.results.find((r) => r.graphic?.layer?.id === SPIDERFY_LINKS_LAYER_ID);
          if (spiderLinkHit) {
            clearSpiderfy();
          }

          const recursoGraphics = response.results
            .filter((r) => r.graphic?.layer?.id === 'layer-recursos')
            .map((r) => r.graphic);

          if (recursoGraphics.length > 1) {
            await spiderfyRecursos(event, recursoGraphics);

            // Dejamos que el usuario elija uno
            VueInst.$eventHub.$emit('showPopup', false);
            return;
          }
        }

        const showCustomTooltip = false;
        let showSimpleTooltip = false;
        let layerId;
        // Calculamos los IDs del panel una vez por click (EC-07: solo capas visibles)
        const panelLayerIds = collectPanelLayerIds(store.getters['layer/layersPanel']);
        const graphicsFilter = response.results.filter((x) => {
          if (event.button === 0) {
            if (!x.graphic || !x.graphic.layer) {
              return false;
            }
            const layer = x.graphic.layer;
            if (layer) {
              layerId = layer.id;

              // Capas con tooltip custom
              const layersCustomTooltip = ['layer-recursos', 'layer-track', 'layer-emergencias'];
              let showCustomTooltip = layersCustomTooltip.includes(layerId);
              if (layerId === 'layer-track' && x.graphic.symbol.type === 'simple-line') {
                showCustomTooltip = false;
              }

              if (showCustomTooltip) {
                return true;
              }

              const layersWithPopup = [
                'layer-autobomba',
                'layer-bomberos',
                'layer-BRIF',
                'layer-central-comunicaciones',
                'layer-repetidor-comunicaciones',
                'layer-reten',
                'layer-SOS',
                'layer-torreta-vigilancia',
                'layer-estaciones-meteorologicas',
                'layer-punto-agua-aer',
                'layer-punto-agua-ter',
                'layer-punto-encuentro',
                'layer-montes-utilidad',
                'layer-emergencias',
                'layer-imagen-satelite',
                'layer-infraestructuras-propias',
                'layer-perimetros',
              ];

              const layersWithoutPopup = ['layer-contorno'];
              if (layersWithoutPopup.includes(layerId)) {
                return false;
              }

              showSimpleTooltip = layersWithPopup.includes(layerId);

              if (!showCustomTooltip && !showSimpleTooltip) {
                // Capas del panel (showPanel: true) — excluidas las operacionales que pasan arriba
                const isPanelLayer =
                  panelLayerIds.has(layerId) &&
                  !layersCustomTooltip.includes(layerId) &&
                  !layersWithPopup.includes(layerId) &&
                  layer.visible === true;
                if (isPanelLayer) return true;
              }
            }

            return showSimpleTooltip;
          }
        });

        if (graphicsFilter.length > 0) {
          let graphic = graphicsFilter[0].graphic;
          if (layerId === 'layer-perimetros' && graphicsFilter.length > 1) {
            const smallestAreaObject = graphicsFilter.reduce((min, current) => {
              return current.graphic.attributes.AREA < min.graphic.attributes.AREA ? current : min;
            });
            const position = graphicsFilter.indexOf(smallestAreaObject);
            graphic = graphicsFilter[position].graphic;
          }

          const highlightGraphic = [];
          highlightGraphic.push(graphic);

          if (layerId === 'layer-contorno' || layerId === 'layer-track') {
            const layer = map.findLayerById('layer-track');
            for (const i of layer.graphics) {
              if (i.attributes.ID_RECURSO === graphic.attributes.ID_RECURSO) {
                highlightGraphic.push(i);
              }
            }
          }

          mapView.whenLayerView(graphic.layer).then((layerView) => {
            for (const i of highlightGraphic) {
              graphicHighlight = layerView.highlight(i);
              trackHighlight.push(graphicHighlight);
            }
          });

          const position = { x: event.x, y: event.y + 3 };
          const attributes = JSON.parse(JSON.stringify(graphic.attributes));

          if (showCustomTooltip) {
            attributes.LATITUDE = graphic.geometry.latitude;
            attributes.LONGITUDE = graphic.geometry.longitude;
          }

          if (attributes) {
            VueInst.$eventHub.$emit('showPopup', {
              layer: graphic.layer.id,
              attributes,
              geometry: graphic.geometry,
              position,
            });
          }
        } else if (event.button === 0) {
          // Sin gráficos con popup: probar las capas WMS con popupEnabled (GetFeatureInfo)
          await showWmsFeatureInfoPopup(event, wmsClickId);
        }
      })
      .catch((err) => {
        VueInst.$log.error(err);
      });
  });

  mapView.on('pointer-up', () => {
    pointerDown = false;
  });
  mapView.on('pointer-down', () => {
    pointerDown = true;
    closeMapPopups();
  });
}

export async function isActivo(idDispositivo) {
  if (!map || !mapView) {
    return null;
  }

  const { feature } = await getRecursoFeature(idDispositivo);
  return feature?.attributes.ACTIVO === 1;
}

export const highlightSearch = async (idDispositivo) => {
  const { layerView, feature } = await getRecursoFeature(idDispositivo, true);

  if (!feature) {
    return;
  }

  graphicHighlight = layerView.highlight(feature);
};

const getRecursoFeature = async (idDispositivo, returnGeometry = false) => {
  if (!idDispositivo || !map || !mapView) {
    return { layerView: null, feature: null };
  }

  const layer = map.findLayerById('layer-recursos');
  const layerView = await mapView.whenLayerView(layer);

  const result = await layer.queryFeatures({
    where: `UPPER(ID_GEO_DISPOSITIVO) = '${idDispositivo.toUpperCase()}'`,
    outFields: ['*'],
    returnGeometry,
  });

  return {
    layerView,
    feature: result.features[0] ?? null,
  };
};

function closeMapPopups() {
  // Cierra el popup / context menu y deselecciona el grafico seleccionado
  cancelPendingWmsFeatureInfo();
  VueInst.$eventHub.$emit('showPopup', false);
  VueInst.$eventHub.$emit('showContextMenu', false);

  if (graphicHighlight) {
    graphicHighlight.remove();
    graphicHighlight = null;
  }

  if (trackHighlight.length > 0) {
    for (let i = 0; i < trackHighlight.length; i++) {
      trackHighlight[i].remove();
    }
  }
}
function sendCoordinates(point) {
  if (point) {
    const X = point.x.toFixed(2);
    const Y = point.y.toFixed(2);
    const LAT = point.latitude.toFixed(4).toString().replace('.', ',');
    const LON = point.longitude.toFixed(4).toString().replace('.', ',');
    const LATGMS = convertirLatLonGMS(point.latitude, 'LATITUDE').valor;
    const LONGMS = convertirLatLonGMS(point.longitude, 'LONGITUDE').valor;
    const ETRS89 = convertirETRS89(X, Y);
    const X_ETRS89 = ETRS89[0].toFixed(0).toString().replace('.', ',');
    const Y_ETRS89 = ETRS89[1].toFixed(0).toString().replace('.', ',');

    const coordenadas = {
      X,
      Y,
      LATITUDE: LAT,
      LONGITUDE: LON,
      LATITUDE_GMS: LATGMS,
      LONGITUDE_GMS: LONGMS,
      X_ETRS89,
      Y_ETRS89,
    };
    VueInst.$eventHub.$emit('coordenadasMap', coordenadas);
  }
}
// #endregion

export const createLayers = async (layerTree) => {
  await buildLayers(layerTree, map);
};

export const refreshEmergenciesLayer = async () => {
  await updateEmergenciesLayer(map);
};

export const deleteLayerById = (layerId) => {
  deleteLayer(map, layerId);
};

export const setExageration3D = (exageration) => {
  const layerEx = map.ground.layers.getItemAt(0);

  if (layerEx) {
    const layer = new ExaggeratedElevationLayerSubClass({
      exaggeration: exageration,
      url: layerEx.url,
    });
    map.ground.layers.remove(layerEx);
    map.ground.layers.add(layer);
    // layer.exaggeration = exageration
  }
};
export const setVisibilityLayer = (id, visibility) => {
  if (map) {
    const layer = map.findLayerById(id);
    if (layer) {
      layer.visible = visibility;
    }
  }
};

export const setVisibilitySubLayer = (idLayer, sublayer, visibility) => {
  if (map) {
    const layer = map.findLayerById(idLayer);
    if (layer.allSublayers) {
      // Arcgis WMS
      const sublayerIndex = layer.allSublayers.items.findIndex((x) => x.id === sublayer.sublayerId);
      layer.allSublayers.items[sublayerIndex].visible = visibility;
    } else if (layer.allLayers) {
      // Arcgis Group Layers
      const sublayerIndex = layer.allLayers.items.findIndex((x) => x.layerId === sublayer.sublayerId);
      if (sublayerIndex !== -1) {
        layer.allLayers.items[sublayerIndex].visible = visibility;
      }
    }
  }
};

export const setOpacityLayer = (id, opacity) => {
  if (map) {
    const layer = map.findLayerById(id);

    if (layer) {
      layer.opacity = opacity;
    }
  }
};

export const setDefinitionExpressionLayer = (id, definitionExpression) => {
  if (map) {
    const layer = map.findLayerById(id);

    if (layer) {
      layer.definitionExpression = definitionExpression;
    }
  }
};

export const setLabelsLayer = (id, labelsVisible) => {
  if (map) {
    const layer = map.findLayerById(id);

    if (layer) {
      layer.labelsVisible = labelsVisible;
    }
  }
};

let recursoLayerRefreshIntervalId = null;

export const startRecursoLayerRefresh = (refreshInterval) => {
  if (recursoLayerRefreshIntervalId) return;

  const layer = map?.findLayerById('layer-recursos');
  if (!layer) {
    throw new Error('startRecursoLayerRefresh: layer-recursos not found');
  }

  if (!refreshInterval || refreshInterval <= 0) {
    throw new Error(`startRecursoLayerRefresh: invalid refreshIntervalSeconds (${refreshInterval})`);
  }

  const refreshMs = refreshInterval * 1000;

  recursoLayerRefreshIntervalId = setInterval(() => {
    const currentLayer = map?.findLayerById('layer-recursos');
    if (!currentLayer) {
      clearInterval(recursoLayerRefreshIntervalId);
      recursoLayerRefreshIntervalId = null;
      return;
    }

    if (!currentLayer.updating) {
      currentLayer.refresh();
    }
  }, refreshMs);
};

let maxSpeed = null; // getMaxSpeed(features)
let minSpeed = null; // getMinSpeed(features)
function getWindRenderer2D() {
  return {
    type: 'simple',

    symbol: {
      type: 'simple-marker',
      path: 'M14.5,29 23.5,0 14.5,9 5.5,0z',
      color: 'white',
      angle: 0,
      size: 10,
      outline: {
        color: [255, 255, 255, 0.5],
        width: 0,
      },
    },
    visualVariables: [
      {
        type: 'rotation',
        field: 'direction',
        rotationType: 'geographic',
      },
      {
        type: 'color',
        field: 'speed',
        stops: [
          {
            value: minSpeed ? minSpeed.toFixed(2) : minSpeed,
            color: '#0010d9',
          },
          {
            value: maxSpeed ? (maxSpeed / 2).toFixed(2) : maxSpeed,
            color: '#0080ff',
          },
          {
            value: maxSpeed ? maxSpeed.toFixed(2) : maxSpeed,
            color: '#00ffff',
          },
        ],
        valueExpressionTitle: 'Intensidad viento',
        valueExpression: '3',
      },

      {
        type: 'size',
        field: 'speed',
        minDataValue: 0,
        maxDataValue: 5,
        minSize: 17,
        maxSize: 17,
        legendOptions: {
          showLegend: false,
        },
      },
    ],
  };
}

async function getWindRenderer3D() {
  return {
    type: 'simple',
    symbol: {
      type: 'point-3d',
      symbolLayers: [
        {
          type: 'object',
          material: { color: '#a38a8a' },
          resource: { href: '/models/flecha_3D.glb' },
          tilt: 0,
          width: 30,
          depth: 80,
          height: 30,
        },
      ],
    },
    visualVariables: [
      {
        type: 'rotation',
        valueExpression: '$feature.direction + 180',
        axis: 'heading',
      },
      {
        type: 'color',
        field: 'speed',
        stops: [
          {
            value: minSpeed ? minSpeed.toFixed(2) : minSpeed,
            color: '#0010d9',
          },
          {
            value: maxSpeed ? (maxSpeed / 2).toFixed(2) : maxSpeed,
            color: '#0080ff',
          },
          {
            value: maxSpeed ? maxSpeed.toFixed(2) : maxSpeed,
            color: '#00ffff',
          },
        ],
      },
      // LONGITUD de la flecha
      {
        type: 'size',
        field: 'speed',
        axis: 'depth',
        stops: [
          { value: minSpeed, size: 50 },
          { value: maxSpeed, size: 200 },
        ],
      },
      // GROSOR vertical
      {
        type: 'size',
        field: 'speed',
        axis: 'height',
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 },
        ],
      },
      // ANCHURA lateral
      {
        type: 'size',
        field: 'speed',
        axis: 'width',
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 },
        ],
      },
    ],
  };
}

async function getWindLineRenderer3D() {
  return {
    type: 'simple',
    symbol: {
      type: 'line-3d',
      symbolLayers: [
        {
          type: 'line',
          size: 2,
          material: { color: '#ffaa00' },
        },
      ],
    },
    visualVariables: [
      {
        type: 'rotation',
        field: 'direction',
        rotationType: 'geographic',
      },
      {
        type: 'color',
        field: 'speed',
        stops: [
          {
            value: minSpeed ? minSpeed.toFixed(2) : minSpeed,
            color: '#0010d9',
          },
          {
            value: maxSpeed ? (maxSpeed / 2).toFixed(2) : maxSpeed,
            color: '#0080ff',
          },
          {
            value: maxSpeed ? maxSpeed.toFixed(2) : maxSpeed,
            color: '#00ffff',
          },
        ],
      },
    ],
  };
}
// #endregion

export const paintPositionEmergencia = async (point) => {
  try {
    const [Graphic, Point] = await loadModules(['esri/Graphic', 'esri/geometry/Point']);

    mapView.graphics = [];

    if (point) {
      const symbol = {
        type: 'simple-marker',
        style: 'x',
        color: 'white',
        size: '15px',
        outline: {
          color: 'red',
          width: '2px',
        },
      };

      const geometry = new Point({
        x: parseFloat(point.LONGITUD),
        y: parseFloat(point.LATITUD),
      });

      const graphic = new Graphic({
        geometry,
        symbol,
      });

      mapView.graphics.add(graphic);
    }
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
};
// #endregion

export const redrawInfraestructuras = async () => {
  if (map) {
    const layerlayerInfraestructurasPropias = map.findLayerById('layer-infraestructuras-propias');

    if (layerlayerInfraestructurasPropias) {
      const graphicsInfraestructuras = await createGraphicsInfraestructuras();
      const graphicsValidos = graphicsInfraestructuras.filter((g) => {
        const { x, y } = g.geometry || {};

        return Number.isFinite(x) && Number.isFinite(y);
      });

      const { features } = await layerlayerInfraestructurasPropias.queryFeatures();
      await layerlayerInfraestructurasPropias.applyEdits({
        addFeatures: graphicsValidos, // new updated features
        deleteFeatures: features, // delete the old features
      });
    }
  }
};

async function createGraphicsInfraestructuras() {
  const [Graphic, Point] = await loadModules(['esri/Graphic', 'esri/geometry/Point']);
  try {
    const infraestructuras = store.getters['infraestructura/infraestructuras'].filter((infra) => infra.X && infra.Y);
    const tiposInfraestructuras = store.getters['infraestructura/tipos'];
    const categoriasInfraestructuras = store.getters['infraestructura/categorias'];
    const municipiosInfraestructuras = store.getters['infraestructura/municipios'];
    const provinciasInfraestructuras = store.getters['infraestructura/provincias'];
    const responsablesInfraestructuras = store.getters['infraestructura/responsables'];
    const sources = [];
    for (let i = 0; i < infraestructuras.length; i++) {
      const infraestructura = infraestructuras[i];
      const tipo = tiposInfraestructuras.find(
        (x) => x.ID_INFRAESTRUCTURA_TIPO === infraestructura.ID_INFRAESTRUCTURA_TIPO
      );
      const municipio = municipiosInfraestructuras.find((x) => x.ID_MUNICIPIO === infraestructura.ID_MUNICIPIO);
      const attributes = {
        ID_INFRAESTRUCTURA: infraestructura.ID_INFRAESTRUCTURA,
        TIPO: tipo ? tipo.NOMBRE : null,
        CATEGORIA: categoriasInfraestructuras.find(
          (x) => x.ID_INFRAESTRUCTURA_CATEGORIA === tipo.ID_INFRAESTRUCTURA_CATEGORIA
        )
          ? categoriasInfraestructuras.find((x) => x.ID_INFRAESTRUCTURA_CATEGORIA === tipo.ID_INFRAESTRUCTURA_CATEGORIA)
              .NOMBRE
          : null,
        MUNICIPIO: municipio ? municipio.NOMBRE : null,
        PROVINCIA: provinciasInfraestructuras.find((x) => x.ID_PROVINCIA === municipio.ID_PROVINCIA)
          ? provinciasInfraestructuras.find((x) => x.ID_PROVINCIA === municipio.ID_PROVINCIA).LITERAL
          : null,
        RESPONSABLE: responsablesInfraestructuras.find((x) => x.ID_ORGANISMO === infraestructura.ID_ORGANISMO)
          ? responsablesInfraestructuras.find((x) => x.ID_ORGANISMO === infraestructura.ID_ORGANISMO).ORGANISMO
          : null,
        NOMBRE: infraestructura.NOMBRE,
        FECHA_ALTA: infraestructura.FECHA_ALTA,
        FECHA_BAJA: infraestructura.FECHA_BAJA,
        BORRADO: infraestructura.BORRADO,
        DESCRIPCION: infraestructura.DESCRIPCION,
        DIRECION: infraestructura.DIRECION,
        CODIGO: infraestructura.CODIGO,
        icon:
          tipo !== null
            ? `${constants.iconosTiposInfraestructuras.find((icono) => icono.TIPO === tipo.NOMBRE).URL}.png`
            : null,
      };
      const latLong = convertirETRS89ToLatLon(infraestructura.X, infraestructura.Y);
      const geometry = new Point({
        x: latLong[0],
        y: latLong[1],
      });

      const graphic = new Graphic({
        geometry,
        attributes,
      });
      sources.push(graphic);
    }
    return sources;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

// #region RUTAS
export const showRutasGeoJSON = async (geojsonResponse) => {
  const ROUTING_LAYER_ID = 'layer-routing';

  if (map) {
    const layer = map.findLayerById(ROUTING_LAYER_ID);

    try {
      const graphics = await createGraphicsRutas(geojsonResponse);
      layer.graphics.items = graphics;
    } catch (error) {
      // console.error('Error al crear la capa de gráficos:', error)
      VueInst.$log.error('Error al crear la capa de gráficos:', error);
    }
  }
};

async function createGraphicsRutas(response) {
  try {
    const [Polyline, Polygon, Graphic] = await loadModules([
      'esri/geometry/Polyline',
      'esri/geometry/Polygon',
      'esri/Graphic',
    ]);

    const featuresJson = response.features;
    const graphics = [];

    for (let i = 0; i < featuresJson.length; i++) {
      const feature = featuresJson[i];
      const geometryType = feature.geometry.type;

      let geometry;
      let symbol;

      if (geometryType === 'LineString') {
        geometry = new Polyline({
          hasZ: false,
          hasM: false,
          paths: feature.geometry.coordinates,
          spatialReference: { wkid: 4326 },
        });

        symbol = {
          type: 'simple-line',
          color: [0, 255, 255, 1],
          width: 2,
          outline: {
            color: 'black',
            width: 1,
          },
          style: 'solid',
        };
      } else if (geometryType === 'Polygon') {
        geometry = new Polygon({
          hasZ: false,
          hasM: false,
          rings: feature.geometry.coordinates,
          spatialReference: { wkid: 4326 },
        });

        symbol = {
          type: 'simple-fill',
          color: feature.properties.color || [255, 255, 0, 0.3],
          style: 'solid',
          outline: {
            color: [0, 0, 0, 0],
            width: 0,
          },
        };
      }

      // const polygonTitle = `Alcance en ${feature.properties.contour} min`

      // const popupTemplate = {
      //   title: geometryType === 'LineString' ? 'Ruta' : polygonTitle,
      //   content: [
      //     {
      //       type: 'fields',
      //       fieldInfos: [
      //         { fieldName: 'Nombre', label: 'Nombre' },
      //         { fieldName: 'Distancia', label: 'Distancia (km)' }
      //       ]
      //     }
      //   ]
      // }

      const graphic = new Graphic({
        geometry,
        attributes: feature.properties,
        symbol,
        // popupTemplate: popupTemplate
      });

      graphics.push(graphic);
    }

    return graphics;
  } catch (err) {
    // console.error('Error en createGraphics:', err)
    VueInst.$log.error('Error en createGraphics:', err);
  }
}

export const clearRutas = () => {
  if (map) {
    const layer = map.findLayerById('layer-routing');
    if (layer) {
      layer.graphics.items = [];
      // map.remove(layer)
      // layer.destroy()
    }
  }
};

export const DISTANCIA_SEGURIDAD_M = 500;
const MARGEN_EXCLUSION_M = 100;
const TOLERANCIA_GENERALIZADO_M = 50;
const MARGEN_SEGURIDAD_AEREA_M = 1000;
const TOLERANCIA_MARGEN_AEREO_M = 10;
const CODIGO_RUTA_AEREA_INSEGURA = "UNSAFE_AERIAL_ROUTE";

export const esRutaAereaInsegura = (error) => error?.code === CODIGO_RUTA_AEREA_INSEGURA;

export async function calcularDestinoSeguro(ptInitLat, ptInitLon, ptEndLat, ptEndLon, anilloMercator) {
  const [geometryEngine, Point, Polygon, webMercatorUtils] = await loadModules([
    "esri/geometry/geometryEngine",
    "esri/geometry/Point",
    "esri/geometry/Polygon",
    "esri/geometry/support/webMercatorUtils"
  ]);
  const poligonoFuego = new Polygon({
    rings: [anilloMercator],
    spatialReference: { wkid: 102100 }
  });

  let inicio = new Point({ latitude: ptInitLat, longitude: ptInitLon, spatialReference: { wkid: 4326 } });
  let destino = new Point({ latitude: ptEndLat, longitude: ptEndLon, spatialReference: { wkid: 4326 } });

  inicio = webMercatorUtils.geographicToWebMercator(inicio);
  destino = webMercatorUtils.geographicToWebMercator(destino);

  if (!geometryEngine.contains(poligonoFuego, destino)) {
    return { modificado: false };
  }

  const zonaSeguridad = geometryEngine.geodesicBuffer(poligonoFuego, DISTANCIA_SEGURIDAD_M, "meters");
  const resultado = geometryEngine.nearestCoordinate(zonaSeguridad, inicio);
  const puntoSeguro = webMercatorUtils.webMercatorToGeographic(resultado.coordinate);

  return {
    latitude: puntoSeguro.latitude,
    longitude: puntoSeguro.longitude,
    modificado: true
  };
}

export async function calcularZonaExclusion(anilloMercator) {
  const [geometryEngine, Polygon, webMercatorUtils] = await loadModules([
    "esri/geometry/geometryEngine",
    "esri/geometry/Polygon",
    "esri/geometry/support/webMercatorUtils"
  ]);
  const poligonoFuego = new Polygon({
    rings: [anilloMercator],
    spatialReference: { wkid: 102100 }
  });

  const generalizado = geometryEngine.generalize(poligonoFuego, TOLERANCIA_GENERALIZADO_M, true, "meters");
  if (!generalizado) return null;

  const zonaExclusion = geometryEngine.geodesicBuffer(generalizado, MARGEN_EXCLUSION_M, "meters");
  if (!zonaExclusion?.rings?.length) return null;

  const anillo = webMercatorUtils
    .webMercatorToGeographic(zonaExclusion)
    .rings[0].map(([lon, lat]) => [lon, lat]);

  const primerPunto = anillo[0];
  const ultimoPunto = anillo.at(-1);
  if (primerPunto[0] !== ultimoPunto[0] || primerPunto[1] !== ultimoPunto[1]) {
    anillo.push(primerPunto);
  }

  return anillo.length < 4 ? null : anillo;
}

export async function calcularRutaAerea(ptInit, ptEnd, emergencias) {

  const VELOCIDAD_HELICOPTERO_M_S = 61.11;

  const [geometryEngine, Polyline, Polygon, Point, webMercatorUtils] = await loadModules([
    "esri/geometry/geometryEngine",
    "esri/geometry/Polyline",
    "esri/geometry/Polygon",
    "esri/geometry/Point",
    "esri/geometry/support/webMercatorUtils"
  ]);

  const ptInitCoords = [ptInit.longitude, ptInit.latitude];
  const ptEndCoords = [ptEnd.longitude, ptEnd.latitude];

  const rutaAerea = new Polyline({
    paths: [[ptInitCoords, ptEndCoords]], spatialReference: { wkid: 4326 }
  })

  const poligonosIncendio = emergencias.reduce((acc, e) => {
    const perimetroJSON = e.PERIMETRO?.at(-1)?.GRAFICOS_PERIMETRO?.[0]?.geometry;
    if (perimetroJSON) {
      const polOriginal = Polygon.fromJSON(perimetroJSON);
      const polGeografico = [102100, 3857].includes(polOriginal.spatialReference.wkid)
        ? webMercatorUtils.webMercatorToGeographic(polOriginal)
        : polOriginal
      acc.push(geometryEngine.simplify(polGeografico));
    }
    return acc;
  }, []);

  const rutaFinal = poligonosIncendio.length > 0
    ? recalcularRutaEsquivando(ptInitCoords, ptEndCoords, rutaAerea, poligonosIncendio, geometryEngine, Polyline, Point)
    : rutaAerea;

  const metros = geometryEngine.geodesicLength(rutaFinal, "meters");
  const durationInSeconds = Math.round(metros / VELOCIDAD_HELICOPTERO_M_S);

  return {
    geoJSON: {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: rutaFinal.toJSON().paths[0]
        },
        properties: {}
      }]
    },
    duration: {
      horas: Math.floor(durationInSeconds / 3600),
      minutos: Math.floor((durationInSeconds % 3600) / 60)
    },
    distance: parseFloat((metros / 1000).toFixed(2))
  };
}

function indiceVerticeMasCercano(anillo, punto) {
  let indiceMasCercano = 0;
  let distanciaMinima = Infinity;
  anillo.forEach((vertice, indice) => {
    const distancia = Math.hypot(vertice[0] - punto[0], vertice[1] - punto[1]);
    if (distancia < distanciaMinima) {
      distanciaMinima = distancia;
      indiceMasCercano = indice;
    }
  });
  return indiceMasCercano;
}

function distanciaGeodesicaAPoligono(geometryEngine, Polyline, Point, ptInitCoords, poligono) {
  const puntoOrigen = new Point({ longitude: ptInitCoords[0], latitude: ptInitCoords[1], spatialReference: { wkid: 4326 } });
  const { coordinate } = geometryEngine.nearestCoordinate(poligono, puntoOrigen);
  const linea = new Polyline({ paths: [[ptInitCoords, [coordinate.x, coordinate.y]]], spatialReference: { wkid: 4326 } });
  return geometryEngine.geodesicLength(linea, "meters");
}

function recalcularRutaEsquivando(ptInitCoords, ptEndCoords, rutaAerea, incendios, geometryEngine, Polyline, Point) {

  const crearLinea = (puntos) => new Polyline({
    paths: [puntos],
    spatialReference: { wkid: 4326 }
  });

  const waypoints = [];

  const ptEndGeom = new Point({
    longitude: ptEndCoords[0],
    latitude: ptEndCoords[1],
    spatialReference: { wkid: 4326 }
  });

  const incendiosOrdenados = incendios
    .map((incendio) => ({ incendio, distancia: distanciaGeodesicaAPoligono(geometryEngine, Polyline, Point, ptInitCoords, incendio) }))
    .sort((a, b) => a.distancia - b.distancia)
    .map(({ incendio }) => incendio);

  for (const incendio of incendiosOrdenados) {

    if (geometryEngine.intersects(ptEndGeom, incendio)) continue

    const zonaExclusion = geometryEngine.geodesicBuffer(incendio, MARGEN_SEGURIDAD_AEREA_M, "meters")
    if (!zonaExclusion || !geometryEngine.intersects(rutaAerea, zonaExclusion)) continue

    const interseccion = geometryEngine.intersect(rutaAerea, zonaExclusion);

    if (interseccion?.paths?.length > 0) {
      const tramo = interseccion.paths[0];
      const anillo = zonaExclusion.rings[0];

      const idxEntrada = indiceVerticeMasCercano(anillo, tramo[0]);
      const idxSalida = indiceVerticeMasCercano(anillo, tramo.at(-1));

      const camino1 = (idxEntrada < idxSalida) ? anillo.slice(idxEntrada, idxSalida) : [...anillo.slice(idxEntrada), ...anillo.slice(0, idxSalida)];
      const camino2 = (idxEntrada < idxSalida) ? [...anillo.slice(idxSalida), ...anillo.slice(0, idxEntrada)].reverse() : anillo.slice(idxSalida, idxEntrada).reverse();

      const rutaCandidata1 = crearLinea([ptInitCoords, ...camino1, ptEndCoords]);
      const rutaCandidata2 = crearLinea([ptInitCoords, ...camino2, ptEndCoords]);

      const dist1 = geometryEngine.geodesicLength(rutaCandidata1, "meters");
      const dist2 = geometryEngine.geodesicLength(rutaCandidata2, "meters");

      const segmento = dist1 < dist2 ? camino1 : camino2;

      let idxOptimoEntrada = 0;
      let idxOptimoSalida = segmento.length - 1;

      const zonaTest = geometryEngine.geodesicBuffer(incendio, MARGEN_SEGURIDAD_AEREA_M - TOLERANCIA_MARGEN_AEREO_M, "meters");

      for (let i = segmento.length - 1; i >= 0; i--) {
        if (!geometryEngine.intersects(crearLinea([ptInitCoords, segmento[i]]), zonaTest)) {
          idxOptimoEntrada = i;
          break;
        }
      }

      for (let i = idxOptimoEntrada; i < segmento.length; i++) {
        if (!geometryEngine.intersects(crearLinea([segmento[i], ptEndCoords]), zonaTest)) {
          idxOptimoSalida = i;
          break;
        }
      }

      const segmentoOptimizado = segmento.slice(idxOptimoEntrada, idxOptimoSalida + 1);

      waypoints.push(...segmentoOptimizado);
    }
  }

  const rutaFinal = crearLinea([ptInitCoords, ...waypoints, ptEndCoords]);

  const cruzaZonaExclusion = incendiosOrdenados.some((incendio) => {
    if (geometryEngine.intersects(ptEndGeom, incendio)) return false;
    const zonaTest = geometryEngine.geodesicBuffer(incendio, MARGEN_SEGURIDAD_AEREA_M - TOLERANCIA_MARGEN_AEREO_M, "meters");
    return geometryEngine.intersects(rutaFinal, zonaTest);
  });

  if (cruzaZonaExclusion) {
    const error = new Error("Aerial route intersects an exclusion zone");
    error.code = CODIGO_RUTA_AEREA_INSEGURA;
    throw error;
  }

  return rutaFinal;
}

// #endregion RUTAS

// #region TRACKS
export const drawTrack = async (idRecurso, positions, activateGOTO) => {
  if (map) {
    try {
      const layer = map.findLayerById('layer-track');

      const response = await api.recurso.getDispositivos(idRecurso);
      const dispositivos = response.data;

      if (dispositivos?.length > 0) {
        for (const dispositivo of dispositivos) {
          const idDispositivo = dispositivo.ID_GEO_DISPOSITIVO;
          const issi = dispositivo.CODIGO_EXTERNO;

          const dispositivoPositions = positions.filter(
            (pos) => pos.ID_GEO_DISPOSITIVO.toLowerCase() === idDispositivo.toLowerCase() // Compara ignorando mayúsculas/minúsculas
          );

          if (dispositivoPositions.length === 0) {
            continue;
          }

          const newPositions = await calcularVelocidad(dispositivoPositions);

          const graphics = await createGraphicsTrack(idRecurso, idDispositivo, issi, newPositions);

          layer.addMany(graphics);
        }
      } else {
        console.error('No se encontraron dispositivos para este recurso.');
      }
      if (activateGOTO) {
        mapView.goTo(layer.graphics.items, {
          duration: 3000,
          easing: 'in-expo',
        });
      }
      layer.visible = true;
    } catch (error) {
      console.error('[Arcgis] Error al dibujar track del recurso: ', error);
      VueInst.$notify('Error al dibujar track del recurso', 'error');
    }
  }
};

export const hideTrack = (idRecurso) => {
  if (map) {
    const layer = map.findLayerById('layer-track');
    if (!layer) return;
    layer.removeMany(layer.graphics.items.filter((x) => x.attributes.ID_RECURSO === idRecurso));

    layer.visible = true;
  }
};

async function createGraphicsTrack(idRecurso, idGeoDispositivo, issi, posiciones) {
  try {
    const [Point, ObjectSymbol3DLayer, PointSymbol3D] = await loadModules([
      'esri/geometry/Point',
      'esri/symbols/ObjectSymbol3DLayer',
      'esri/symbols/PointSymbol3D',
    ]);

    const rgbRandomArrow = [
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
    ];

    const is3D = store.getters['map/isMap3D'];

    const rgbRandom = [
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
    ];

    posiciones.reverse(); // Poner en orden creciente

    const sources = [];
    const pathPosiciones = [];

    for (let i = 0; i < posiciones.length; i++) {
      let rumbo = 0;

      if (i < posiciones.length - 1) {
        // Calcula el rumbo para todas las posiciones menos para la ultima
        rumbo = calcularRumbo(
          posiciones[i].LATITUD,
          posiciones[i].LONGITUD,
          posiciones[i + 1].LATITUD,
          posiciones[i + 1].LONGITUD
        );
      }
      rumbo = (rumbo + 180) % 360; // La flecha original apunta hacia abajo

      pathPosiciones.push([posiciones[i].LONGITUD, posiciones[i].LATITUD]);

      const commonAttributes = {
        ID_RECURSO: idRecurso,
        ID_GEO_DISPOSITIVO: idGeoDispositivo,
        MEDIO: issi,
        FECHA: posiciones[i].FECHA,
        VELOCIDAD: posiciones[i].VELOCIDAD,
      };

      if (is3D) {
        sources.push({
          // Cada punto
          geometry: new Point({
            x: posiciones[i].LONGITUD,
            y: posiciones[i].LATITUD,
          }),
          attributes: commonAttributes,
          symbol: new PointSymbol3D({
            symbolLayers: [
              new ObjectSymbol3DLayer({
                anchor: 'relative',
                anchorPosition: { x: 0, y: 0, z: -0.5 },

                width: 200,
                height: 200,
                tilt: 85,
                heading: rumbo,
                resource: {
                  primitive: 'tetrahedron',
                },
                material: {
                  color: '#FFD700',
                },
              }),
            ],
          }),
        });
      } else {
        sources.push({
          // Cada punto
          geometry: new Point({
            x: posiciones[i].LONGITUD,
            y: posiciones[i].LATITUD,
          }),
          attributes: commonAttributes,
          symbol: {
            type: 'simple-marker',
            path: 'M14.5,29 23.5,0 14.5,9 5.5,0z',
            angle: rumbo,
            color: rgbRandomArrow,
            size: '10px',
            outline: {
              color: rgbRandomArrow,
            },
          },
        });

        // Etiqueta del issi sobre el punto
        // se añade cada 3 puntos, para no sobrecargar
        if (i % 3 === 0) {
          sources.push({
            // Cada punto
            geometry: new Point({
              x: posiciones[i].LONGITUD,
              y: posiciones[i].LATITUD,
            }),
            attributes: { ...commonAttributes, IS_TAG: true },
            symbol: {
              type: 'text', // autocasts as new TextSymbol()
              color: rgbRandomArrow,
              haloColor: 'black',
              haloSize: '0.75px',
              text: issi,
              yoffset: 6,
              font: {
                // autocasts as new Font()
                size: 10,
                family: 'Arial Unicode MS',
              },
            },
          });
        }
      }
    }
    sources.unshift({
      // Linea
      geometry: {
        type: 'polyline',
        paths: pathPosiciones,
      },
      attributes: {
        ID_RECURSO: idRecurso,
        ID_GEO_DISPOSITIVO: idGeoDispositivo,
      },
      symbol: {
        type: 'simple-line',
        style: 'short-dot',
        color: rgbRandom,
        width: 1,
      },
    });

    return sources;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

async function calcularVelocidad(puntos) {
  // TODO: hacer en backend

  try {
    const [Point, geometryEngine, SpatialReference, webMercatorUtils] = await loadModules([
      'esri/geometry/Point',
      'esri/geometry/geometryEngine',
      'esri/geometry/SpatialReference',
      'esri/geometry/support/webMercatorUtils',
    ]);

    const sr = new SpatialReference({ wkid: 102100 });
    for (let i = 0; i < puntos.length; i++) {
      if (i === 0) {
        puntos[i].VELOCIDAD = 0;
      } else if (i < puntos.length - 1) {
        const posPoint = webMercatorUtils.lngLatToXY(puntos[i].LONGITUD, puntos[i].LATITUD);
        const posFollowPoint = webMercatorUtils.lngLatToXY(puntos[i + 1].LONGITUD, puntos[i + 1].LATITUD);

        const point = new Point({
          x: posPoint[0],
          y: posPoint[1],
          spatialReference: sr,
        });

        const followPoint = new Point({
          x: posFollowPoint[0],
          y: posFollowPoint[1],
          spatialReference: sr,
        });

        const distancia = geometryEngine.distance(point, followPoint, 'kilometers');

        const fechaPoint = VueInst.$date.toDayjs(puntos[i].FECHA);
        const fechaFollowPoint = VueInst.$date.toDayjs(puntos[i + 1].FECHA);

        const tiempo = fechaPoint.diff(fechaFollowPoint, 'seconds') / 3600;

        const velocidad = distancia / tiempo;

        puntos[i].VELOCIDAD = Math.trunc(velocidad);
      }
    }
    return puntos;
  } catch (err) {
    VueInst.$log.error(err);
  }
}
function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians) {
  return (radians * 180) / Math.PI;
}
function calcularRumbo(startLat, startLng, destLat, destLng) {
  // TODO: hacer en backend
  startLat = toRadians(startLat);
  startLng = toRadians(startLng);
  destLat = toRadians(destLat);
  destLng = toRadians(destLng);

  const y = Math.sin(destLng - startLng) * Math.cos(destLat);
  const x =
    Math.cos(startLat) * Math.sin(destLat) - Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng);
  let brng = Math.atan2(y, x);
  brng = toDegrees(brng);
  return (brng + 360) % 360;
}

export const exportTrack = (data, kml, shp) => {
  if (!map) return;
  try {
    const layer = map.findLayerById('layer-track');

    if (!layer) return;

    const points = layer.graphics.items
      .filter((x) => x.attributes.ID_GEO_DISPOSITIVO === data.idGeoDispositivo)
      .sort((a, b) => (a.attributes.FECHA > b.attributes.FECHA ? 1 : -1));

    if (!points.length) return;

    // Construir GEOJSON
    const features = [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: points.map((x) => x.geometry).map((x) => [x.longitude, x.latitude]),
        },
        properties: {
          MEDIO: data.medio,
        },
      },
    ];

    for (let i = 0; i < points.length; i++) {
      if (!points[i].attributes.VELOCIDAD) {
        points[i].attributes.VELOCIDAD = 0;
      }
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [points[i].geometry.longitude, points[i].geometry.latitude],
        },
        properties: points[i].attributes,
      });
    }

    const geoJson = {
      type: 'FeatureCollection',
      features,
    };
    const nombre = sanitizeFileName(`${data.medio}_${DateService.getCurrentDayJs().format(FMT.dateCompact)}`);

    if (shp) exportTrackSHP(geoJson, nombre);
    if (kml) exportTrackKML(geoJson, nombre);
  } catch (err) {
    VueInst.$log.error('Error exportando el track', err);
    VueInst.$notify.error('Error exportando el track');
  }
};

export const exportTrackSHP = (geoJson, nombre) => {
  try {
    const options = {
      folder: `Track_${nombre}`,
      types: {
        point: `PUNTOS_${nombre}`,
        polygon: `POLIGONOS_${nombre}`,
        line: `LINEAS_${nombre}`,
        polyline: `POLYLINE_${nombre}`,
      },
    };
    shpwrite.download(geoJson, options);
  } catch (err) {
    VueInst.$log.error('Error generando shp', err);
  }
};

export const exportTrackKML = (geoJson, nombre) => {
  try {
    let kml = toKML(geoJson);

    const style = `
      <Style id="circle">
        <IconStyle>
          <color>ff00ffff</color>
          <scale>1.0</scale>
          <Icon>
            <href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href>
          </Icon>
        </IconStyle>
      </Style>
    `;

    kml = kml.replace(/<Document>/, `<Document>\n${style}`);

    kml = kml.replace(/<Placemark>/g, '<Placemark>\n<styleUrl>#circle</styleUrl>');

    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(kml)}`);
    element.setAttribute('download', `Track_${nombre}.kml`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  } catch (err) {
    VueInst.$log.error('Error generando kml', err);
  }
};

const buildPolygonGeometry = (grafico) => {
  try {
    const rings = grafico.geometry?.rings;

    if (!Array.isArray(rings) || rings.length === 0) {
      return null;
    }

    for (const ring of rings) {
      if (!Array.isArray(ring) || ring.length < 3) {
        return null;
      }

      const uniqueCoords = new Set(ring.map((coord) => coord.join(',')));

      if (uniqueCoords.size < 3) {
        return null;
      }

      const first = ring[0];
      const last = ring[ring.length - 1];

      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([...first]);
      }
    }

    return rings;
  } catch (err) {
    console.error('Error creando geometrias para exportar:', err);
    return null;
  }
};

const buildProperties = (perimetro, grafico) => {
  const simbolo = grafico.symbol;
  const layer = simbolo?.data?.symbol?.symbolLayers?.[0];

  return {
    NOMBRE: perimetro.NOMBRE || '',
    FECHA: perimetro.FECHA || '',
    COLOR: JSON.stringify(layer?.color || []),
    WIDTH: layer?.width || 2.5,
    DASH: !!layer?.effects,
  };
};

const buildGeoJsonPerimetro = (perimetro) => {
  const features = [];

  perimetro.GRAFICOS_PERIMETRO?.filter((g) => g.type === 'polygon' && g.geometry).forEach((grafico) => {
    const geometry = buildPolygonGeometry(grafico);

    if (!geometry) {
      return;
    }

    features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: geometry,
      },
      properties: buildProperties(perimetro, grafico),
    });
  });

  return {
    type: 'FeatureCollection',
    features,
  };
};

const generateKMLFiles = (zip, perimetros) => {
  perimetros.forEach((perimetro) => {
    const geoJson = buildGeoJsonPerimetro(perimetro);

    if (!geoJson.features.length) {
      return;
    }

    const nombrePerimetro = sanitizeFileName(perimetro.NOMBRE);
    const geoJsonTransformado = transformarGeoJsonAEPSG4326(geoJson);
    const kml = toKML(geoJsonTransformado);

    zip.file(`${nombrePerimetro}.kml`, kml);
  });
};

const generateSHPFiles = async (zip, perimetros) => {
  for (const perimetro of perimetros) {
    const geoJson = buildGeoJsonPerimetro(perimetro);

    if (!geoJson.features.length) {
      continue;
    }

    const nombrePerimetro = sanitizeFileName(perimetro.NOMBRE);

    const geoJsonTransformado = transformarGeoJsonAEPSG4326(geoJson);

    const shpZipBlob = await shpwrite.zip(geoJsonTransformado, {
      folder: nombrePerimetro,
      filename: nombrePerimetro,
      types: {
        polygon: nombrePerimetro,
      },
    });

    zip.file(`${nombrePerimetro}.zip`, shpZipBlob);
  }
};

export const exportPerimetros = async (perimetros, tipo = 'KML') => {
  try {
    if (!perimetros?.length) {
      throw new Error('No hay perímetros para exportar');
    }

    const zip = new JSZip();

    if (tipo === 'KML') {
      generateKMLFiles(zip, perimetros);
    } else if (tipo === 'SHP') {
      await generateSHPFiles(zip, perimetros);
    }

    const timestamp = DateService.toString(DateService.getCurrentDayJs(), FMT.onlyDateDash);

    const nombreZip = sanitizeFileName(`Perimetros_${tipo}_${timestamp}.zip`);

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    downloadBlob(zipBlob, nombreZip);
  } catch (err) {
    console.error('Error exportando perimetros:', err);
    throw err;
  }
};

// #endregion
// #region PLANES_OPERACIONES
let planOpSVM = null;

export const initPlanOperacionesSketchViewModel = async (data) => {
  try {
    const [SketchViewModel] = await loadModules(['esri/widgets/Sketch/SketchViewModel']);
    const callbackCreate = data.callbackCreate;
    const callbackUpdate = data.callbackUpdate;
    const callbackDelete = data.callbackDelete;

    const layer = map.findLayerById('layer-plan-operaciones');

    planOpSVM = new SketchViewModel({
      view: mapView,
      layer,
    });

    planOpSVM.on(['create', 'delete', 'update'], async (evt) => {
      // Arrow logic
      if (isDrawingArrow && evt.type === 'create' && evt.graphic?.geometry.type === 'polyline') {
        if (evt.state === 'active') {
          if (evt.graphic.geometry.paths[0].length === 3) {
            planOpSVM.complete();
          }
        }
      }

      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico
        const attributes = {
          ID_GRAFICO: VueInst.$uuid.createUUID(),
        };

        evt.graphic.attributes = JSON.parse(JSON.stringify(attributes)); // Hack para quitar el __observer__ que causa un bucle infinito a arcgis

        let geometry;
        let type = evt.graphic.geometry.type;
        let symbol;
        if (evt.graphic.geometry.type === 'point' && evt.graphic.symbol.type === 'simple-marker') {
          // Punto
          const defaultSymbol = planOpSVM.pointSymbol;

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y];
          symbol = {
            type: defaultSymbol.type,
            color: defaultSymbol.color,
            // 'angle': defaultSymbol.angle,
            size: defaultSymbol.size,
            style: defaultSymbol.style,
          };
        } else if (evt.graphic.geometry.type === 'point' && evt.graphic.symbol.type === 'picture-marker') {
          // Icono
          const defaultSymbol = planOpSVM.pointSymbol;

          attributes.LABEL = {
            label: defaultSymbol.label,
            src: defaultSymbol.url,
          };

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y];

          type = 'icon';

          // Simbolo que añade una circunferencia al icono, en caso de ser medio externo
          // mediante la variable 'tipoLinea' para reutilizar
          symbol = {
            type: 'cim',
            // CIM Line Symbol
            data: {
              type: 'CIMSymbolReference',
              symbol: {
                type: 'CIMPointSymbol',
                rotation: defaultSymbol.rotation,
                symbolLayers: [
                  {
                    // arrow symbol
                    type: 'CIMVectorMarker',
                    enable: tipoLinea,
                    size: 30,
                    frame: {
                      xmin: 2.5,
                      ymin: 2.5,
                      xmax: 14,
                      ymax: 14,
                    },
                    markerGraphics: [
                      {
                        type: 'CIMMarkerGraphic',
                        geometry: {
                          rings: [
                            [
                              [8.5, 0.2],
                              [7.06, 0.33],
                              [5.66, 0.7],
                              [4.35, 1.31],
                              [3.16, 2.14],
                              [2.14, 3.16],
                              [1.31, 4.35],
                              [0.7, 5.66],
                              [0.33, 7.06],
                              [0.2, 8.5],
                              [0.33, 9.94],
                              [0.7, 11.34],
                              [1.31, 12.65],
                              [2.14, 13.84],
                              [3.16, 14.86],
                              [4.35, 15.69],
                              [5.66, 16.3],
                              [7.06, 16.67],
                              [8.5, 16.8],
                              [9.94, 16.67],
                              [11.34, 16.3],
                              [12.65, 15.69],
                              [13.84, 14.86],
                              [14.86, 13.84],
                              [15.69, 12.65],
                              [16.3, 11.34],
                              [16.67, 9.94],
                              [16.8, 8.5],
                              [16.67, 7.06],
                              [16.3, 5.66],
                              [15.69, 4.35],
                              [14.86, 3.16],
                              [13.84, 2.14],
                              [12.65, 1.31],
                              [11.34, 0.7],
                              [9.94, 0.33],
                              [8.5, 0.2],
                            ],
                          ],
                        },
                        symbol: {
                          // black fill for the arrow symbol
                          type: 'CIMPolygonSymbol',
                          symbolLayers: [
                            {
                              type: 'CIMSolidStroke', // CIMSolidFill | CIMSolidStroke
                              enable: true,
                              width: 2,
                              color: [10, 10, 10, 255],
                            },
                          ],
                        },
                      },
                    ],
                  },
                  {
                    type: 'CIMPictureMarker',
                    enable: true,
                    url: defaultSymbol.url,
                    size: 40,
                    rotation: defaultSymbol.rotation,
                  },
                  // Fondo del icono
                  {
                    type: 'CIMVectorMarker',
                    enable: showBackground,
                    size: 44,
                    frame: {
                      xmin: 0,
                      ymin: 0,
                      xmax: 20,
                      ymax: 20,
                    },
                    markerGraphics: [
                      {
                        type: 'CIMMarkerGraphic',
                        geometry: {
                          rings: [
                            [
                              [0, 0],
                              [20, 0],
                              [20, 20],
                              [0, 20],
                              [0, 0],
                            ],
                          ],
                        },
                        symbol: {
                          type: 'CIMPolygonSymbol',
                          symbolLayers: [
                            {
                              type: 'CIMSolidFill',
                              enable: true,
                              color: [255, 255, 255, 180],
                            },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
            },
          };
          evt.graphic.symbol = symbol;
        } else if (evt.graphic.geometry.type === 'point' && evt.graphic.symbol.type === 'text') {
          // Texto
          const defaultSymbol = planOpSVM.pointSymbol;

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y];
          type = 'text';
          symbol = {
            type: 'text',
            color: defaultSymbol.color,
            text: defaultSymbol.text,
            xoffset: 0,
            yoffset: 0,
            angle: defaultSymbol.angle,
            haloColor: 'white',
            haloSize: '20px',
            font: {
              size: defaultSymbol.font.size,
              family: 'Arial Unicode MS',
              weight: 'bold',
            },
          };
        } else if (!isDrawingArrow && evt.graphic.geometry.type === 'polyline') {
          // Linea
          if (!tipoLinea) {
            // Lineas antiguas
            const defaultSymbol = planOpSVM.polylineSymbol;

            geometry = evt.graphic.geometry.paths[0];
            symbol = {
              type: defaultSymbol.type,
              color: defaultSymbol.color,
              width: defaultSymbol.width,
              style: defaultSymbol.style,
              join: defaultSymbol.join,
              miterLimit: defaultSymbol.miterLimit,
            };
          } else {
            // Lineas nuevas
            const is3D = store.getters['map/isMap3D'];
            let nombreURL;
            if (is3D) {
              nombreURL = `${tipoLinea}3D`;
            } else {
              nombreURL = tipoLinea;
            }

            const geometria = evt.graphic.geometry.paths[0];

            const g = await makeCustomLine(geometria, nombreURL);

            geometry = geometria;
            evt.graphic.symbol = g.symbol;
            symbol = g.symbol;
          }
          attributes.LABEL = planOpSVM.polylineSymbol.label;
        } else if (isDrawingArrow || evt.graphic.geometry.type === 'polygon') {
          // Poligonos y flecha
          if (!tipoLinea) {
            // Poligonos y flechas old
            const defaultSymbol = planOpSVM.polygonSymbol;

            if (isDrawingArrow) {
              // Convertir la linea a flecha y crear el graphic
              isDrawingArrow = false;
              const g = await makeArrow(evt.graphic.geometry.paths[0], defaultSymbol.color);
              evt.graphic.geometry = g.geometry;
              evt.graphic.symbol = g.symbol;
              type = 'polygon';
            }

            geometry = evt.graphic.geometry.rings[0];
            symbol = {
              type: defaultSymbol.type,
              color: defaultSymbol.color,
              style: defaultSymbol.style,
              outline: {
                color: defaultSymbol.outline.color,
                width: defaultSymbol.outline.width,
              },
            };
          } else if (tipoLinea === 'cimPolygon' || evt.graphic.symbol.type === 'cim') {
            geometry = evt.graphic.geometry.rings[0];
            symbol = structuredClone(evt.graphic.symbol);
            type = 'polygon';
          } else {
            // New poligons
            const is3D = store.getters['map/isMap3D'];
            let nombreURL;

            if (is3D) {
              nombreURL = `${tipoLinea}3D`;
            } else {
              nombreURL = tipoLinea;
            }

            const g = await makeCustomPolygon(evt.graphic.geometry.rings[0], nombreURL);

            geometry = evt.graphic.geometry.rings[0];

            evt.graphic.geometry = g.geometry;
            evt.graphic.symbol = g.symbol;
            type = 'polygon';
            symbol = g.symbol;
          }
          attributes.LABEL = planOpSVM.polygonSymbol.label;
        }
        const graphic = {
          // TODO: poner los mismos nombres que en BBDD y cambiar en backend en (plan_operaciones addGraphics)
          attributes,
          geometry,
          type,
          symbol,
        };
        callbackCreate(graphic);
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const graphic = evt.graphics[0];
        const geom = graphic.geometry;

        let newGeometryGraphic;
        if (geom.type === 'point') {
          newGeometryGraphic = [geom.x, geom.y];
        } else if (geom.type === 'polyline') {
          newGeometryGraphic = geom.paths[0];
        } else if (geom.type === 'polygon') {
          newGeometryGraphic = geom.rings[0];
        }

        // geometry.type = graphic.geometry.type
        const newGraphic = {
          ATRIBUTOS: graphic.attributes,
          GEOMETRIA: newGeometryGraphic,
        };

        callbackUpdate(newGraphic);
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICO);
      }
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
};

export const paintEventPlanOperaciones = (event) => {
  let typeEvent = event.function;

  const symbolColor = event.color ? event.color : [255, 0, 0, 0.5];

  isDrawingArrow = false;
  tipoLinea = undefined;
  showBackground = event.showBackground;

  if (typeEvent === 'cancel') {
    planOpSVM.cancel();
  } else if (typeEvent === 'point') {
    planOpSVM.pointSymbol = {
      type: 'simple-marker',
      style: 'circle',
      color: symbolColor,
      size: '16px',
      outline: { color: symbolColor, width: 3 },
      label: { style: 'point', color: symbolColor },
    };
  } else if (typeEvent === 'polyline') {
    planOpSVM.polylineSymbol = null;

    if (event.cimSymbol) {
      planOpSVM.polylineSymbol = null;
      const simboloLimpio = structuredClone(event.cimSymbol);
      planOpSVM.polylineSymbol = simboloLimpio;
      planOpSVM.polylineSymbol.label = {
        style: event.type_line || 'solid',
        color: symbolColor,
      };
    } else {
      const estiloSeguro =
        event.type_line && ['solid', 'shortDash', 'shortDot', 'shortDashDotDot'].includes(event.type_line)
          ? event.type_line
          : 'solid';

      planOpSVM.polylineSymbol = {
        type: 'simple-line',
        color: symbolColor,
        width: 3,
        style: estiloSeguro,
        label: {
          style: event.type_line,
          label: event.label || '',
          color: symbolColor,
        },
      };
    }
  } else if (typeEvent === 'polygon') {
    const is3D = store.getters['map/isMap3D'];

    if (event.cimSymbol && !is3D) {
      planOpSVM.polygonSymbol = structuredClone(event.cimSymbol);
      tipoLinea = 'cimPolygon';
    } else {
      tipoLinea = event.type_polygon;
    }

    planOpSVM.polygonSymbol.label = event.labelPolygon;
  } else if (typeEvent === 'icon') {
    typeEvent = 'point';
    tipoLinea = event.medio_externo;
    planOpSVM.pointSymbol = {
      type: 'picture-marker',
      url: event.src,
      label: event.label,
      width: '35px',
      height: '35px',
      rotation: event.rotation,
      angle: -event.rotation,
    };
  } else if (typeEvent === 'text') {
    typeEvent = 'point';
    planOpSVM.pointSymbol = {
      type: 'text',
      color: symbolColor,
      text: event.text,
      xoffset: 0,
      yoffset: 0,
      angle: event.angle || 0,
      haloColor: 'white',
      haloSize: '2px',
      font: {
        size: event.size,
        family: 'Arial Unicode MS',
        weight: 'bold',
      },
    };
  } else if (typeEvent === 'arrow') {
    typeEvent = 'polyline';
    isDrawingArrow = true;
    planOpSVM.polygonSymbol.color = symbolColor;
    planOpSVM.polygonSymbol.outline.color = symbolColor;
  }
  planOpSVM.pointSymbol.label = event.label ?? 'Sin nombre';
  planOpSVM.create(typeEvent);
};

export const stopPlanOperacionesSketchViewModel = () => {
  if (planOpSVM === null) return;
  const layer = map.findLayerById('layer-plan-operaciones');
  layer.removeAll();
  planOpSVM.cancel();
  planOpSVM.destroy();
};

let isDrawingArrow = false;
let tipoLinea;
let showBackground;
// Crea un poligono con forma de flecha a partir de una linea de dos puntos
async function makeArrow(geom, color) {
  const [Point, Polygon, Graphic, GeometryEngine] = await loadModules([
    'esri/geometry/Point',
    'esri/geometry/Polygon',
    'esri/Graphic',
    'esri/geometry/geometryEngine',
  ]);

  const start = new Point({ x: geom[0][0], y: geom[0][1] });
  const end = new Point({ x: geom[1][0], y: geom[1][1] });

  const dist = start.distance(end);
  const dy = end.y - start.y;
  const dx = end.x - start.x;
  let rads = Math.atan2(dx, dy);

  if (rads < 0) {
    rads = Math.abs(rads);
  } else {
    rads = 2 * Math.PI - rads;
  }

  const degrees = (rads * 180) / Math.PI;

  const width = 0.2 * dist; // 1/5, can adjust

  const bottomLeft = [start.x - width / 10, start.y];
  const bottomRight = [start.x + width / 10, start.y];
  const midLeft = [bottomLeft[0], [bottomLeft[1] + dist - width]];
  const midRight = [bottomRight[0], [bottomRight[1] + dist - width]];
  const edgeLeft = [midLeft[0] - width / 2, midLeft[1]];
  const edgeRight = [midRight[0] + width / 2, midRight[1]];
  const point = [start.x, start.y + dist];

  const rings = [point, edgeLeft, midLeft, bottomLeft, bottomRight, midRight, edgeRight];

  const poly = new Polygon({
    rings: [rings],
    spatialReference: { wkid: 102100 },
  });

  const rotatedPoly = GeometryEngine.rotate(poly, degrees, start);
  const symbol = {
    type: 'simple-fill',
    color,
    outline: {
      color,
      width: '2',
    },
  };

  return new Graphic({
    geometry: rotatedPoly,
    symbol,
  });
}
async function makeCustomLine(geom, url, color) {
  const [Polyline, CIMSymbol] = await loadModules(['esri/geometry/Polyline', 'esri/symbols/CIMSymbol']);

  const poly = new Polyline({
    paths: geom,
    spatialReference: { wkid: 102100 },
  });

  let symbolJSON = null;

  if (symbology.getCIMSymbol && typeof symbology.getCIMSymbol === 'function') {
    symbolJSON = symbology.getCIMSymbol(url, color);
  }

  if (!symbolJSON) {
    symbolJSON = symbology[url];
  }

  let symbol;
  if (symbolJSON) {
    const safeJSON = structuredClone(symbolJSON);

    if (safeJSON.type === 'cim') {
      delete safeJSON.type;
      delete safeJSON.style;
      delete safeJSON.color;
      delete safeJSON.width;
      delete safeJSON.outline;
      delete safeJSON.font;

      symbol = new CIMSymbol(safeJSON);
    } else {
      symbol = symbolJSON;
    }
  }

  return {
    geometry: poly,
    symbol,
  };
}

async function makeCustomPolygon(geom, url, color) {
  try {
    const [Polygon] = await loadModules(['esri/geometry/Polygon']);

    const poly = new Polygon({
      rings: geom,
      spatialReference: { wkid: 102100 },
    });

    const is3D = store.getters['map/isMap3D'];
    const symbol = symbology.getCIMSymbol(url, color, is3D);

    return {
      geometry: poly,
      symbol,
    };
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
}

async function createGraphicsDraw(graphicList) {
  try {
    const [Graphic, Point, Polyline, Polygon, GeometryEngine] = await loadModules([
      'esri/Graphic',
      'esri/geometry/Point',
      'esri/geometry/Polyline',
      'esri/geometry/Polygon',
      'esri/geometry/geometryEngine',
    ]);

    const graphics = [];

    for (let i = 0; i < graphicList.length; i++) {
      const item = graphicList[i];
      let coords = item.geometry || item.GEOMETRIA || item.coordenadas;

      if (!coords || coords === 'undefined' || coords === 'null') continue;
      if (typeof coords === 'string') {
        try {
          coords = JSON.parse(coords);
        } catch (e) {
          console.error('Error al parsear las coords createGraphicsDraw: ', e);
          continue;
        }
      }

      const tipo = item.type || item.TIPO;

      const rawAttr = item.attributes || item.ATRIBUTOS;
      let atributos = {};
      if (typeof rawAttr === 'string') {
        try {
          atributos = JSON.parse(rawAttr);
        } catch (e) {
          console.error('Error al parsear los atributos createGraphicsDraw', e);
          atributos = {};
        }
      } else if (rawAttr) {
        atributos = JSON.parse(JSON.stringify(rawAttr));
      }

      let estiloNombre = 'solid';
      let estiloColor = [34, 139, 34, 255];

      if (atributos && atributos.LABEL) {
        if (atributos.LABEL.style) estiloNombre = atributos.LABEL.style;
        if (atributos.LABEL.color) {
          estiloColor = atributos.LABEL.color;
        }
      }

      let simbolo = null;
      let geometry = null;

      if (tipo === 'point' || tipo === 'icon' || tipo === 'text') {
        geometry = new Point({
          x: coords[0],
          y: coords[1],
          spatialReference: { wkid: 3857 },
        });
        let rawSym = item.symbol || item.SIMBOLO;
        rawSym = typeof rawSym === 'string' ? JSON.parse(rawSym) : rawSym;
        simbolo = rawSym;
      } else if (tipo === 'polyline') {
        let fixedPaths = coords;
        if (
          Array.isArray(coords) &&
          coords.length > 0 &&
          Array.isArray(coords[0]) &&
          typeof coords[0][0] === 'number'
        ) {
          fixedPaths = [coords];
        }

        geometry = new Polyline({
          hasZ: false,
          hasM: true,
          paths: fixedPaths,
          spatialReference: { wkid: 3857 },
        });
        atributos.LONGITUD = GeometryEngine.geodesicLength(geometry);
        try {
          const g = await makeCustomLine(fixedPaths, estiloNombre, estiloColor);
          if (g && g.symbol) {
            simbolo = g.symbol;
          }
        } catch (err) {
          console.warn(err);
        }
      } else if (tipo === 'polygon') {
        geometry = new Polygon({
          hasZ: false,
          hasM: true,
          rings: coords,
          spatialReference: { wkid: 3857 },
        });
        atributos.LONGITUD = GeometryEngine.geodesicLength(geometry);
        atributos.AREA = Math.abs(GeometryEngine.geodesicArea(geometry));
        try {
          const g = await makeCustomPolygon(coords, estiloNombre, estiloColor);
          if (g && g.symbol) simbolo = g.symbol;
        } catch (err) {
          console.warn('Error al crear un custom polygon: ', err);
        }
      }

      if (!simbolo || (typeof simbolo === 'object' && !simbolo.type)) {
        if (geometry.type === 'point') {
          simbolo = {
            type: 'simple-marker',
            style: 'circle',
            color: [255, 0, 0, 1],
            size: 8,
            outline: { color: [255, 255, 255], width: 1 },
          };
        } else if (geometry.type === 'polyline') {
          simbolo = {
            type: 'simple-line',
            color: [34, 139, 34, 1],
            width: 3,
            style: 'solid',
          };
        } else if (geometry.type === 'polygon') {
          simbolo = {
            type: 'simple-fill',
            color: [255, 0, 0, 0.2],
            outline: { color: [255, 0, 0], width: 1 },
          };
        }
      }

      const graphic = new Graphic({
        geometry,
        symbol: simbolo,
        attributes: atributos,
      });
      graphics.push(graphic);
    }

    return graphics;
  } catch (err) {
    console.error('Error createGraphicsDraw', err);
    return [];
  }
}

function sortGraphics(graphics) {
  return graphics.sort((a, b) => {
    if (!a.geometry || !b.geometry) return 0;

    if (a.geometry.type === 'point') {
      return 1;
    }
    if (b.geometry.type === 'point') {
      return -1;
    }
    if (a.geometry.type === 'polyline' && b.geometry.type === 'polygon') {
      return 1;
    }
    if (b.geometry.type === 'polyline' && a.geometry.type === 'polygon') {
      return -1;
    }
    return 1;
  });
}

export const drawGraphicsPlanOperaciones = async (graphicList) => {
  const layer = map.findLayerById('layer-plan-operaciones');
  layer.removeAll();
  try {
    const graphics = await createGraphicsDraw(graphicList);
    const validGraphics = graphics.filter((g) => g.geometry);
    const graphicsSorted = sortGraphics(validGraphics);
    createSizedGraphics(graphicsSorted);
    layer.addMany(graphicsSorted);
  } catch (err) {
    console.error('Error en drawGraphicsPlanOperaciones:', err);
    throw err;
  }
};

async function createSizedGraphics(graphicList) {
  try {
    if (!graphicList) return [];

    if (!mapViewPlanOperaciones) return graphicList;

    const [CIMSymbol] = await loadModules(['esri/symbols/CIMSymbol']);

    const currentZoom = mapViewPlanOperaciones.zoom;
    const size = getSymbolSizeForZoom(currentZoom);

    const cleanList = graphicList.filter((g) => g.symbol);

    return cleanList.map((g) => {
      const baseSymbol = g.symbol;

      if (baseSymbol?.type !== 'cim') return g;

      const symbolData = baseSymbol.data;
      const symbol = symbolData?.symbol;

      if (!symbol) return g;

      let updatedSymbol;

      if (symbol.type === 'CIMPointSymbol') {
        updatedSymbol = new CIMSymbol({
          data: {
            ...symbolData,
            symbol: {
              ...symbol,
              symbolLayers: symbol.symbolLayers
                ? symbol.symbolLayers.map((layer) => ({
                    ...layer,
                    size,
                  }))
                : [],
            },
          },
        });
        g.symbol = updatedSymbol;
      } else if (symbol.type === 'CIMLineSymbol') {
        const sizeArrow = size / 2;
        updatedSymbol = new CIMSymbol({
          data: {
            ...symbolData,
            symbol: {
              ...symbol,
              symbolLayers: symbol.symbolLayers
                ? symbol.symbolLayers.map((layer) => {
                    if (layer.type !== 'CIMVectorMarker' && 'size' in layer) {
                      return { ...layer, size: sizeArrow };
                    }
                    return layer;
                  })
                : [],
            },
          },
        });
        g.symbol = updatedSymbol;
      }

      return g;
    });
  } catch (e) {
    console.warn('Error controlado en createSizedGraphics:', e);
    return graphicList || [];
  }
}

function getSymbolSizeForZoom(zoom) {
  const baseSize = 12;
  const baseZoom = 15;
  const scaleFactor = 1.25; // más cerca de 1 = transición más suave
  return baseSize * Math.pow(scaleFactor, zoom - baseZoom);
}

// #endregion

// #region PERIMETROS ********************************************************************************************************************************************************************
let perimeterSVM = null;
let perimetroSketchCallbacks = null;
let colorPerimetro = [255, 10, 10, 255];

export const initPerimetroSketchViewModel = async (data) => {
  perimetroSketchCallbacks = data;
  await createPerimetroSketchViewModel();
};

function destroyPerimeterSVM() {
  try {
    perimeterSVM.cancel();
    perimeterSVM.destroy();
  } catch (err) {
    VueInst.$log.error(err);
  } finally {
    perimeterSVM = null;
  }
}

async function createPerimetroSketchViewModel() {
  try {
    const [SketchViewModel, GeometryEngine] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel',
      'esri/geometry/geometryEngine',
    ]);

    const callbackCreate = perimetroSketchCallbacks.callbackCreate;
    const callbackUpdate = perimetroSketchCallbacks.callbackUpdate;
    const callbackDelete = perimetroSketchCallbacks.callbackDelete;

    const layer = map.findLayerById('layer-perimetros');
    layer.elevationInfo = { mode: 'on-the-ground' };

    perimeterSVM = new SketchViewModel({
      view: mapView,
      layer,
    });

    perimeterSVM.on(['create', 'delete', 'update'], async (evt) => {
      if (evt.type === 'create' && evt.graphic && evt.graphic.geometry.type === 'polyline') {
        if (evt.state === 'active') {
          if (evt.graphic.geometry.paths[0].length === 3) {
            // Si esta dibujando flecha y tiene 3 puntos (2 reales) terminar el dibujo
            perimeterSVM.complete();
          }
        }
      }

      let needsTickRefresh = false;

      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico
        const attributes = {
          ID_GRAFICO: VueInst.$uuid.createUUID(),
        };

        let geometry;
        let type = evt.graphic.geometry.type;
        let symbol;

        if (type === 'polygon') {
          const g = await makeCustomPolygon(evt.graphic.geometry.rings[0], tipoLineaPerimetro, colorPerimetro);

          geometry = evt.graphic.geometry;

          evt.graphic.geometry = g.geometry;
          evt.graphic.symbol = g.symbol;
          type = 'polygon';
          symbol = symbology.getCIMSymbol(tipoLineaPerimetro, colorPerimetro);

          attributes.LABEL = labelPerimetro || { style: tipoLineaPerimetro };
          if (!labelPerimetro) {
            VueInst.$log.error(new Error(`paintEventPerimetro: labelPerimetro was undefined at create-complete for style ${tipoLineaPerimetro}`));
          }

          const metrics = geodesicMetrics(GeometryEngine, geometry);
          attributes.AREA = metrics.areaM2;
          attributes.LONGITUD = metrics.lengthM;
        }

        const clonedAttributes = JSON.parse(JSON.stringify(attributes));
        evt.graphic.attributes = clonedAttributes;

        const graphic = {
          // TODO: poner los mismos nombres que en BBDD y cambiar en backend en (plan_operaciones addGraphics)
          attributes: clonedAttributes,
          geometry,
          type,
          symbol,
        };
        callbackCreate(graphic);
        needsTickRefresh = true;
      } else if (evt.state === 'start' && evt.type === 'update') {
        const graphicId = evt.graphics[0]?.attributes?.ID_GRAFICO;
        if (graphicId) {
          const tickLayer = await ensurePerimetroTickLayer();
          const staleTicks = tickLayer.graphics.items.filter(
            (tick) => tick.attributes?.ID_GRAFICO_PERIMETRO === graphicId
          );
          if (staleTicks.length) {
            tickLayer.removeMany(staleTicks);
          }
        }
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const graphic = evt.graphics[0];

        if (graphic.geometry.type === 'polygon') {
          const metrics = geodesicMetrics(GeometryEngine, graphic.geometry);
          graphic.attributes.AREA = metrics.areaM2;
          graphic.attributes.LONGITUD = metrics.lengthM;
        }

        const newGraphic = {
          attributes: graphic.attributes,
          geometry: graphic.geometry,
        };

        callbackUpdate(newGraphic);
        needsTickRefresh = true;
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICO);
        needsTickRefresh = true;
      }

      if (needsTickRefresh) {
        await refreshPerimetroTickLayer();
      }
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
}

let tipoLineaPerimetro;
let labelPerimetro;
export const paintEventPerimetro = (event) => {
  if (perimeterSVM === null) return;

  const typeEvent = event.function;

  tipoLineaPerimetro = undefined;
  colorPerimetro = [255, 10, 10, 255];
  labelPerimetro = undefined;

  if (typeEvent === 'cancel') {
    perimeterSVM.cancel();
    return;
  }

  if (typeEvent === 'polygon') {
    if (event.custom) {
      colorPerimetro = event.color;
      // Esto es para pasar de gamma de 0-1 a 0-255
      colorPerimetro[3] *= 255;
      tipoLineaPerimetro = event.type_line; // aunque funcione como polígono, todo viene desde una línea
      event.label.color = event.color;

      if (store.getters['map/isMap3D']) {
        perimeterSVM.polygonSymbol = symbology.getCIMSymbol(tipoLineaPerimetro, colorPerimetro, true);
      }

      labelPerimetro = event.label;
    }
  }
  perimeterSVM.create(typeEvent);
};

export const stopPerimeterSketchViewModel = async () => {
  if (perimeterSVM === null) return;
  const layer = map.findLayerById('layer-perimetros');
  layer.removeAll();

  const tickLayer = await ensurePerimetroTickLayer();
  tickLayer.removeAll();

  destroyPerimeterSVM();
  perimetroSketchCallbacks = null;
};

function resolvePerimetroStyleForLoad(atributos, simboloRaw) {
  const label = atributos?.LABEL;
  if (PERIMETRO_KNOWN_STYLES.includes(label?.style) && label?.color) {
    return { styleKey: label.style, color: label.color };
  }

  const cimResolved = resolvePerimetroStyleFromCIM(simboloRaw?.data);
  if (cimResolved) {
    return { styleKey: cimResolved.styleKey, color: cimResolved.color || colorPerimetro };
  }

  return { styleKey: 'perimetroActivo', color: colorPerimetro };
}

async function createGraphicsDrawPerimeter(graphicList) {
  try {
    const [Graphic, Polygon, CIMSymbol, GeometryEngine] = await loadModules([
      'esri/Graphic',
      'esri/geometry/Polygon',
      'esri/symbols/CIMSymbol',
      'esri/geometry/geometryEngine',
    ]);
    const graphics = [];
    let coords, simboloRaw, atributos;

    for (const item of graphicList) {
      if (item.geometry) {
        // Geometrias de la store
        coords = item.geometry;
        simboloRaw = item.symbol;
        atributos = item.attributes;
      } else {
        // Geometrias de la bbdd
        coords = typeof item.GEOMETRIA === 'string' ? JSON.parse(item.GEOMETRIA) : item.GEOMETRIA;
        simboloRaw = typeof item.SIMBOLO === 'string' ? JSON.parse(item.SIMBOLO) : item.SIMBOLO;
        atributos = typeof item.ATRIBUTOS === 'string' ? JSON.parse(item.ATRIBUTOS) : item.ATRIBUTOS;
      }
      atributos = atributos ? JSON.parse(JSON.stringify(atributos)) : {};

      try {
        const geometry = new Polygon({
          rings: [coords.rings[0]],
          spatialReference: { wkid: 3857 },
        });

        if (atributos.AREA == null || atributos.LONGITUD == null) {
          const metrics = geodesicMetrics(GeometryEngine, geometry);
          atributos.AREA = metrics.areaM2;
          atributos.LONGITUD = metrics.lengthM;
        }

        let finalSymbol;
        const is3D = store.getters['map/isMap3D'];
        if (is3D) {
          const { styleKey, color } = resolvePerimetroStyleForLoad(atributos, simboloRaw);
          finalSymbol =
            symbology.getCIMSymbol(styleKey, color, true) ||
            symbology.getCIMSymbol('perimetroActivo', color, true);
        } else if (simboloRaw?.type === 'cim') {
          finalSymbol = new CIMSymbol({
            data: simboloRaw.data,
          });
        } else {
          const { styleKey, color } = resolvePerimetroStyleForLoad(atributos, simboloRaw);
          const cimSymbol = symbology.getCIMSymbol(styleKey, color) || symbology.getCIMSymbol('perimetroActivo', color);
          finalSymbol = new CIMSymbol({ data: cimSymbol.data });
        }
        const graphic = new Graphic({
          geometry,
          symbol: finalSymbol,
          attributes: atributos,
        });

        graphics.push(graphic);
      } catch (parseError) {
        console.error('Error procesando fila de BBDD:', parseError, item);
      }
    }
    return graphics;
  } catch (err) {
    console.error('error en createGraphicsDrawPerimeter:', err);
    throw err;
  }
}

let displayCoords;
let archivoQML = null;
const R = 6378137.0;
async function perimeterGeoJsonToGraphicsPerimeter(geojson, isPerimeter) {
  try {
    const [Polygon, Polyline, Point] = await loadModules([
      'esri/geometry/Polygon',
      'esri/geometry/Polyline',
      'esri/geometry/Point',
    ]);

    let geometry;
    const graphics = [];
    let forma;
    let simbolo;

    switch (geojson.features[0].geometry.type.toLowerCase()) {
      case 'multipolygon':
        displayCoords = geojson.features[0].geometry.coordinates[0][0][0];
        break;
      case 'point':
        displayCoords = geojson.features[0].geometry.coordinates;
        break;
      case 'polygon':
        displayCoords = geojson.features[0].geometry.coordinates[0][0];
        break;
      case 'geometrycollection':
        displayCoords = geojson.features[0].geometry.geometries[0].coordinates[0][0];
        break;
      default:
        displayCoords = null;
    }

    if (displayCoords && isLatLon(displayCoords[0], displayCoords[1])) {
      displayCoords = changeCoords(displayCoords, 'point');
    }

    for (let i = 0; i < geojson.features.length; i++) {
      if (!geojson.features[i].geometry) {
        continue;
      }

      let tipo = geojson.features[i].geometry.type.toLowerCase();
      const coords = changeCoords(geojson.features[i].geometry.coordinates, tipo);
      const atributos = geojson.features[i].properties;
      let colorDefault = [
        Math.floor(Math.random() * 256),
        Math.floor(Math.random() * 256),
        Math.floor(Math.random() * 256),
        0.5,
      ];

      if (tipo === 'polygon') {
        geometry = new Polygon({
          hasZ: false,
          hasM: true,
          rings: coords,
          spatialReference: { wkid: 3857 },
        });

        forma = await crearPoligonoPerimetro(geometry);

        if (isPerimeter) {
          colorDefault = [0, 0, 0, 0];
          forma.symbol.color = colorDefault;
          forma.symbol.outline.color = [255, 0, 0, 255];
        } else {
          if (atributos.fill) {
            forma.symbol.color = hexToBytes(atributos.fill.substring(1));
          } else {
            if (archivoQML) {
              const categoriasQML = [...archivoQML.getElementsByTagName('category')].map((cat) => ({
                value: cat.getAttribute('value'),
                symbol: cat.getAttribute('symbol'),
              }));
              const simbolosQML = getSymbols(archivoQML);

              forma.symbol.color = getCoords(atributos.Elapsed_Mi, categoriasQML, simbolosQML);
            } else {
              forma.symbol.color = colorDefault;
            }
          }
        }

        simbolo = forma.symbol;
      }

      if (tipo === 'multipolygon') {
        for (let j = 0; j < coords.length; j++) {
          geometry = new Polygon({
            hasZ: false,
            hasM: true,
            rings: coords[j],
            spatialReference: { wkid: 3857 },
          });

          forma = await crearPoligonoPerimetro(geometry);

          if (isPerimeter) {
            colorDefault = [0, 0, 0, 0];
            forma.symbol.color = colorDefault;
            forma.symbol.outline.color = [255, 0, 0, 255];
          } else {
            if (atributos.fill) {
              forma.symbol.color = hexToBytes(atributos.fill.substring(1));
            } else {
              if (archivoQML) {
                const categoriasQML = [...archivoQML.getElementsByTagName('category')].map((cat) => ({
                  value: cat.getAttribute('value'),
                  symbol: cat.getAttribute('symbol'),
                }));
                const simbolosQML = getSymbols(archivoQML);

                forma.symbol.color = getCoords(atributos.Elapsed_Mi, categoriasQML, simbolosQML);
              } else {
                forma.symbol.color = colorDefault;
              }
            }
          }

          simbolo = forma.symbol;

          atributos.ID_GRAFICO = VueInst.$uuid.createUUID();
          const graphic = {
            attributes: atributos,
            geometry,
            type: 'polygon',
            symbol: simbolo,
          };

          graphics.push(graphic);
        }
      }

      if (tipo === 'point') {
        geometry = new Point({
          longitude: coords[0],
          latitude: coords[1],
          coords,
        });

        forma = await crearPuntoPerimetro(geometry);

        if (atributos.fill) {
          forma.symbol.color = hexToBytes(atributos.fill.substring(1));
        } else {
          forma.symbol.color = colorDefault;
        }

        simbolo = forma.symbol;
      }

      if (tipo === 'linestring' || tipo === 'polyline') {
        geometry = new Polyline({
          hasZ: false,
          hasM: true,
          paths: coords,
          spatialReference: { wkid: 3857 },
        });

        forma = await crearLineaPerimetro(geometry);

        if (atributos.fill) {
          forma.symbol.color = hexToBytes(atributos.fill.substring(1));
        } else {
          forma.symbol.color = colorDefault;
        }

        simbolo = forma.symbol;
        tipo = geometry.type;
      }
      atributos.ID_GRAFICO = VueInst.$uuid.createUUID();
      const graphic = {
        attributes: atributos,
        geometry,
        type: tipo,
        symbol: simbolo,
      };
      graphics.push(graphic);
    }
    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

function changeCoords(coords, tipo) {
  let coordX;
  let coordY;

  if (tipo === 'multipolygon' || tipo === 'polygon' || tipo === 'linestring' || tipo === 'polyline') {
    for (let i = 0; i < coords.length; i++) {
      for (let j = 0; j < coords[i].length; j++) {
        if (!(typeof coords[i][j][0] === 'number')) {
          for (let z = 0; z < coords[i][j].length; z++) {
            coordX = coords[i][j][z][0];
            coordY = coords[i][j][z][1];

            if (isLatLon(coordX, coordY)) {
              const coordXFixed = (R * coordX * Math.PI) / 180;
              const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI) / 180 / 2));

              coords[i][j][z][0] = coordXFixed;
              coords[i][j][z][1] = coordYFixed;
            }
          }
        } else {
          coordX = coords[i][j][0];
          coordY = coords[i][j][1];

          if (isLatLon(coordX, coordY)) {
            const coordXFixed = (R * coordX * Math.PI) / 180;
            const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI) / 180 / 2));

            coords[i][j][0] = coordXFixed;
            coords[i][j][1] = coordYFixed;
          }
        }
      }
    }
  } else if (tipo === 'point') {
    coordX = coords[0];
    coordY = coords[1];

    if (isLatLon(coordX, coordY)) {
      const coordXFixed = (R * coordX * Math.PI) / 180;
      const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI) / 180 / 2));

      coords[0] = coordXFixed;
      coords[1] = coordYFixed;
    }
  }

  return coords;
}

export function isLatLon(lon, lat) {
  return lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}

export function getDisplayCoords() {
  return displayCoords;
}

export function coordsForDisplay(coords) {
  if (coords) {
    const lon = (coords[0] / R) * (180 / Math.PI);
    const latRad = 2 * Math.atan(Math.exp(coords[1] / R)) - Math.PI / 2;
    const lat = latRad * (180 / Math.PI);
    return { lon, lat };
  } else {
    return null;
  }
}

function getSymbols(archivoQML) {
  const symbols = [];

  const symbolNodes = archivoQML.getElementsByTagName('symbol');

  for (let i = 0; i < symbolNodes.length; i++) {
    const symbol = symbolNodes[i];
    const name = symbol.getAttribute('name');
    const type = symbol.getAttribute('type');

    const layers = [];
    const layerNodes = symbol.getElementsByTagName('layer');

    for (let j = 0; j < layerNodes.length; j++) {
      const layer = layerNodes[j];
      const layerClass = layer.getAttribute('class');

      const props = {};
      const optionContainers = layer.getElementsByTagName('Option');

      for (let k = 0; k < optionContainers.length; k++) {
        const optionGroup = optionContainers[k];

        if (optionGroup.getAttribute('type') === 'Map') {
          const innerOptions = optionGroup.getElementsByTagName('Option');

          for (let m = 0; m < innerOptions.length; m++) {
            const opt = innerOptions[m];
            const key = opt.getAttribute('name');
            const value = opt.getAttribute('value');
            if (key && value !== null) {
              props[key] = value;
            }
          }
        }
      }

      layers.push({
        class: layerClass,
        props,
      });
    }

    symbols.push({
      name,
      type,
      layers,
    });
  }

  return symbols;
}

function getCoords(elapsed_mi, categorias, simbolos) {
  let index;
  for (let i = 0; i < categorias.length; i++) {
    if (categorias[i].value === elapsed_mi.toString()) {
      index = categorias[i].symbol;
      break;
    }
  }

  let colorQML;
  for (let j = 0; j < simbolos.length; j++) {
    if (index === simbolos[j].name) {
      colorQML = simbolos[j].layers[0].props.color;
      break;
    }
  }

  const colorFinal = [];
  colorFinal[0] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[0]);
  colorFinal[1] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[1]);
  colorFinal[2] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[2]);
  colorFinal[3] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[3]);

  return colorFinal;
}

export const drawGraphicsPerimetro = async (graphicList) => {
  const layer = map.findLayerById('layer-perimetros');
  closeMapPopups();
  const graphics = await createGraphicsDrawPerimeter(graphicList);
  layer.addMany(graphics);
  await refreshPerimetroTickLayer();
};

export const removeGraphicsPerimetro = async (graphicList) => {
  const layer = map.findLayerById('layer-perimetros');
  closeMapPopups();

  const list = graphicList.map((graphic) => graphic.attributes.ID_GRAFICO);
  const graphics = layer.graphics.items.filter((graphic) => list.includes(graphic.attributes.ID_GRAFICO));
  layer.removeMany(graphics);
  await refreshPerimetroTickLayer();
};

export const getIncendioVisiblePerimetros = async (graphics) => {
  try {
    const layerID = map.findLayerById('layer-perimetros');
    const array = [];

    graphics.forEach(async (graphic) => {
      if (layerID.title === 'Gráficos Perímetros' && layerID.graphics.length > 0) {
        for (const layerGraphic of layerID.graphics) {
          const intersects = await intersectGraphic(layerGraphic, graphic);
          if (intersects) {
            array.push(layerGraphic);
          }
        }
      } else {
        return [];
      }
      return array;
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
};

const PERIMETRO_KNOWN_STYLES = ['perimetroPotencial', 'perimetroActivo', 'perimetroEstabilizado'];
const perimetroStyleMemo = new WeakMap();

function resolvePerimetroStyleFromCIM(cimData) {
  const layers = cimData?.symbol?.symbolLayers;
  if (!Array.isArray(layers) || !layers.length) return null;

  if (layers[0]?.type === 'CIMVectorMarker') {
    return { styleKey: 'perimetroEstabilizado', color: layers[1]?.color };
  }

  const hasDashes = layers[0]?.effects?.some((effect) => effect.type === 'CIMGeometricEffectDashes');
  return {
    styleKey: hasDashes ? 'perimetroPotencial' : 'perimetroActivo',
    color: layers[0]?.color,
  };
}

function resolvePerimetroStyle(graphic) {
  if (perimetroStyleMemo.has(graphic)) {
    return perimetroStyleMemo.get(graphic);
  }

  const label = graphic.attributes?.LABEL;
  const symbol = graphic.symbol;
  const cimResolved = symbol?.type === 'cim' ? resolvePerimetroStyleFromCIM(symbol.data) : null;

  const styleKey = PERIMETRO_KNOWN_STYLES.includes(label?.style) ? label.style : cimResolved?.styleKey;

  let result = null;
  if (PERIMETRO_KNOWN_STYLES.includes(styleKey)) {
    const color = cimResolved?.color || label?.color || colorPerimetro;
    result = { styleKey, color };
  }

  perimetroStyleMemo.set(graphic, result);
  return result;
}

const PERIMETRO_TICKS_LAYER_ID = 'layer-perimetros-marcas';
let perimetroTicksLayer = null;

async function ensurePerimetroTickLayer() {
  if (perimetroTicksLayer) return perimetroTicksLayer;

  const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);
  const perimetrosLayer = map.findLayerById('layer-perimetros');

  perimetroTicksLayer = new GraphicsLayer({
    id: PERIMETRO_TICKS_LAYER_ID,
    title: 'Marcas perímetros estabilizados',
    visible: perimetrosLayer ? perimetrosLayer.visible : true,
    opacity: 1,
    listMode: 'hide',
    elevationInfo: { mode: 'on-the-ground' },
  });

  map.addMany([perimetroTicksLayer]);

  if (perimetrosLayer) {
    map.reorder(perimetroTicksLayer, map.layers.indexOf(perimetrosLayer) + 1);
    perimetrosLayer.watch('visible', (visible) => {
      perimetroTicksLayer.visible = visible;
    });
  }

  return perimetroTicksLayer;
}

let tickRefreshToken = 0;
async function refreshPerimetroTickLayer() {
  tickRefreshToken += 1;
  const currentToken = tickRefreshToken;

  const tickLayer = await ensurePerimetroTickLayer();
  if (currentToken !== tickRefreshToken) return;

  tickLayer.removeAll();

  const is3D = store.getters['map/isMap3D'];
  if (!is3D) return;

  const perimetrosLayer = map.findLayerById('layer-perimetros');
  if (!perimetrosLayer) return;

  const [Graphic, Polyline] = await loadModules(['esri/Graphic', 'esri/geometry/Polyline']);
  if (currentToken !== tickRefreshToken) return;

  const tickGraphics = [];
  for (const graphic of perimetrosLayer.graphics.items) {
    const resolved = resolvePerimetroStyle(graphic);
    if (resolved?.styleKey !== 'perimetroEstabilizado') continue;

    const geometry = graphic.geometry;
    if (!geometry || geometry.type !== 'polygon' || !geometry.rings?.length) continue;

    const paths = buildTickPaths(geometry);
    if (!paths.length) continue; // degenerate ring(s), §6 edge case (a) — no ticks, still solid outline

    tickGraphics.push(
      new Graphic({
        geometry: new Polyline({
          paths,
          spatialReference: geometry.spatialReference, // never hard-coded (§10 risk table)
        }),
        symbol: symbology.getCIMSymbol('perimetroEstabilizadoMarcas', resolved.color, true),
        attributes: { ID_GRAFICO_PERIMETRO: graphic.attributes?.ID_GRAFICO },
      })
    );
  }

  if (currentToken !== tickRefreshToken) return;
  if (tickGraphics.length) {
    tickLayer.addMany(tickGraphics);
  }
}

export async function updatePerimetroSymbology() {
  try {
    const layer = map.findLayerById('layer-perimetros');
    if (!layer) return;

    layer.elevationInfo = { mode: 'on-the-ground' };

    const is3D = store.getters['map/isMap3D'];
    const [CIMSymbol] = await loadModules(['esri/symbols/CIMSymbol']);

    layer.graphics.items.forEach((graphic) => {
      const resolved = resolvePerimetroStyle(graphic);
      if (!resolved) return;

      const { styleKey, color } = resolved;
      if (is3D) {
        const symbol3D = symbology.getCIMSymbol(styleKey, color, true);
        if (symbol3D) {
          graphic.symbol = symbol3D;
        }
      } else {
        const cimSymbol = symbology.getCIMSymbol(styleKey, color);
        if (cimSymbol) {
          graphic.symbol = new CIMSymbol({ data: cimSymbol.data });
        }
      }
    });

    if (!is3D) {
      layer.graphics.items.forEach((graphic) => {
        if (!graphic.symbol?.type?.endsWith('-3d')) return;

        console.warn(
          `updatePerimetroSymbology: graphic ${graphic.attributes?.ID_GRAFICO} still had a 3D symbol in 2D mode — repairing`
        );
        const resolved = resolvePerimetroStyle(graphic);
        const { styleKey, color } = resolved || { styleKey: 'perimetroActivo', color: colorPerimetro };
        const cimSymbol = symbology.getCIMSymbol(styleKey, color);
        if (cimSymbol) {
          graphic.symbol = new CIMSymbol({ data: cimSymbol.data });
        }
      });
    }

    await refreshPerimetroTickLayer();
  } catch (err) {
    VueInst.$log.error(err);
  }
}

// #region CREACION DE FORMAS
async function crearPuntoPerimetro(geometry) {
  const forma = {
    coordenadas: {
      longitude: geometry.longitude,
      latitude: geometry.latitude,
    },
    symbol: {
      type: 'simple-marker',
      color: [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), 0.5],
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid',
      },
    },
  };
  return forma;
}

async function crearPoligonoPerimetro(geometry) {
  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-fill',
      color: [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), 0.5],
      style: 'solid',
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid',
      },
    },
    attributes: {},
    popupTemplate: null,
  };
  return forma;
}

async function crearLineaPerimetro(geometry) {
  const forma = {
    aggregateGeometries: null,
    geometry,
    color: 'lightblue',
    symbol: {
      type: 'simple-line',
      color: [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), 1],
      width: 3,
      style: 'solid',
    },
    attributes: {},
    popupTemplate: null,
  };
  return forma;
}

function hexToBytes(hex) {
  const bytes = [];
  for (let c = 0; c < hex.length; c += 2) {
    bytes.push(parseInt(hex.substr(c, 2), 16));
  }
  return bytes;
}

async function fetchJSON(url) {
  return await fetch(url).then((response) => {
    return response.json();
  });
}

export async function dragAndDrop(selectedFile, type, fileNumber) {
  let layer = {};

  if (type === 'geojson') {
    const urlCreada = URL.createObjectURL(selectedFile);
    layer = iniciarLayer('geojson');
    layer.url = urlCreada;

    const archivo = await fetchJSON(urlCreada);
    if (archivo.metadata) {
      layer.id = archivo.metadata.title.split(' ').join('-');
      layer.title = archivo.metadata.title;
    } else {
      layer.id = `layer-geojson ${fileNumber}`;
      layer.title = `${'Layer GeoJSON '}${fileNumber}`;
    }
  } else {
    switch (type) {
      case 'kml':
        layer = iniciarLayer('kml');
        break;
      case 'zip':
        layer = iniciarLayer('shp');
        break;
      default:
        Vue.notify({
          title: 'Error Drag and Drop',
          text: 'Los archivos deben ser .geojson, .kml o .zip(para mostrar archivos .shp)',
          type: 'error',
        });

        return null;
    }
  }

  return layer;
}

function iniciarLayer(formato) {
  const layer = {
    id: '',
    type: '',
    title: '',
    url: '',
    visible: true,
    opacity: 1,
    posicion: 999,
    addListLayer: true,
  };

  switch (formato) {
    case 'geojson':
      layer.type = 'geojson';
      break;
    case 'kml':
      layer.id = 'layerKML';
      layer.type = 'graphicsLayer';
      layer.title = 'Layer KML';
      break;
    case 'shp':
      layer.id = 'layerSHP';
      layer.type = 'graphicsLayer';
      layer.title = 'Layer SHP';
  }

  return layer;
}

export async function insertLayer(archivo, formato, retorno) {
  if (!archivo) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'No se ha proporcionado ningún archivo.',
      type: 'error',
    });

    return null;
  }

  let geojson;

  switch (formato) {
    case 'SHP':
      geojson = await procesarArchivoSHP(archivo);
      break;
    case 'KML':
      geojson = await procesarArchivoKML(archivo);
      break;
  }

  if (!geojson) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'Formato no soportado.',
      type: 'error',
    });

    return null;
  }

  const graficos = await perimeterGeoJsonToGraphicsPerimeter(geojson, false);

  if (!retorno) {
    if (formato === 'KML') {
      const capa = map.findLayerById('layerKML');
      capa.removeAll();
      capa.addMany(graficos);
    } else if (formato === 'SHP') {
      const capa = map.findLayerById('layerSHP');
      capa.removeAll();
      capa.addMany(graficos);
    }
  } else {
    return graficos;
  }

  return graficos;
}

export async function fileToGeoJSON(file) {
  if (!file) throw new Error('No se ha proporcionado ningún archivo.');

  try {
    const zip = await JSZip.loadAsync(file);
    const files = zip.files;

    const shpFileName = Object.keys(files).find((name) => name.endsWith('.shp'));
    const dbfFileName = Object.keys(files).find((name) => name.endsWith('.dbf'));
    const prjFileName = Object.keys(files).find((name) => name.endsWith('.prj'));
    const shxFileName = Object.keys(files).find((name) => name.endsWith('.shx'));

    if (!shpFileName || !dbfFileName || !prjFileName || !shxFileName) {
      throw new Error('Faltan archivos necesarios en el ZIP (.shp, .dbf, .prj, .shx).');
    }

    const [shpArrayBuffer, dbfArrayBuffer, prjString, shxArrayBuffer] = await Promise.all([
      zip.file(shpFileName).async('arraybuffer'),
      zip.file(dbfFileName).async('arraybuffer'),
      zip.file(prjFileName).async('string'),
      zip.file(shxFileName).async('arraybuffer'),
    ]);

    const formData = new FormData();

    formData.append('shp', new Blob([shpArrayBuffer]));
    formData.append('dbf', new Blob([dbfArrayBuffer]));
    formData.append('prj', new Blob([prjString]));
    formData.append('shx', new Blob([shxArrayBuffer]));

    const respuesta = await api.emergencia.addImportedPerimetro(formData);
    const graphics = await perimeterGeoJsonToGraphicsPerimeter(respuesta.data, true);

    const layer = map.findLayerById('layer-perimetros');
    layer.removeAll();
    layer.addMany(graphics);

    return graphics;
  } catch (error) {
    console.error('Error procesando el archivo ZIP:', error);
    throw error;
  }
}

async function procesarArchivoSHP(archivo) {
  const zip = await JSZip.loadAsync(archivo);
  const archivos = zip.files;

  const nombreShp = Object.keys(archivos).find((n) => n.endsWith('.shp'));
  const nombreDbf = Object.keys(archivos).find((n) => n.endsWith('.dbf'));
  const nombrePrj = Object.keys(archivos).find((n) => n.endsWith('.prj'));
  const nombreShx = Object.keys(archivos).find((n) => n.endsWith('.shx'));
  const nombreQml = Object.keys(archivos).find((n) => n.endsWith('.qml'));

  if (!nombreShp || !nombreDbf || !nombrePrj || !nombreShx) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'Faltan archivos necesarios en el ZIP (.shp, .dbf, .prj, .shx).',
      type: 'error',
    });

    return null;
  }
  if (tienecarpeta(archivos, nombreQml !== undefined)) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'El archivo ZIP no debe contener carpetas, solo los archivos necesarios (.shp, .dbf, .prj, .shx, .qml (opcional))',
      type: 'error',
    });

    return null;
  }

  const [shp, dbf, prj, shx] = await Promise.all([
    zip.file(nombreShp).async('arraybuffer'),
    zip.file(nombreDbf).async('arraybuffer'),
    zip.file(nombrePrj).async('string'),
    zip.file(nombreShx).async('arraybuffer'),
  ]);

  if (nombreQml) {
    const qml = await zip.file(nombreQml).async('string');

    archivoQML = new DOMParser().parseFromString(qml, 'text/xml');
  }

  const formData = new FormData();
  formData.append('shp', new Blob([shp]));
  formData.append('dbf', new Blob([dbf]));
  formData.append('prj', new Blob([prj]));
  formData.append('shx', new Blob([shx]));

  const respuesta = await api.emergencia.addImportedPerimetro(formData);
  return respuesta.data;
}

function tienecarpeta(archivo, tieneQML) {
  let index = null;

  const archivos = [
    Object.keys(archivo).find((n) => n.endsWith('.shp')),
    Object.keys(archivo).find((n) => n.endsWith('.dbf')),
    Object.keys(archivo).find((n) => n.endsWith('.prj')),
    Object.keys(archivo).find((n) => n.endsWith('.shx')),
  ];

  if (tieneQML) {
    archivos.push(Object.keys(archivo).find((n) => n.endsWith('.qml')));
  }

  for (let i = 0; i < archivos.length; i++) {
    index = archivos[i].indexOf('/');
    if (index !== -1) {
      return true;
    }
  }

  return false;
}

async function procesarArchivoKML(archivo) {
  const textoKML = await archivo.text();
  const domKML = new DOMParser().parseFromString(textoKML, 'application/xml');
  const geojson = toGeoJSON.kml(domKML);

  geojson.crs = {
    type: 'name',
    properties: {
      name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
    },
  };

  return reproyectarGeoJSON(geojson);
}

function reproyectarGeoJSON(geojson) {
  const origen = 'EPSG:4326';
  const destino = 'EPSG:3857';
  let status = true;

  geojson.features = geojson.features.map((feature) => {
    if (!feature.geometry) {
      Vue.notify({
        title: 'Error Drag and Drop',
        text: 'Error al procesar el archivo kml',
        type: 'error',
      });

      status = false;
      return null;
    }
    const tipo = feature.geometry.type;
    const coords = feature.geometry.coordinates;

    if (tipo === 'Polygon') {
      feature.geometry.coordinates = coords.map((anillo) =>
        anillo.map(([lon, lat]) => proj4(origen, destino, [lon, lat]))
      );
    } else if (tipo === 'MultiPolygon') {
      feature.geometry.coordinates = coords.map((poligono) =>
        poligono.map((anillo) => anillo.map(([lon, lat]) => proj4(origen, destino, [lon, lat])))
      );
    }

    return feature;
  });

  if (!status) {
    return null;
  }

  return geojson;
}
// #endregion

// #endregion *******************************************************************************************************************************************************************

// #region SIMULACION
let simulacionSVM = null;
// let simulacionGraphics = []
// let simulationGraphics.isocronas = []
// let longitudLlamaGraphics = []
// let intensidadLlamaGraphics = []
// let velocidadPropagacionGraphics = []

let simulationGraphics = {
  simulacion: [],
  isocronas: [],
  longitudLlama: [],
  intensidadLlama: [],
  velocidadPropagacion: [],
  graficosSimulacion: [],
};

const TIPO_GRAFICO_FOCO = 'foco';

export const initSimulacionSketchViewModel = async (data) => {
  // TODO:
  try {
    const [SketchViewModel, projection] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel',
      'esri/geometry/projection',
    ]);
    const event = data.event;
    const callback = data.callback;

    let color;

    switch (data.categoria) {
      case 'foco':
        color = [255, 0, 0, 0.5];
        break;
      case 'combustible':
        color = [183, 244, 216, 0.5];
        break;
      case 'zonaNoCombustible':
        color = [15, 10, 222, 0.5];
        break;
    }

    const layer = map.findLayerById('layer-sketch-simulacion');

    simulacionSVM = new SketchViewModel({
      view: mapView,
      layer,
      pointSymbol: {
        type: 'simple-marker',
        color,
        size: 6,
      },
      polylineSymbol: {
        type: 'simple-line',
        style: 'solid',
        color,
        width: 2,
      },
      polygonSymbol: {
        type: 'simple-fill',
        style: 'solid',
        color,
      },
    });
    simulacionSVM.on(['create', 'delete', 'update'], async (evt) => {
      let symbol, coordinates;
      let geometry = null;
      if (evt.state === 'complete' && evt.type === 'create') {
        await projection.load();
        geometry = projection.project(evt.graphic.geometry, {
          wkid: 25830,
        });

        if (evt.graphic.geometry.type === 'point' && evt.graphic.symbol.type === 'simple-marker') {
          symbol = simulacionSVM.pointSymbol;
          coordinates = [geometry.x, geometry.y];
        } else if (evt.graphic.geometry.type === 'polyline') {
          symbol = simulacionSVM.polylineSymbol;
          coordinates = geometry.paths[0];
        } else if (evt.graphic.geometry.type === 'polygon') {
          symbol = simulacionSVM.polygonSymbol;
          coordinates = geometry.rings[0];
        }

        const graphic = {
          coordinates,
          type: evt.graphic.geometry.type,
          symbol,
        };
        callback(graphic);
      }
    });

    // Create graphic
    simulacionSVM.create(event);
  } catch (err) {
    VueInst.$log.error(err);
  }
};

export const addPointElement = async (data) => {
  const layer = map.findLayerById('layer-sketch-simulacion');

  const callback = data.callback;

  const geometry = {
    type: 'point',
    latitude: data.latitude,
    longitude: data.longitude,
  };

  let color;

  switch (data.categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5];
      break;
    case 'combustible':
      color = [183, 244, 216, 0.5];
      break;
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5];
      break;
  }

  const punto = {
    geometry,
    // attributes: null, // attributes,
    symbol: {
      type: 'simple-marker',
      color,
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid',
      },
    },
  };

  layer.add(punto);
  callback(punto);
};

export const drawSimulationElements = async (elements) => {
  elements = JSON.parse(JSON.stringify(elements));

  const layer = map.findLayerById('layer-sketch-simulacion');
  // layer.graphics.removeAll()

  for (let i = 0; i < elements.length; i++) {
    let forma;

    if (elements[i].geometry.symbol.type === 'esriSMS' || elements[i].geometry.symbol.type === 'simple-marker') {
      forma = await crearPunto(elements[i].latitud, elements[i].longitud, elements[i].categoria);
    }

    if (elements[i].geometry.symbol.type === 'esriSLS') {
      forma = await crearLinea(elements[i].geometry.coordinates, elements[i].categoria);
    }

    if (elements[i].geometry.symbol.type === 'esriSFS') {
      forma = await crearPoligono(elements[i].geometry.coordinates, elements[i].categoria);
    }
    layer.add(forma);
  }
};

async function crearPunto(lat, lon, categoria) {
  const geometry = {
    type: 'point',
    latitude: lat,
    longitude: lon,
  };

  let color;

  switch (categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5];
      break;
    case 'combustible':
      color = [183, 244, 216, 0.5];
      break;
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5];
      break;
  }

  const forma = {
    geometry,
    symbol: {
      type: 'simple-marker',
      color,
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid',
      },
    },
  };
  return forma;
}

async function crearLinea(paths, categoria) {
  const [Polyline] = await loadModules(['esri/geometry/Polyline']);
  let geometry = new Polyline({
    paths,
    spatialReference: {
      wkid: 25830,
    },
  });

  geometry = await projectGeometryForView(geometry);

  let color;

  switch (categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5];
      break;
    case 'combustible':
      color = [183, 244, 216, 0.5];
      break;
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5];
      break;
  }

  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-line',
      color,
      width: 2,
      style: 'solid',
    },
    attributes: {},
    popupTemplate: null,
  };

  return forma;
}

async function crearPoligono(rings, categoria) {
  const [Polygon] = await loadModules(['esri/geometry/Polygon']);
  let geometry = new Polygon({
    rings,
    spatialReference: {
      wkid: 25830,
    },
  });

  geometry = await projectGeometryForView(geometry);

  let color;

  switch (categoria) {
    case 'foco':
      color = [255, 42, 4, 0.5];
      break;
    case 'combustible':
      color = [183, 244, 216, 0.5];
      break;
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5];
      break;
  }

  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-fill',
      color,
      style: 'solid',
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid',
      },
    },
    attributes: {},
    popupTemplate: null,
  };

  return forma;
}

export async function obtenerCentro(geometriaData, wkidEntrada) {
  const [Polygon, Polyline] = await loadModules(['esri/geometry/Polygon', 'esri/geometry/Polyline']);

  const spatialReferenceEntrada = { wkid: wkidEntrada };
  let puntoCentro;
  const { type } = geometriaData;
  const coordinates = geometriaData.coordinates ? geometriaData.coordinates : geometriaData.rings;
  const rings = !!geometriaData.rings;

  if (type === 'point') {
    if (!coordinates || coordinates.length < 2) {
      throw new Error('Las coordenadas del Point son inválidas.');
    }
    puntoCentro = { x: coordinates[0], y: coordinates[1] };
  } else if (type === 'polygon') {
    if (rings) {
      const coordinatesLimpias = coordinates.filter((punto) => Array.isArray(punto) && punto.length === 2);
      const poligono = new Polygon({
        rings: coordinatesLimpias,
        spatialReference: spatialReferenceEntrada,
      });
      puntoCentro = poligono.centroid;
    } else {
      const poligono = new Polygon({
        rings: [coordinates],
        spatialReference: spatialReferenceEntrada,
      });
      puntoCentro = poligono.centroid;
    }
  } else if (type === 'polyline') {
    if (rings) {
      const coordinatesLimpias = coordinates.filter((punto) => Array.isArray(punto) && punto.length === 2);
      const polilinea = new Polyline({
        paths: coordinatesLimpias,
        spatialReference: spatialReferenceEntrada,
      });
      puntoCentro = polilinea.extent.center;
    } else {
      const polilinea = new Polyline({
        paths: [coordinates],
        spatialReference: spatialReferenceEntrada,
      });
      puntoCentro = polilinea.extent.center;
    }
  } else {
    throw new Error(`Tipo de geometría no soportado para calcular el centro: ${type}`);
  }

  const pointWebMercator = {
    z: 0,
    spatialReference: {
      wkid: 102100,
      latestWkid: 3857,
    },
  };

  if (wkidEntrada === 25830) {
    const [x3857, y3857] = convertirETRS89UTM30NToMercXY(puntoCentro.x, puntoCentro.y);

    pointWebMercator.x = x3857;
    pointWebMercator.y = y3857;
  } else {
    pointWebMercator.x = puntoCentro.x;
    pointWebMercator.y = puntoCentro.y;
  }

  return pointWebMercator;
}

export const clearSimulacionSketch = () => {
  if (!map) return;
  const layer = map.findLayerById('layer-sketch-simulacion');
  layer.removeAll();
};
export const stopSimulacionSketchViewModel = () => {
  simulacionSVM.cancel();
};

export const convertListGeometryToLatLon = (listgeometry) => {
  const newList = [];
  for (const geometry of listgeometry) {
    const newGeom = [];
    for (const point of geometry) {
      const newpoint = convertirETRS89ToLatLon(point[0], point[1]);

      const temp = newpoint[0];
      newpoint[0] = newpoint[1];
      newpoint[1] = temp;

      newGeom.push(newpoint);
    }
    newList.push(newGeom);
  }

  return newList;
};

export const paintSimulacion = async (simulacion) => {
  if (map) {
    try {
      let response;
      if (simulationGraphics[simulacion.id].length === 0) {
        switch (simulacion.id) {
          case 'simulacion':
            response = await api.simulacion.getSimulacion(simulacion.sim.ID_SIMULACION, 28530);

            if (response.data.simStatus) {
              // Hacer que salga error cuando el simStatus no sea correcto
              return response.data.simStatus;
            }

            simulationGraphics.simulacion = await createGraphicsSimulacion(response.data);
            simulationGraphics.isocronas = await createGraphicsIsocronas(response.data);
            await mergeOriginGraphics(simulacion.sim.GRAFICOS);
            simulationGraphics.graficosSimulacion = simulacion.sim.GRAFICOS
              ? await createAuxiliarGraphicsSimulacion(simulacion.sim.GRAFICOS)
              : [];

            break;
          case 'longitudLlama':
            response = await api.simulacion.getFlameLength(simulacion.sim.ID_SIMULACION, 28530);
            simulationGraphics[simulacion.id] = await createGraphics(response.data);
            break;
          case 'intensidadLlama':
            response = await api.simulacion.getIntensity(simulacion.sim.ID_SIMULACION, 28530);
            simulationGraphics[simulacion.id] = await createGraphics(response.data);
            break;
          case 'velocidadPropagacion':
            response = await api.simulacion.getSpreadRate(simulacion.sim.ID_SIMULACION, 28530);
            simulationGraphics[simulacion.id] = await createGraphics(response.data);
            break;
          default:
            break;
        }
      }
      store.dispatch('shareMap/paintSimulation', { response, simulacion });
      paintSimulacionLayer(simulacion);
      centerSimulacion(simulacion.sim.TIPO);
    } catch (err) {
      // console.error('Error: ', err)
      VueInst.$log.error('Error: ', err);

      throw err;
    }
  }
};

function paintSimulacionLayer(simulacion) {
  const layerSimulacion = map.findLayerById('layer-simulacion');
  layerSimulacion.removeAll();
  const layerIsocronas = map.findLayerById('layer-isocronas');
  layerIsocronas.removeAll();
  const layerGraficosSimulacion = map.findLayerById('layer-graficos-simulacion');
  layerGraficosSimulacion.removeAll();

  if (simulacion.id === 'simulacion') {
    simulationGraphics.isocronas.sort((a, b) => {
      const isPointA = a.geometry.type === 'point';
      const isPointB = b.geometry.type === 'point';

      if (isPointA !== isPointB) {
        return isPointA ? 1 : -1;
      }

      return b.attributes.Elapsed_Mi - a.attributes.Elapsed_Mi;
    });

    const initialElapsedMin = simulationGraphics.simulacion.reduce(
      (max, g) => Math.max(max, g.attributes.Elapsed_Mi),
      0
    );
    const initialGraphics = simulationGraphics.simulacion.filter(
      (x) => x.attributes.Elapsed_Mi <= initialElapsedMin
    );
    const initialOrigin = initialGraphics.filter((g) => g.attributes.TIPO_GRAFICO === TIPO_GRAFICO_FOCO);
    layerSimulacion.graphics.items = [
      ...initialGraphics.filter((g) => g.attributes.TIPO_GRAFICO !== TIPO_GRAFICO_FOCO),
      ...initialOrigin,
    ];
    layerIsocronas.graphics.items = [
      ...simulationGraphics.isocronas.filter((x) => x.attributes.Elapsed_Mi <= initialElapsedMin),
      ...initialOrigin.map((g) => g.clone()),
    ];

    layerGraficosSimulacion.graphics.items = simulationGraphics.graficosSimulacion;
    layerGraficosSimulacion.visible = true;
    layerIsocronas.visible = true;
  } else {
    layerSimulacion.graphics.items = simulationGraphics[simulacion.id];
  }

  layerSimulacion.visible = true;
}

async function createAuxiliarGraphicsSimulacion(data) {
  try {
    const graphics = [];

    const auxiliares = (data ?? []).filter((g) => g.TIPO_GRAFICO !== TIPO_GRAFICO_FOCO);
    for (let i = 0; i < auxiliares.length; i++) {
      switch (auxiliares[i].TIPO_GEOMETRIA) {
        case 'polyline':
          graphics.push(await crearLinea(auxiliares[i].GEOMETRIA, auxiliares[i].TIPO_GRAFICO));
          break;
        case 'polygon':
          graphics.push(await crearPoligono(auxiliares[i].GEOMETRIA, auxiliares[i].TIPO_GRAFICO));
          break;
      }
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

async function createOriginGraphics(graficos) {
  try {
    const focos = (graficos ?? []).filter((g) => g.TIPO_GRAFICO === TIPO_GRAFICO_FOCO);
    if (focos.length === 0) return [];

    const [Point, Graphic] = await loadModules(['esri/geometry/Point', 'esri/Graphic']);

    const graphics = [];

    for (let i = 0; i < focos.length; i++) {
      const foco = focos[i];
      let geometry;
      let symbol;

      switch (foco.TIPO_GEOMETRIA) {
        case 'point':
          geometry = new Point({
            x: foco.GEOMETRIA[0],
            y: foco.GEOMETRIA[1],
            spatialReference: { wkid: 25830 },
          });
          symbol = {
            type: 'simple-marker',
            color: [255, 0, 0, 0.75],
            size: 6,
            outline: { type: 'simple-line', color: [50, 50, 50, 255], width: 1, style: 'solid' },
          };
          geometry = await projectGeometryForView(geometry);
          break;
        case 'polyline': {
          const forma = await crearLinea(foco.GEOMETRIA, 'foco');
          geometry = forma.geometry;
          symbol = forma.symbol;
          break;
        }
        case 'polygon': {
          const forma = await crearPoligono(foco.GEOMETRIA, 'foco');
          geometry = forma.geometry;
          symbol = forma.symbol;
          break;
        }
        default:
          continue;
      }

      graphics.push(
        new Graphic({ geometry, symbol, attributes: { Elapsed_Mi: 0, TIPO_GRAFICO: TIPO_GRAFICO_FOCO } })
      );
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
    return [];
  }
}

async function mergeOriginGraphics(graficos) {
  const originGraphics = await createOriginGraphics(graficos);
  if (originGraphics.length === 0) return;
  simulationGraphics.simulacion = [...originGraphics, ...simulationGraphics.simulacion];
}

async function projectGeometryForView(geometry) {
  if (!mapView?.spatialReference) return geometry;

  if (geometry.spatialReference?.wkid === mapView.spatialReference?.wkid) {
    return geometry;
  }

  const [projection] = await loadModules([
    'esri/geometry/projection',
  ]);

  await projection.load();

  return projection.project(
    geometry,
    mapView.spatialReference
  );
}

async function createGraphicsSimulacion(response) {
  try {
    const [Polygon, Graphic, GeometryEngine] = await loadModules([
      'esri/geometry/Polygon',
      'esri/Graphic',
      'esri/geometry/geometryEngine',
    ]);

    const featuresJson = response.features;

    const groups = featuresJson.reduce((acc, item) => {
      const elapsedMin = item.properties?.Elapsed_Mi;
      if (!acc[elapsedMin]) acc[elapsedMin] = [];
      acc[elapsedMin].push(item);
      return acc;
    }, {});

    const timeSteps = Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b);

    const initColor = [255, 255, 0];
    const endColor = [255, 0, 0];
    const colorGradient = getColorGradient(initColor, endColor, timeSteps.length);

    const graphics = [];
    let geometriaAcumuladaAnterior = null;

    for (let i = 0; i < timeSteps.length; i++) {
      const currentTime = timeSteps[i];
      const featuresInGroup = groups[currentTime];

      const rawGeometries = [];

      featuresInGroup.forEach((f) => {
        if (!f.geometry) return;

        let rings = [];
        if (f.geometry.type === 'MultiPolygon') {
          rings = f.geometry.coordinates.flat(1);
        } else if (f.geometry.type === 'Polygon') {
          rings = f.geometry.coordinates;
        }

        const polygon = new Polygon({
          hasZ: false,
          hasM: false,
          rings,
          spatialReference: { wkid: 4326 },
        });
        rawGeometries.push(polygon);
      });

      if (rawGeometries.length === 0) continue;
      let unionGeometry = GeometryEngine.union(rawGeometries);
      unionGeometry = GeometryEngine.simplify(unionGeometry);

      let geometriaParaPintar = unionGeometry;

      if (geometriaAcumuladaAnterior) {
        geometriaParaPintar = GeometryEngine.difference(unionGeometry, geometriaAcumuladaAnterior);
      }

      if (geometriaParaPintar) {
        const color = colorGradient[i];

        const symbol = {
          type: 'simple-fill',
          color: [color[0], color[1], color[2], 0.8],
          style: 'solid',
          outline: {
            color: 'white',
            width: 0,
          },
        };

        const popupTemplate = {
          title: 'Simulación',
          content: [
            {
              type: 'fields',
              fieldInfos: [
                {
                  fieldName: 'Elapsed_Mi',
                  label: 'Tiempo (min)',
                },
              ],
            },
          ],
        };

        const graphic = new Graphic({
          geometry: geometriaParaPintar,
          attributes: {
            Elapsed_Mi: currentTime,
            ...featuresInGroup[0].properties,
          },
          symbol,
          popupTemplate,
        });

        graphics.push(graphic);
      }

      if (geometriaAcumuladaAnterior) {
        geometriaAcumuladaAnterior = GeometryEngine.union([geometriaAcumuladaAnterior, unionGeometry]);
      } else {
        geometriaAcumuladaAnterior = unionGeometry;
      }
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

async function createGraphics(response) {
  try {
    const [Polygon, Graphic] = await loadModules(['esri/geometry/Polygon', 'esri/Graphic']);

    const featuresJson = response.features;

    const initColor = [255, 255, 0];
    const endColor = [255, 0, 0];
    const colorGradient = getColorGradient(initColor, endColor, featuresJson.length);

    // Crear las geometrias juntando los poligonos del mismo ElapsedMin
    const mergedFeatures = [];
    featuresJson.forEach((v) => {
      const features = v;
      let geometry;
      if (features.geometry.coordinates.length > 1) {
        features.geometry.coordinates.forEach((geo) => {
          const newGeometry = new Polygon({
            hasZ: false,
            hasM: false,
            rings: geo,
            spatialReference: {
              wkid: 4326,
            },
          });

          mergedFeatures.push({
            geometry: newGeometry,
            properties: v.properties,
          });
        });
      } else {
        geometry = new Polygon({
          hasZ: false,
          hasM: false,
          rings: features.geometry.coordinates,
          spatialReference: {
            wkid: 4326,
          },
        });

        mergedFeatures.push({
          geometry,
          properties: v.properties,
        });
      }
    });

    // Crear los graficos
    const graphics = [];
    for (let i = 0; i < mergedFeatures.length; i++) {
      const f = mergedFeatures[i];
      const geometry = f.geometry;

      const color = colorGradient[i];
      const symbol = {
        type: 'simple-fill',
        color: f.properties.fill ? f.properties.fill : [color[0], color[1], color[2], 1],
        style: 'solid',
        outline: {
          color: 'white',
          width: 0,
        },
      };

      const popupTemplate = {
        title: 'Simulación',
        content: [
          {
            type: 'fields',
            fieldInfos: [
              {
                fieldName: 'UpperValue',
                label: 'Valor por encima',
              },
              {
                fieldName: 'lowerValue',
                label: 'Valor por debajo',
              },
            ],
          },
        ],
      };

      const graphic = new Graphic({
        geometry,
        attributes: f.properties,
        symbol,
        popupTemplate,
      });

      graphics.push(graphic);
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

async function createGraphicsIsocronas(response) {
  try {
    const [Point, Polygon, Graphic, GeometryEngine] = await loadModules([
      'esri/geometry/Point',
      'esri/geometry/Polygon',
      'esri/Graphic',
      'esri/geometry/geometryEngine',
    ]);

    const featuresJson = response.features;

    // Agrupar por ElapsedMin
    const groups = featuresJson.reduce((groups, item) => {
      const elapsedMin = item.properties?.Elapsed_Mi;
      const group = groups[elapsedMin] || [];
      group.push(item);
      groups[elapsedMin] = group;
      return groups;
    }, {});

    const validGroups = Object.fromEntries(
      Object.entries(groups)
        .map(([elapsedMin, features]) => [elapsedMin, features.filter((f) => f.geometry)])
        .filter(([, features]) => features.length > 0)
    );

    const sortedElapsedMins = Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b);

    const initColor = [255, 255, 0];
    const endColor = [255, 0, 0];
    const colorGradient = getColorGradient(initColor, endColor, sortedElapsedMins.length);
    const colorByElapsedMin = {};
    sortedElapsedMins.forEach((elapsedMin, idx) => {
      colorByElapsedMin[elapsedMin] = colorGradient[idx];
    });

    // Crear las geometrias juntando los poligonos del mismo ElapsedMin
    const mergedFeatures = [];
    let elapsedMinText = 0;

    Object.entries(validGroups).forEach(([_k, v]) => {
      const features = v;
      const type = features[0].geometry.type;

      let geometry;

      switch (type) {
        case 'MultiPolygon':
          for (let j = 0; j < features[0].geometry.coordinates.length; j++) {
            geometry = new Polygon({
              hasZ: false,
              hasM: false,
              rings: features.map((x) => x.geometry.coordinates[j]).flat(),
              spatialReference: {
                wkid: 4326,
              },
            });

            mergedFeatures.push({
              geometry,
              properties: v[0].properties,
            });
          }
          break;
        case 'Polygon':
          geometry = new Polygon({
            hasZ: false,
            hasM: false,
            rings: features.map((x) => x.geometry.coordinates).flat(),
            spatialReference: {
              wkid: 4326,
            },
          });
          break;
      }

      mergedFeatures.push({
        geometry,
        properties: v[0].properties,
      });
    });

    // Crear los graficos
    const graphics = [];
    for (let i = 0; i < mergedFeatures.length; i++) {
      const f = mergedFeatures[i];
      let geometry = f.geometry;

      if (i > 0) {
        const prevFeature = mergedFeatures[i - 1];
        const prevGeometry = prevFeature.geometry;
        geometry = GeometryEngine.difference(geometry, prevGeometry);

        if (!geometry) {
          continue;
        }
      }

      const color = colorByElapsedMin[f.properties.Elapsed_Mi];

      const symbol = {
        type: 'simple-fill',
        color: [color[0], color[1], color[2], 1],
        style: 'solid',
        outline: {
          color: 'white',
          width: 1,
        },
      };
      const popupTemplate = {
        title: 'Simulación',
        content: [
          {
            type: 'fields',
            fieldInfos: [
              {
                fieldName: 'Elapsed_Mi',
                label: 'Elapsed Mins',
              },
            ],
          },
        ],
      };

      const graphic = new Graphic({
        geometry,
        attributes: f.properties,
        symbol,
      });

      const punto = new Point({
        x: geometry.rings[0][0][0],
        y: geometry.rings[0][0][1],
      });

      const graphicText = new Graphic({
        geometry: punto,
        attributes: f.properties,
        symbol: {
          type: 'text',
          color: 'black',
          haloColor: 'white',
          haloSize: '1px',
          text: '',
          xoffset: 0,
          yoffset: 0,
          font: {
            size: 8,
            family: 'Arial Unicode MS',
            weight: 'bold',
          },
        },
        popupTemplate,
      });

      if (f.properties.Elapsed_Mi !== elapsedMinText) {
        elapsedMinText = f.properties.Elapsed_Mi;
        graphicText.symbol.text = f.properties.Elapsed_Mi;
      }

      graphics.push(graphicText);
      graphics.push(graphic);
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
  }
}

export const updateSimulacionTime = (elapsedMin) => {
  if (map) {
    // Simulacion
    let layerSimulacion = map.findLayerById('layer-simulacion');
    layerSimulacion.removeAll();

    let graphics = simulationGraphics.simulacion.filter((x) => x.attributes.Elapsed_Mi <= elapsedMin);
    const originGraphics = graphics.filter((g) => g.attributes.TIPO_GRAFICO === TIPO_GRAFICO_FOCO);
    layerSimulacion.addMany([
      ...graphics.filter((g) => g.attributes.TIPO_GRAFICO !== TIPO_GRAFICO_FOCO),
      ...originGraphics,
    ]);
    layerSimulacion.visible = true;

    // Isocronas
    const layerIsocronas = map.findLayerById('layer-isocronas');
    layerIsocronas.removeAll();

    graphics = simulationGraphics.isocronas.filter((x) => x.attributes.Elapsed_Mi <= elapsedMin);
    layerIsocronas.addMany([...graphics, ...originGraphics.map((g) => g.clone())]);

    const simIndex = map.layers.indexOf(layerSimulacion);
    map.reorder(layerIsocronas, simIndex + 1);

    loadModules(['esri/geometry/geometryEngine']).then(([geometryEngine]) => {
      let superficie = 0;
      let elapsedMinPrev = 0;
      const unionGeometrias = [];

      for (let i = 0; i < graphics.length; i++) {
        const min = graphics[i].attributes.Elapsed_Mi;

        if (min !== elapsedMinPrev) {
          unionGeometrias.push(graphics[i].geometry);
          elapsedMinPrev = min;
        }
      }

      if (unionGeometrias.length === 0) {
        VueInst.$eventHub.$emit('superficieSimulacion', 0);
        return;
      }

      const geometriaUnida = geometryEngine.union(unionGeometrias);
      const area = Math.abs(geometryEngine.geodesicArea(geometriaUnida, 'square-meters'));
      superficie = area / 10000;

      VueInst.$eventHub.$emit('superficieSimulacion', Math.round(superficie * 100) / 100);
    });
  }
};

export const centerSimulacion = async (tipo) => {
  if (map) {
    const layer = map.findLayerById('layer-simulacion');
    let auxCentro = null;

    let target;

    if (tipo === 'INCENDIO') {
      const framed =
        layer.graphics.items?.length > 0 ? layer.graphics.items : simulationGraphics.simulacion;

      if (!framed?.length) return;

      target = framed.every((g) => g.geometry?.type === 'point')
        ? { target: framed, zoom: 14 }
        : framed;
    } else {
      auxCentro = {
        x: simulationGraphics.simulacion.x,
        y: simulationGraphics.simulacion.y,
      };

      target = {
        center: [auxCentro.x, auxCentro.y],
        zoom: 13,
      };
    }

    mapView
      .goTo(target, {
        duration: 3000,
        easing: 'in-expo',
      })
      .catch((error) => {
        if (error.name !== 'view:goto-interrupted') {
          console.error(error);
        }
      });
  }
};

export const clearSimulacion = async () => {
  if (map) {
    const layerSim = map.findLayerById('layer-simulacion');
    layerSim.removeAll();
    const layerIso = map.findLayerById('layer-isocronas');
    layerIso.removeAll();
    const layerGraph = map.findLayerById('layer-graficos-simulacion');
    layerGraph.removeAll();
    const layerWind = map.findLayerById('layer-viento');

    const { features } = await layerWind.queryFeatures();

    layerWind.applyEdits({
      deleteFeatures: features,
    });

    const layerLines = map.findLayerById('layer-viento-lineas');
    const { features: featuresLineas } = await layerLines.queryFeatures();

    layerLines.applyEdits({
      deleteFeatures: featuresLineas,
    });

    simulationGraphics = {
      simulacion: [],
      isocronas: [],
      longitudLlama: [],
      intensidadLlama: [],
      velocidadPropagacion: [],
      graficosSimulacion: [],
    };
  }
};

export const exportSimulacion = async (simulacion) => {
  if (map) {
    let response = await api.simulacion.getSimulacion(simulacion.ID_SIMULACION, 28530);

    if (response.data.simStatus) {
      return response.data.simStatus;
    }

    const features = response.data.features?.reverse();

    let geoJson = {
      type: 'FeatureCollection',
      features: features,
    };

    const nombre = `${simulacion.NOMBRE}_${new Date(simulacion.FECHA_CREACION)
      .toLocaleDateString()
      .replaceAll('/', '-')}`;

    // Fichero SHP
    try {
      let options = {
        folder: `Simulacion_${nombre}`,
        types: { polygon: `POLIGONOS_${nombre}` },
        outputType: 'blob',
      };
      const shpZipBlob = await shpwrite.zip(geoJson, options);

      const qml = await fetch('/qgis_styles/SIMULACION.qml');
      if (!qml.ok) throw new Error(`No se pudo cargar SIMULACION.qml (${qml.status})`);
      const qmlText = await qml.text();

      const zip = await JSZip.loadAsync(shpZipBlob);
      zip.file(`${options.folder}/${options.types.polygon}.qml`, qmlText);
      const finalBlob = await zip.generateAsync({ type: 'blob' });

      const a = document.createElement('a');
      a.href = URL.createObjectURL(finalBlob);
      a.download = `${options.folder}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Error generando shp/qml', err);
      throw err;
    }

    // Fichero KML
    try {
      let kml = toKML(geoJson);
      let element = document.createElement('a');
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(kml));
      element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(kml)}`);
      element.setAttribute('download', `Simulacion_${nombre}.kml`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (err) {
      console.error('Error generando kml', err);
      throw err;
    }
  }
};

export const exportSimulacionBack = async (simulacion, responseType, fileType) => {
  if (!map) return;

  const fecha = DateService.toString(DateService.getCurrentDayJs(), FMT.onlyDate).replaceAll('/', '-');
  const nombreSim = simulacion.NOMBRE || 'SinNombre';

  try {
    const fechas = [];
    if (simulacion.TIPO === 'VIENTO') {
      for (let i = 0; i <= simulacion.NUM_HORAS; i++) {
        const horaSim = VueInst.$date
          .toDayjs(simulacion.FECHA_INICIO)
          .utc()
          .add(i, 'hours')
          .format(FMT.simDate);
        const fechaLimpia = `${horaSim.split('+')[0]}Z`;
        fechas.push(fechaLimpia);
      }
    }

    const response = await store.dispatch('simulacion/exportSimulacion', {
      idSimulacion: simulacion.ID_SIMULACION,
      tipo: simulacion.TIPO,
      fechas,
      responseType,
      fileType,
    });

    if (response.status === 204 || response.data?.simStatus === 406 || response.data?.simStatus === 404) {
      return response.data?.simStatus || response.status;
    }

    switch (responseType) {
      case 'blob': {
        handleBlobDownload(response.data, response.headers, nombreSim, fecha);
        break;
      }
      case 'json': {
        const downloadUrl = response.data.downloadUrl;

        if (document.hasFocus()) {
          navigator.clipboard.writeText(downloadUrl);
        } else {
          console.warn('No se puede copiar URL de simulacion. La pestaña no está en foco');
        }
        return downloadUrl;
      }
      default: {
        console.error('Parámetro responseType mal configurado');
        throw new Error(`Parámetro responseType mal configurado: ${responseType}`);
      }
    }
  } catch (error) {
    if (error.response?.status === 406 || error.response?.status === 404) return error.response?.status;
    console.error('Error exportando simulación:', error);
    return 500;
  }
};

// Funcion para exportar simulacion a pdf
export const paintSimulacionFinal = async (simulacion) => {
  if (map) {
    const layer = map.findLayerById('layer-simulacion');

    try {
      const response = await api.simulacion.getSimulacion(simulacion.ID_SIMULACION, 28530);

      if (response.data.simStatus) {
        // Hacer que salga error cuando el simStatus no sea correcto
        return response.data.simStatus;
      }

      const graphics = await createGraphicsIsocronas(response.data);
      graphics.sort(comparador);

      simulationGraphics.simulacion = graphics;

      layer.graphics.items = simulationGraphics.simulacion;
      await mapView.goTo(simulationGraphics.simulacion, {
        duration: 3000,
        easing: 'in-expo',
      });
      layer.visible = true;
    } catch (err) {
      console.error('[ArcGIS.js] Error paintSimulacionFinal', err);
      throw err;
    }
  }
};

/**
 * @param {*} from => [r, g, b, a]
 * @param {*} to => [r, g, b, a]
 * @param {*} steps => n
 */
function getColorGradient(from, to, totalNumberOfColors) {
  if (totalNumberOfColors === 0) {
    return [[0, 0, 0]];
  }
  if (totalNumberOfColors === 1) {
    return [from];
  }
  if (totalNumberOfColors === 2) {
    return [from, to];
  }

  const diffR = to[0] - from[0];
  const diffG = to[1] - from[1];
  const diffB = to[2] - from[2];
  // let diffA = to[3] - from[3]

  const steps = totalNumberOfColors - 1;

  const stepR = diffR / steps;
  const stepG = diffG / steps;
  const stepB = diffB / steps;
  // let stepA = diffA / steps

  const gradient = [];
  gradient.push(from);
  for (let i = 1; i < steps; ++i) {
    const r = Math.round(from[0] + stepR * i);
    const g = Math.round(from[1] + stepG * i);
    const b = Math.round(from[2] + stepB * i);
    // let a = Math.round(from[3] + stepA * i)

    gradient.push([r, g, b]);
  }
  gradient.push(to);

  return gradient;
}
// #endregion

// #region VIENTOS
export const paintViento = async (simulacion) => {
  const intervalo = simulacion.time ? simulacion.time : 0;
  if (map) {
    try {
      let response;
      let velocidades;

      try {
        let aux = null;

        if (simulacion.sim.TIPO === 'VIENTO') {
          const horaSim = DateService.toDayjs(simulacion.sim.FECHA_INICIO)
            .utc()
            .add(intervalo, DayjsUnit.Minute)
            .format(FMT.simDate);
          const fechaLimpia = `${horaSim.split('+')[0]}Z`;
          aux = await api.simulacion.getSimulacionWind(simulacion.sim.ID_SIMULACION, fechaLimpia);

          simulationGraphics.simulacion = {
            x: aux.data.features[0].geometry.coordinates[0],
            y: aux.data.features[0].geometry.coordinates[1],
          };
        } else {
          const horaSim = DateService.toDayjs(simulacion.sim.FECHA_INICIO)
            .utc()
            .add(intervalo, DayjsUnit.Minute)
            .format(FMT.simulationWithWind);
          aux = await api.simulacion.getJsonWindNinja(simulacion.sim.ID_SIMULACION, horaSim);
        }

        response = aux.data;
        velocidades = {
          maxSpeed: response.maxSpeed,
          minSpeed: response.minSpeed,
        };
      } catch (error) {
        console.error('[ArcGIS.js] Error: paint viento', error);
        throw error;
      }

      store.dispatch('shareMap/paintViento', response);

      await paintVientoLayer(response);
      return velocidades;
    } catch (err) {
      console.error('[ArcGIS.js] Error: paint viento', err);
      throw err;
    }
  }
};

export const paintVientoLayer = async (response) => {
  const is3D = store.getters['map/isMap3D'];
  const mode = store.getters['map/wind3DMode'];

  maxSpeed = Number(response.maxSpeed);
  minSpeed = Number(response.minSpeed);

  const pointLayer = map.findLayerById('layer-viento');
  const lineLayer = map.findLayerById('layer-viento-lineas');

  const graphicsVientos = await featuresVientoLines(response.features);

  const { features: oldFeaturesVientos } = await lineLayer.queryFeatures();

  await lineLayer.applyEdits({
    addFeatures: graphicsVientos,
    deleteFeatures: oldFeaturesVientos,
  });
  lineLayer.visible = false;

  const graphics = await featuresVientos(response.features);

  const { features: oldFeatures } = await pointLayer.queryFeatures();

  await pointLayer.applyEdits({
    addFeatures: graphics,
    deleteFeatures: oldFeatures,
  });
  pointLayer.visible = true;

  if (!is3D) {
    pointLayer.renderer = await getWindRenderer2D();
  } else if (mode === 'objetos') {
    pointLayer.renderer = await getWindRenderer3D();
  } else {
    lineLayer.renderer = await getWindLineRenderer3D();
    lineLayer.visible = true;
    pointLayer.visible = false;
  }
};

async function featuresVientos(features) {
  const [Graphic, Point] = await loadModules(['esri/Graphic', 'esri/geometry/Point']);

  const graphics = [];

  features.forEach((element) => {
    const symbol = {
      type: 'simple-marker',
      style: 'triangle',
      outline: {
        color: 'yellow',
        width: '3px',
      },
    };

    const geometry = new Point({
      x: element.geometry.coordinates[0],
      y: element.geometry.coordinates[1],
    });

    const graphic = new Graphic({
      attributes: element.properties,
      geometry,
      symbol,
    });

    graphics.push(graphic);
  });

  return graphics;
}

async function featuresVientoLines(features) {
  const [Graphic, Polyline] = await loadModules(['esri/Graphic', 'esri/geometry/Polyline']);

  const graphics = [];

  features.forEach((element) => {
    const coords = element.geometry.coordinates;
    const direction = element.properties.direction;

    const arrowGeometry = buildArrowPolyline(coords, direction);

    const geometry = new Polyline({
      paths: arrowGeometry.paths,
      spatialReference: { wkid: 4326 },
    });

    const graphic = new Graphic({
      attributes: element.properties,
      geometry,
    });

    graphics.push(graphic);
  });

  return graphics;
}

function buildArrowPolyline(coords, direction, length = 0.0012) {
  const [lon, lat] = coords;

  const drawDirection = (direction + 180) % 360;
  const rad = ((90 - drawDirection) * Math.PI) / 180;

  const dx = Math.cos(rad) * length;
  const dy = Math.sin(rad) * length;

  const headLon = lon + dx;
  const headLat = lat + dy;

  const sideLen = length * 0.35;
  const leftRad = rad + Math.PI / 6;
  const rightRad = rad - Math.PI / 6;

  const leftLon = headLon - Math.cos(leftRad) * sideLen;
  const leftLat = headLat - Math.sin(leftRad) * sideLen;

  const rightLon = headLon - Math.cos(rightRad) * sideLen;
  const rightLat = headLat - Math.sin(rightRad) * sideLen;

  return {
    paths: [
      [
        [lon, lat],
        [headLon, headLat],
      ],
      [
        [headLon, headLat],
        [leftLon, leftLat],
      ],
      [
        [leftLon, leftLat],
        [headLon, headLat],
      ],
      [
        [headLon, headLat],
        [rightLon, rightLat],
      ],
    ],
  };
}

export async function updateRendererViento() {
  const is3D = store.getters['map/isMap3D'];
  const mode = store.getters['map/wind3DMode'];

  const pointLayer = map.findLayerById('layer-viento');
  const lineLayer = map.findLayerById('layer-viento-lineas');

  if (pointLayer && lineLayer) {
    if (!is3D) {
      pointLayer.visible = true;
      lineLayer.visible = false;
      pointLayer.renderer = await getWindRenderer2D();
      return;
    }

    if (mode === 'objetos') {
      pointLayer.visible = true;
      lineLayer.visible = false;
      pointLayer.renderer = await getWindRenderer3D();
    } else {
      pointLayer.visible = false;
      lineLayer.visible = true;
      lineLayer.renderer = await getWindLineRenderer3D();
    }
  }
}
// #endregion VIENTOS

// #region IMAGEN SATELITE

export const drawImagenSatelite = async (data, url, bbox) => {
  if (map) {
    const layer = map.findLayerById('layer-imagen-satelite');

    const graphic = await createGraphicsImagenSateliteWMS(data, url, bbox);

    layer.source.elements.removeAll(); // Para que solo haya una imagen cada vez
    await layer.source.elements.add(graphic);

    layer.visible = true;
  }
};

// Funcion para introducir valores en la capa de imagen satelite para la implementación WFSWMS
async function createGraphicsImagenSateliteWMS(url, bbox) {
  try {
    const [ImageElement, ExtentAndRotationGeoreference, Extent] = await loadModules([
      'esri/layers/support/ImageElement',
      'esri/layers/support/ExtentAndRotationGeoreference',
      'esri/geometry/Extent',
    ]);

    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.src = url;

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r < 50 && g < 50 && b > 150) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);

    const source = new ImageElement({
      image: canvas,
      georeference: new ExtentAndRotationGeoreference({
        extent: new Extent({
          spatialReference: {
            wkid: 3857,
          },
          xmin: bbox.xmin,
          ymin: bbox.ymin,
          xmax: bbox.xmax,
          ymax: bbox.ymax,
        }),
      }),
    });

    return source;
  } catch (error) {
    // console.error('Error al crear la capa de imagen satelital: ', error)
    VueInst.$log.error('Error al crear la capa de imagen satelital: ', error);
  }
}

export const hideImagenSatelite = () => {
  if (map) {
    const layer = map.findLayerById('layer-imagen-satelite');
    layer.source.elements.removeAll();
    layer.visible = false;
  }
};

export const generarBbox3857 = (latitud, longitud) => {
  const coordenadas = [longitud, latitud];

  const proyeccion = proj4(proj4.defs('EPSG:4326'), proj4.defs('EPSG:3857'), coordenadas);

  const offset = 10000; // Un buffer de 1000 metros alrededor del punto central
  const bbox = {
    xmin: proyeccion[0] - offset,
    ymin: proyeccion[1] - offset,
    xmax: proyeccion[0] + offset,
    ymax: proyeccion[1] + offset,
  };

  return {
    xmin: bbox.xmin,
    ymin: bbox.ymin,
    xmax: bbox.xmax,
    ymax: bbox.ymax,
  };
};

// #endregion

// #region CONVERT_COORDINATES

/**
 * Calcula el huso UTM (1–60) a partir de una longitud en grados.
 * Fórmula: floor((lon + 180) / 6) + 1, acotado a [1, 60].
 * @param {number|string} lon - Longitud en grados (WGS84/ETRS89). Acepta string numérico.
 * @returns {number} Huso UTM entre 1 y 60.
 */
export const utmZoneFromLon = (lon) => {
  const z = Math.floor((Number(lon) + 180) / 6) + 1;
  return Math.max(1, Math.min(60, z));
};

/**
 * Devuelve el hemisferio UTM ("N" o "S") según la latitud.
 *
 * @param {number} lat - Latitud en grados decimales.
 * @returns {"N"|"S"} Hemisferio UTM.
 */
export function utmHemisphereFromLat(lat) {
  if (lat >= 0) return 'N';
  return 'S';
}

/**
 * Convierte Lat/Lon (EPSG:4326) a ETRS89/UTM en el huso indicado.
 * Usa elipsoide GRS80 (equivalente a ETRS89).
 * @param {number|string} lat - Latitud en grados.
 * @param {number|string} lon - Longitud en grados.
 * @param {number} zone - Huso UTM (1–60).
 * @returns {[number, number]} Coordenadas [X, Y] en metros (ETRS89/UTM).
 */
export const latLonToEtrs89Utm = (lat, lon, zone) => {
  const firstProjection = 'EPSG:4326';
  const secondProjection = `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs`;
  return proj4(firstProjection, secondProjection, [Number(lon), Number(lat)]);
};

export const convertir4326to3857Mercator = async (lon, lat) => {
  const [Point, webMercatorUtils] = await loadModules([
    'esri/geometry/Point',
    'esri/geometry/support/webMercatorUtils',
  ]);

  const point4326 = new Point({
    longitude: lon,
    latitude: lat,
    spatialReference: { wkid: 4326 },
  });

  const point3857 = await webMercatorUtils.geographicToWebMercator(point4326);
  return [point3857.toJSON().x, point3857.toJSON().y];
};

export const convertirMercXYToETRS89UTM30N = (x, y) => {
  const firstProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 ' +
    '+x_0=0.0 +y_0=0 +units=m +k=1.0 +nadgrids=@null +no_defs';

  const secondProjection = '+proj=utm +zone=30 +ellps=GRS80 +units=m +no_defs';

  return proj4(firstProjection, secondProjection, [parseFloat(x), parseFloat(y)]);
};

export const convertirGMSLatLon = (gmsLat, gmsLon) => {
  const partsLat = gmsLat.split(/[^\d\w]+/);
  const partsLon = gmsLon.split(/[^\d\w]+/);
  const lat = convertirDMSToDD(
    Number(partsLat[0]),
    Number(partsLat[1]),
    Number(`${partsLat[2]}.${partsLat[3]}`),
    partsLat[4]
  );
  const lng = convertirDMSToDD(
    Number(partsLon[0]),
    Number(partsLon[1]),
    Number(`${partsLon[2]}.${partsLon[3]}`),
    partsLon[4]
  );
  return [lat, lng];
};

function convertirDMSToDD(degrees, minutes, seconds, direction) {
  let dd = degrees + minutes / 60 + seconds / (60 * 60);

  if (direction === 'S' || direction === 's' || direction === 'W' || direction === 'w') {
    dd = dd * -1;
  } // Don't do anything for N or E
  return dd;
}

export const convertirLatLonGMS = (valor, tipo) => {
  let grados = Math.abs(parseInt(valor, 10));
  let minutos = (Math.abs(valor) - grados) * 60;
  let segundos = minutos;
  minutos = Math.abs(parseInt(minutos, 10));
  segundos = Math.round((segundos - minutos) * 60 * 1000000) / 1000000;
  const signo = valor < 0 ? -1 : 1;
  const direccion = tipo === 'LATITUDE' ? (signo > 0 ? 'N' : 'S') : signo > 0 ? 'E' : 'W';

  if (isNaN(direccion)) {
    grados = grados * signo;
  }

  return {
    grados,
    minutos,
    segundos,
    direccion,
    valor: `${grados}º ${minutos.toString().padStart(2, '0')}' ${segundos
      .toFixed(2)
      .toString()
      .padStart(5, '0')
      .replace('.', ',')}" ${isNaN(direccion) ? ` ${direccion}` : ''}`,
  };
};

export const convertirETRS89 = (x, y) => {
  const zone = constants.HUSO_UTM;
  const firstProjection = 'EPSG:3857';
  const secondProjection = `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs `;
  return proj4(firstProjection, secondProjection, [parseFloat(x), parseFloat(y)]);
};

export const convertirLatLonToETRS89 = (lat, lon) => {
  const zone = constants.HUSO_UTM;
  const firstProjection = 'EPSG:4326';
  const secondProjection = `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs `;
  return proj4(firstProjection, secondProjection, [parseFloat(lon), parseFloat(lat)]);
};

export const convertirETRS89ToLatLon = (x, y) => {
  const zone = constants.HUSO_UTM;
  const firstProjection = `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs`;
  const secondProjection = 'EPSG:4326';
  return proj4(firstProjection, secondProjection, [parseFloat(x), parseFloat(y)]);
};

export const convertirETRS89UTM30NToMercXY = (x, y) => {
  const firstProjection = '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs';

  const secondProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs';

  return proj4(firstProjection, secondProjection, [parseFloat(x), parseFloat(y)]);
};

export const convertirMercXYToLatLon = (x, y) => {
  const firstProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +units=m +k=1.0 +nadgrids=@null +no_defs';
  const secondProjection = 'EPSG:4326';
  return proj4(firstProjection, secondProjection, [parseFloat(x), parseFloat(y)]);
};

export const transformarGeoJsonAEPSG4326 = (geoJson) => {
  return {
    ...geoJson,
    features: geoJson.features.map((feature) => {
      if (feature.geometry.type === 'Polygon') {
        const nuevasCoordenadas = feature.geometry.coordinates.map((ring) =>
          ring.map(([x, y]) => convertirMercXYToLatLon(x, y))
        );

        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: nuevasCoordenadas,
          },
        };
      }

      return feature;
    }),
  };
};
// #endregion

// #region TOKEN
async function getToken() {
  try {
    const tokenData = constants.ArcGISToken;

    const params = new FormData();
    params.append('client_id', tokenData.client_id);
    params.append('client_secret', tokenData.client_secret);
    params.append('grant_type', tokenData.grant_type);
    const response = await VueInst.$http.post(tokenData.urlToken, params);

    return response.data.access_token;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
async function registerToken() {
  const [IdentityManager] = await loadModules(['esri/identity/IdentityManager']);
  const urlServices = constants.ArcGISToken.serverLayer;
  if (IdentityManager.findCredential(urlServices) === undefined) {
    const accessToken = await getToken();
    await IdentityManager.registerToken({
      server: constants.ArcGISToken.serverLayer,
      token: accessToken,
    });
  }
}

export const ensureArcGISToken = registerToken;
// #region MAP MOVEMENT
export const setBaseMap = async (basemap) => {
  try {
    await viewSwitchInFlight;

    const type = basemap.BASE_MAPA_TIPO?.NOMBRE;
    if (type === 'basemap') {
      await applyWellKnownBasemap(basemap);
      return true;
    }

    const baseLayer = await createCustomBaseLayer(type, basemap);
    if (!baseLayer) {
      VueInst.$log.warn('Tipo de basemap no soportado:', type);
      return false;
    }

    await baseLayer.load();
    if (type === 'wmts') {
      const tileMatrixSet = selectWebMercatorTileMatrixSet(baseLayer);
      await snapTileInfoOriginToWebMercator(tileMatrixSet.tileInfo);
    }

    await applyCustomBasemap(basemap, baseLayer);
    return true;
  } catch (err) {
    VueInst.$log.error(err);
    notifyBaseMapLoadError(basemap.LABEL);
    return false;
  }
};

const CUSTOM_BASE_LAYER_MODULES = {
  webtile: 'esri/layers/WebTileLayer',
  wms: 'esri/layers/WMSLayer',
  wmts: 'esri/layers/WMTSLayer',
  'map-image': 'esri/layers/MapImageLayer',
  feature: 'esri/layers/FeatureLayer',
  imagery: 'esri/layers/ImageryLayer',
};

function buildCustomBaseLayerProperties(type, basemap) {
  switch (type) {
    case 'webtile':
      return { urlTemplate: basemap.URL };
    case 'wms':
      return {
        url: basemap.URL,
        sublayers: (basemap.SUBLAYERS || []).map((sublayer) => ({
          name: sublayer.NOMBRE,
        })),
        version: '1.3.0',
      };
    case 'wmts':
      return { url: basemap.URL, serviceMode: 'KVP' };
    default:
      return { url: basemap.URL };
  }
}

async function createCustomBaseLayer(type, basemap) {
  const modulePath = CUSTOM_BASE_LAYER_MODULES[type];
  if (!modulePath) return null;

  const [LayerClass] = await loadModules([modulePath]);
  return new LayerClass(buildCustomBaseLayerProperties(type, basemap));
}

function selectWebMercatorTileMatrixSet(layer) {
  const activeLayer = layer.activeLayer;
  const webMercatorSet = activeLayer?.tileMatrixSets?.find((set) => set.tileInfo?.spatialReference?.isWebMercator);

  if (!webMercatorSet) {
    throw new Error(`WMTS layer ${activeLayer?.id} has no Web Mercator tile matrix set`);
  }

  activeLayer.tileMatrixSetId = webMercatorSet.id;
  return webMercatorSet;
}

const MAX_ORIGIN_SNAP_DISTANCE_METERS = 1;

async function snapTileInfoOriginToWebMercator(tileInfo) {
  const [spatialReferenceUtils, Point] = await loadModules([
    'esri/geometry/support/spatialReferenceUtils',
    'esri/geometry/Point',
  ]);

  const canonicalOrigin = spatialReferenceUtils.getInfo(tileInfo.spatialReference)?.origin;
  if (!canonicalOrigin) return;

  const [x, y] = canonicalOrigin;
  const isNearCanonical =
    Math.abs(tileInfo.origin.x - x) < MAX_ORIGIN_SNAP_DISTANCE_METERS &&
    Math.abs(tileInfo.origin.y - y) < MAX_ORIGIN_SNAP_DISTANCE_METERS;
  if (!isNearCanonical) return;

  tileInfo.origin = new Point({ x, y, spatialReference: tileInfo.spatialReference });
  snapLodOrigins(tileInfo.lods, x, y);
}

function snapLodOrigins(lods, x, y) {
  lods?.forEach((lod) => {
    if (lod.origin != null) {
      lod.origin = [x, y];
    }
  });
}

let pendingBasemapRevert = null;

function rememberCurrentBasemap(label) {
  pendingBasemapRevert = {
    basemap: map.basemap,
    hadHybridReferenceLayer: Boolean(map.findLayerById('hybrid-reference-layer')),
    label,
  };
}

async function applyWellKnownBasemap(basemap) {
  const isHybrid = basemap.URL === 'hybrid';

  rememberCurrentBasemap(basemap.LABEL);
  map.basemap = isHybrid ? 'satellite' : basemap.URL;
  removeHybridReferenceLayer();

  if (isHybrid) {
    await addHybridReferenceLayer();
  }
}

async function applyCustomBasemap(basemap, baseLayer) {
  const [Basemap] = await loadModules(['esri/Basemap']);

  const customBasemap = new Basemap({
    id: basemap.ID_BASE_MAPA,
    title: basemap.LABEL,
    baseLayers: [baseLayer],
  });

  rememberCurrentBasemap(basemap.LABEL);
  map.basemap = customBasemap;
  removeHybridReferenceLayer();
}

function revertBasemapOnLayerViewError(event) {
  const failedChange = pendingBasemapRevert;
  if (!failedChange?.basemap || !map.basemap?.baseLayers?.includes(event.layer)) return;

  pendingBasemapRevert = null;
  VueInst.$log.error(event.error);

  map.basemap = failedChange.basemap;
  removeHybridReferenceLayer();
  if (failedChange.hadHybridReferenceLayer) {
    addHybridReferenceLayer();
  }

  notifyBaseMapLoadError(failedChange.label);
}

function notifyBaseMapLoadError(label) {
  Vue.notify({
    title: i18n.t('map.baseMap'),
    text: i18n.t('map.baseMapLoadError', { label }),
    type: 'error',
  });
}

let hybridReferenceLayer = null;
export const removeHybridReferenceLayer = () => {
  const layer = map.findLayerById('hybrid-reference-layer');

  if (layer) {
    map.remove(layer);
  }

  hybridReferenceLayer = null;
};

export const addHybridReferenceLayer = async () => {
  try {
    const [VectorTileLayer] = await loadModules(['esri/layers/VectorTileLayer']);

    hybridReferenceLayer = new VectorTileLayer({
      id: 'hybrid-reference-layer',

      portalItem: {
        id: '30d6b8271e1849cd9c3042060001f425',
      },
    });

    map.add(hybridReferenceLayer, 0);
  } catch (err) {
    console.error(err);
  }
};

// Metodo nuevo: crea uno al principio en la toolbar del mapa
let searchWidget = null;
export const setSearchWidget = async () => {
  try {
    // Update Locator to match 4.24 API
    const [Search, Extent] = await loadModules(['esri/widgets/Search', 'esri/geometry/Extent']);

    // Filtrar locator por extent de zona
    const paramsComunidad = store.getters['map/paramsComunidad'];
    const extent = paramsComunidad ? new Extent(paramsComunidad.EXTENT) : null;
    const searchFilter = {
      geometry: extent,
    };
    if (!searchWidget) {
      searchWidget = new Search({
        sources: [
          {
            // locator: new Locator({ url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer' }),
            url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer',
            countryCode: 'ES',
            singleLineFieldName: 'SingleLine',
            name: 'Geocoding España',
            localSearchOptions: {
              minScale: 300000,
              distance: 50000,
            },
            filter: searchFilter,
            placeholder: 'Buscar topónimos',
            maxResults: 3,
            maxSuggestions: 6,
            suggestionsEnabled: true,
            minSuggestCharacters: 0,
            enableInfoWindow: false,
            popupEnabled: false,
          },
        ],
        view: mapView,
        container: 'searchWidget',
        includeDefaultSources: false,
        id: 'search',
      });
    }
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
};

export const setZoom = (value) => {
  const newZoom = mapView.zoom + value;
  mapView.goTo({
    target: mapView.center,
    zoom: newZoom,
  });
};
export const setExtent = (extent) => {
  setExtentMap(extent);
};

async function setExtentMap(extent) {
  try {
    const [Extent, SpatialReference] = await loadModules(['esri/geometry/Extent', 'esri/geometry/SpatialReference']);

    const properties = {
      xmin: extent.xmin,
      ymin: extent.ymin,
      xmax: extent.xmax,
      ymax: extent.ymax,
    };

    if (extent.wkid) {
      properties.spatialReference = new SpatialReference({ wkid: extent.wkid });
    } else {
      properties.spatialReference = extent.spatialReference;
    }

    if (extent.viewpoint) {
      extent.viewpoint.targetGeometry.type = 'point';
      mapView.viewpoint = extent.viewpoint;
    }

    mapView.extent = new Extent(properties);
  } catch (err) {
    VueInst.$log.error(err);
  }
}

let gotoRecurso;

export const setCenterMap = async (data) => {
  gotoRecurso = structuredClone(data);
  if (mapView) {
    await mapView.goTo({
      target: data.target,
      zoom: data.zoom,
    });
    gotoRecurso = null;
  }
};

const getVisibleLeftPanelOverlap = () => {
  const mapNode = document.getElementById('mapNode');
  if (!mapNode) {
    return 0;
  }
  const leftDrawer = document.getElementById('map-left-menu-drawer');
  if (!leftDrawer) {
    return 0;
  }
  const mapRect = mapNode.getBoundingClientRect();
  const drawerRect = leftDrawer.getBoundingClientRect();
  const isVisible = drawerRect.width > 0 && drawerRect.height > 0;
  const overlapsMap = drawerRect.left <= mapRect.left && drawerRect.right > mapRect.left;
  if (!isVisible || !overlapsMap) {
    return 0;
  }
  const overlap = Math.min(drawerRect.right, mapRect.right) - Math.max(drawerRect.left, mapRect.left);
  return Math.max(0, overlap);
};

const waitForLayout = () =>
  new Promise((resolve) => {
    requestAnimationFrame(resolve);
  });

const getMapFitConfig = () => {
  const leftPanelOverlap = getVisibleLeftPanelOverlap();
  return {
    expandFactor: 1.4,
    leftPanelOverlap,
  };
};

const centerArcGISGraphicsOnMap = async (graphics) => {
  if (!mapView || !graphics?.length) {
    return;
  }
  try {
    await mapView.when();
    const [Extent, Point, projection] = await loadModules([
      'esri/geometry/Extent',
      'esri/geometry/Point',
      'esri/geometry/projection',
    ]);
    const targetSpatialReference = mapView.spatialReference;
    const validGraphics = graphics.filter((graphic) => graphic?.geometry);
    if (!validGraphics.length) {
      return;
    }
    const extents = [];
    for (const graphic of validGraphics) {
      let geometry = graphic.geometry;
      if (!geometry) {
        continue;
      }
      const geometrySpatialReference = geometry.spatialReference;
      const hasSpatialReference = geometrySpatialReference && targetSpatialReference;
      const sameSpatialReference = hasSpatialReference && geometrySpatialReference.wkid === targetSpatialReference.wkid;
      if (hasSpatialReference && !sameSpatialReference) {
        try {
          geometry = projection.project(geometry, targetSpatialReference);
        } catch (projectionError) {
          VueInst.$log.error(projectionError);
          continue;
        }
      }
      if (!geometry) {
        continue;
      }
      let extent = geometry.extent ? geometry.extent.clone() : null;
      if (geometry.type === 'point' || !extent || (extent.xmin === extent.xmax && extent.ymin === extent.ymax)) {
        const point =
          geometry.type === 'point'
            ? geometry
            : new Point({
                x: geometry.extent?.center?.x,
                y: geometry.extent?.center?.y,
                spatialReference: geometry.spatialReference || targetSpatialReference,
              });
        if (!point || point.x == null || point.y == null) {
          continue;
        }
        const pointPadding = 250;
        extent = new Extent({
          xmin: point.x - pointPadding,
          ymin: point.y - pointPadding,
          xmax: point.x + pointPadding,
          ymax: point.y + pointPadding,
          spatialReference: point.spatialReference || targetSpatialReference,
        });
      }
      extents.push(extent);
    }
    if (!extents.length) {
      return;
    }
    let combinedExtent = extents[0].clone();
    for (let i = 1; i < extents.length; i++) {
      combinedExtent = combinedExtent.union(extents[i]);
    }
    await waitForLayout();
    const { expandFactor, leftPanelOverlap } = getMapFitConfig();
    const expandedExtent = combinedExtent.clone().expand(expandFactor);
    const viewWidth = mapView.width || 0;
    const visibleWidth = Math.max(1, viewWidth - leftPanelOverlap);
    if (leftPanelOverlap <= 0 || viewWidth <= 0 || expandedExtent.width == null) {
      await mapView.goTo(
        {
          target: expandedExtent,
        },
        {
          animate: true,
          duration: 700,
        }
      );
      return;
    }
    const adjustedWidth = (expandedExtent.width * viewWidth) / visibleWidth;
    const centerShift = (adjustedWidth - expandedExtent.width) / 2;
    const adjustedCenterX = expandedExtent.center.x - centerShift;
    const adjustedExtent = new Extent({
      xmin: adjustedCenterX - adjustedWidth / 2,
      ymin: expandedExtent.ymin,
      xmax: adjustedCenterX + adjustedWidth / 2,
      ymax: expandedExtent.ymax,
      spatialReference: expandedExtent.spatialReference,
    });
    await mapView.goTo(
      {
        target: adjustedExtent,
      },
      {
        animate: true,
        duration: 700,
      }
    );
  } catch (error) {
    if (error?.name !== 'view:goto-interrupted' && error?.name !== 'AbortError') {
      VueInst.$log.error('[ArcGIS] centerArcGISGraphicsOnMap: Error al centrar el mapa', error);
    }
  }
};

const centerMapFromGraphicList = async (graphicList, createGraphicsFn) => {
  if (!mapView || !graphicList?.length) {
    return;
  }
  const graphics = await createGraphicsFn(graphicList);
  await centerArcGISGraphicsOnMap(graphics);
};

export const centerGraphicsOnMap = async (graphicList) => {
  await centerMapFromGraphicList(graphicList, createGraphicsDraw);
};

export const centerPerimetroOnMap = async (graphicList) => {
  await centerMapFromGraphicList(graphicList, createGraphicsDrawPerimeter);
};
// #endregion

// #region CAPTURE MAP
/**
 * Captures the map view as a base64 JPEG. When `area` (CSS px, `{x, y, width, height}`) is
 * given, only that sub-rectangle of the view is captured — used by the Plan de Operaciones
 * export to skip the part of the view hidden behind the left menu drawer. Omitting `area`
 * keeps the previous whole-view behaviour unchanged for existing callers.
 *
 * @param {{x:number, y:number, width:number, height:number}} [area]
 * @returns {Promise<string>}
 */
export const captureMap = async (area = null) => {
  try {
    const pixelRatio = 3;

    const screenshot = await mapView.takeScreenshot({
      width: (area?.width ?? mapView.width) * pixelRatio,
      height: (area?.height ?? mapView.height) * pixelRatio,
      format: 'jpg',
      ...(area ? { area } : {}),
    });

    return screenshot.dataUrl;
  } catch (error) {
    console.error('Error al capturar el mapa:', error);
    throw error;
  }
};

export const captureOverviewMap = async () => {
  try {
    const pixelRatio = 3;

    const screenshot = await mapViewOverview.takeScreenshot({
      width: mapViewOverview.width * pixelRatio,
      height: mapViewOverview.height * pixelRatio,
      // area: store.getters['map/paramsComunidad'].RINGS,
      format: 'jpg',
    });
    return screenshot.dataUrl;
  } catch (error) {
    console.error('Error al capturar el mapa', error);
    return null;
  }
};

export const setCenterAndCaptureMap = async (data) => {
  if (mapView) {
    await mapView.goTo({
      target: data.target,
      zoom: data.zoom,
    });
    setTimeout(async () => {
      const image = await captureMap(mapView);
      return image;
    }, 500);
  }
};

export const getCaptureMapFeatures = async (graphics, layer) => {
  try {
    const layerID = map.findLayerById(layer) || null;
    const array = [];

    if (!layerID || layerID.visible !== true) {
      return [];
    }

    if (layerID.type === 'feature') {
      for (const graphic of graphics) {
        const query = layerID.createQuery();
        graphic.geometry = graphic.geometry ? graphic.geometry : JSON.parse(graphic.GEOMETRIA);
        query.spatialRelationship = 'intersects';
        query.returnGeometry = true;
        query.outFields = ['*'];

        try {
          const result = await layerID.queryFeatures(query);

          let coordMin = convertirETRS89(graphic.geometry.xmin, graphic.geometry.ymin);
          let coordMax = convertirETRS89(graphic.geometry.xmax, graphic.geometry.ymax);
          coordMin = convertirETRS89ToLatLon(coordMin[0], coordMin[1]);
          coordMax = convertirETRS89ToLatLon(coordMax[0], coordMax[1]);
          const x = [coordMin[0], coordMax[0]];
          const y = [coordMin[1], coordMax[1]];

          const filteredFeatures = result.features.filter(
            (feature) =>
              feature.geometry.x >= x[0] &&
              feature.geometry.x <= x[1] &&
              feature.geometry.y >= y[0] &&
              feature.geometry.y <= y[1]
          );

          const attr = filteredFeatures.map((feature) => feature.attributes);
          array.push(...attr);
        } catch (err) {
          VueInst.$log.error(err);
        }
      }

      return array;
    } else if (layerID.type === 'graphics') {
      for (const graphic of graphics) {
        if (layerID.id === 'layer-perimetros' && layerID.graphics.length > 0) {
          for (const layerGraphic of layerID.graphics) {
            const intersects = await intersectGraphic(layerGraphic, graphic);
            if (!intersects) continue;
            const resolved = resolvePerimetroStyleForLoad(layerGraphic.attributes, layerGraphic.symbol);
            const datos = buildPerimetroLegendItem(
              layerGraphic.attributes?.LABEL,
              { styleKey: resolved.styleKey, color: resolved.color },
              store.getters['map/symbologyLines']
            );
            if (datos) array.push(datos);
          }
        } else if (layerID.id === 'layer-plan-operaciones' && layerID.graphics.length > 0) {
          for (const layerGraphic of layerID.graphics) {
            const intersects = await intersectGraphic(layerGraphic, graphic);
            if (!intersects) continue;
            const datos = buildPlanOperacionesLegendItem(
              layerGraphic.geometry.type,
              layerGraphic.attributes?.LABEL,
              layerGraphic.symbol?.url
            );
            if (datos) array.push(datos);
          }
        }
      }

      return array;
    } else {
      return [];
    }
  } catch (err) {
    VueInst.$log.error(err);
    return [];
  }
};

export const intersectGraphic = async (layerGraphic, graphic) => {
  const [Extent, Polygon, Polyline, Point, geometryEngine] = await loadModules([
    'esri/geometry/Extent',
    'esri/geometry/Polygon',
    'esri/geometry/Polyline',
    'esri/geometry/Point',
    'esri/geometry/geometryEngine',
  ]);
  let geometry;
  const extent = new Extent({
    xmin: graphic.geometry.xmin,
    ymin: graphic.geometry.ymin,
    xmax: graphic.geometry.xmax,
    ymax: graphic.geometry.ymax,
    spatialReference: { wkid: 102100 },
  });

  if (layerGraphic.geometry.type === 'polygon') {
    geometry = new Polygon({
      rings: layerGraphic.geometry.rings,
      spatialReference: { wkid: 102100 },
    });
  } else if (layerGraphic.geometry.type === 'point') {
    geometry = new Point({
      x: layerGraphic.geometry.x,
      y: layerGraphic.geometry.y,
      spatialReference: { wkid: 102100 },
    });
  } else if (layerGraphic.geometry.type === 'polyline') {
    geometry = new Polyline({
      paths: layerGraphic.geometry.paths,
      spatialReference: { wkid: 102100 },
    });
  } else {
    return false;
  }

  return geometryEngine.intersects(geometry, extent);
};
// #endregion

// #region MEASUREMENT
let measurementTool;
export const measure = async (type) => {
  try {
    const [Measurement] = await loadModules(['esri/widgets/Measurement']);

    if (!measurementTool) {
      // Se inicializa si no lo está ya
      measurementTool = new Measurement({ areaUnit: 'hectares' }); // areaUnit:

      mapView.ui.add(measurementTool, 'bottom-right');
      measurementTool.view = mapView;
    }

    if (type === 'distance') {
      measurementTool.activeTool = mapView.type.toUpperCase() === '2D' ? type : 'direct-line';
    } else if (type === 'area') {
      measurementTool.activeTool = type;
    }
  } catch (err) {
    VueInst.$log.error(err);
  }
};
export const clearMeasurement = () => {
  if (measurementTool) {
    measurementTool.clear();
    measurementTool = null;
  }
};
// #endregion

export const pointInBuffer = async (lat, lon) => {
  try {
    const [Point, GeometryEngine, projection] = await loadModules([
      'esri/geometry/Point',
      'esri/geometry/geometryEngine',
      'esri/geometry/projection',
    ]);

    let point = new Point(lon, lat);
    point = projection.project(point, { wkid: 3857 });
    const buffer = store.getters['map/bufferComunidad'];
    if (!buffer) {
      return true;
    }
    const contains = GeometryEngine.contains(buffer, point);

    return contains;
  } catch (err) {
    VueInst.$log.error(err);
  }
};
// #endregion

// #region COORDS_MAP
export const drawPointCoords = async (lat, lon) => {
  try {
    const [Graphic, Point] = await loadModules(['esri/Graphic', 'esri/geometry/Point']);

    mapView.graphics = [];

    if (lat && lon) {
      const symbol = {
        type: 'simple-marker',
        style: 'cross',
        outline: {
          color: 'yellow',
          width: '3px',
        },
      };

      const geometry = new Point({
        x: lon,
        y: lat,
      });

      const graphic = new Graphic({
        geometry,
        symbol,
      });
      mapView.graphics.add(graphic);
      setTimeout(deletePointCoords, 60 * 1000);
    }
  } catch (err) {
    VueInst.$log.error(err);
  }
};
function deletePointCoords() {
  mapView.graphics = [];
}
// #endregion

// #region SHARE MAP SKETCH VIEW MODEL
let shareMapSVM = null;
export const initShareMapSketchViewModel = async () => {
  try {
    const [SketchViewModel] = await loadModules(['esri/widgets/Sketch/SketchViewModel']);

    const layer = map.findLayerById('layer-drawSketcher');

    shareMapSVM = new SketchViewModel({
      view: mapView,
      layer,
    });

    shareMapSVM.on(['create', 'delete', 'update'], async (evt) => {
      if (evt.state === 'complete' && evt.type === 'create') {
        let graphic;

        const attributes = {
          ID: VueInst.$uuid.createUUID(),
        };

        const color = [255, 100, 100, 0.4]; // REMINDER: si no se pone el color en este formato, al convertirse a JSON el alpha se 'rompe'

        if (evt.graphic.geometry.type === 'multipoint') {
          // eslint-disable-next-line no-unreachable-loop
          for (let i = 0; i < evt.graphic.geometry.points.length; i++) {
            graphic = {
              attributes,
              coordenadas: evt.graphic.geometry.points[i],
              type: 'point',
              symbol: {
                type: shareMapSVM.pointSymbol.type,
                color, // shareMapSVM.pointSymbol.color,
                angle: shareMapSVM.pointSymbol.angle,
                size: shareMapSVM.pointSymbol.size,
                style: shareMapSVM.pointSymbol.style,
              },
            };
            store.dispatch('shareMap/addGraphics', graphic);
          }
        } else if (evt.graphic.geometry.type === 'point') {
          graphic = {
            attributes,
            coordenadas: [evt.graphic.geometry.x, evt.graphic.geometry.y],
            type: evt.graphic.geometry.type,
            symbol: {
              type: shareMapSVM.pointSymbol.type,
              color, // shareMapSVM.pointSymbol.color,
              size: shareMapSVM.pointSymbol.size,
              style: shareMapSVM.pointSymbol.style,
            },
          };
        } else if (evt.graphic.geometry.type === 'polyline') {
          graphic = {
            attributes,
            coordenadas: evt.graphic.geometry.paths[0],
            type: 'polyline',
            symbol: {
              type: shareMapSVM.polylineSymbol.type,
              color, // shareMapSVM.polylineSymbol.color,
              width: shareMapSVM.polylineSymbol.width,
              style: shareMapSVM.polylineSymbol.style,
              join: shareMapSVM.polylineSymbol.join,
              miterLimit: shareMapSVM.polylineSymbol.miterLimit,
            },
          };
        } else if (evt.graphic.geometry.type === 'polygon') {
          graphic = {
            attributes,
            coordenadas: evt.graphic.geometry.rings[0],
            type: 'polygon',
            symbol: {
              type: shareMapSVM.polygonSymbol.type,
              color, // shareMapSVM.polygonSymbol.color,
              style: shareMapSVM.polygonSymbol.style,
              outline: {
                color: 'white',
                width: 0,
              },
            },
          };
        }

        store.dispatch('shareMap/addGraphics', graphic);
      } else if (evt.type === 'delete') {
        store.dispatch('shareMap/deleteGraphics', evt.graphics); // TODO:
      } else if (evt.state === 'complete' && evt.type === 'update') {
        const geometry = JSON.parse(JSON.stringify(evt.graphics[0].geometry));
        geometry.type = evt.graphics[0].geometry.type;

        const g = {
          attributes: evt.graphics[0].attributes,
          geometry,
        };
        store.dispatch('shareMap/updateGraphics', [g]); // TODO:
      }
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
};

export const paintEventShareMap = (event) => {
  const layer = map.findLayerById('layer-drawSketcher');

  const typeEvent = event.function;

  isDrawingArrow = false;
  if (typeEvent === 'cancel') {
    shareMapSVM.cancel();
  } else if (typeEvent === 'reset') {
    layer.removeAll();
    shareMapSVM.reset();

    store.dispatch('shareMap/removeAllGraphics');
  } else {
    shareMapSVM.create(typeEvent);
  }
};

export const resetShareMapLayer = () => {
  const layer = map.findLayerById('layer-drawSketcher');
  layer.removeAll();
  if (shareMapSVM) {
    shareMapSVM.cancel();
  }
};

export const shareMapSimulacion = async (data) => {
  if (map) {
    try {
      if (data.simulacion.id === 'simulacion') {
        if (simulationGraphics.simulacion.length === 0) {
          simulationGraphics.simulacion = await createGraphicsSimulacion(data.response.data);
          simulationGraphics.isocronas = await createGraphicsIsocronas(data.response.data);
          await mergeOriginGraphics(data.simulacion.sim.GRAFICOS);
        }

        if (data.simulacion.sim.GRAFICOS) {
          simulationGraphics.graficosSimulacion =
            simulationGraphics.graficosSimulacion.length > 0
              ? simulationGraphics.graficosSimulacion
              : await createAuxiliarGraphicsSimulacion(data.simulacion.sim.GRAFICOS); // Probar que funcione
        }
      } else {
        simulationGraphics[data.simulacion.id] =
          simulationGraphics[data.simulacion.id].length > 0
            ? simulationGraphics[data.simulacion.id]
            : await createGraphics(data.response.data);
      }

      paintSimulacionLayer(data.simulacion);
    } catch (err) {
      console.error('Error: ', err);
      throw err;
    }
  }
};

function comparador(a) {
  // Texto tiene que estar siempre antes
  if (a.symbol.type === 'text') {
    return 1;
  }

  // En cualquier otro caso, va despues
  return -1;
}

export const drawShareMapGraphics = async (isNotEditingMode) => {
  const graphicsList = store.getters['shareMap/graphics'];
  const graphics = await createGraphicsDraw(graphicsList);

  if (shareMapSVM) {
    shareMapSVM.updateOnGraphicClick = !isNotEditingMode;
  }

  const graphicsSorted = sortGraphics(graphics);

  const layer = map?.findLayerById('layer-drawSketcher');

  if (!layer) {
    console.warn('No existe layer-drawSketcher todavía');
    return;
  }

  layer.removeAll();
  layer.addMany(graphicsSorted);
};
export const stopShareMapSketchViewModel = () => {
  store.dispatch('shareMap/removeAllGraphics');

  shareMapSVM.cancel();
  shareMapSVM.destroy();
};
// #endregion

// #region ALINEACIONES CAMPBELL
let planACSVM = null;

export const initAlineacionesCampbellSketchViewModel = async (data) => {
  try {
    const [SketchViewModel] = await loadModules(['esri/widgets/Sketch/SketchViewModel']);
    const callbackCreate = data.callbackCreate;
    const callbackUpdate = data.callbackUpdate;
    const callbackDelete = data.callbackDelete;

    const layer = map.findLayerById('layer-alineaciones-campbell');

    planACSVM = new SketchViewModel({
      view: mapView,
      layer,
      defaultCreateOptions: { hasZ: false },
    });
    planACSVM.on(['create', 'delete', 'update'], async (evt) => {
      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico

        let geometry;
        let type = evt.graphic.geometry.type;
        let symbol;
        if (evt.graphic.symbol.type === 'text') {
          // Texto
          const defaultSymbol = planACSVM.pointSymbol;

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y];
          type = 'text';
          symbol = {
            type: 'text',
            color: defaultSymbol.arrowColor,
            text: defaultSymbol.text,
            xoffset: 0,
            yoffset: 0,
            haloColor: 'white',
            haloSize: '5px',
            font: {
              size: defaultSymbol.font.size,
              family: 'Arial Unicode MS',
              weight: 'bold',
            },
          };
        } else if (evt.graphic.geometry.type === 'polygon') {
          // Poligonos
          const defaultSymbol = planACSVM.polygonSymbol;

          geometry = evt.graphic.geometry.rings[0];
          symbol = {
            type: defaultSymbol.type,
            color: defaultSymbol.color,
            style: defaultSymbol.style,
            outline: {
              color: defaultSymbol.outline.color,
              width: defaultSymbol.outline.width,
            },
          };
        }

        const graphic = {
          ID_GRAFICOS_CAMPBELL: VueInst.$uuid.createUUID(),
          GEOMETRIA: geometry,
          TIPO: type,
          SIMBOLO: symbol,
        };

        callbackCreate(graphic);
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const geom = evt.graphics[0].geometry;

        let newGeometryGraphic;
        if (geom.type === 'polygon') {
          newGeometryGraphic = geom.rings[0];
        } else {
          newGeometryGraphic = [geom.x, geom.y];
        }

        // geometry.type = graphic.geometry.type
        const newGraphic = {
          ID_GRAFICOS_CAMPBELL: evt.graphics[0].attributes.ID_GRAFICOS_CAMPBELL,
          GEOMETRIA: newGeometryGraphic,
        };

        callbackUpdate(newGraphic);
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICOS_CAMPBELL);
      }
    });
  } catch (err) {
    VueInst.$log.error(err);
  }
};

export const paintEventAlineacionesCampbell = async (event) => {
  let typeEvent = event.function;

  if (typeEvent === 'polygon') {
    planACSVM.polygonSymbol.color = event.color;
    planACSVM.polygonSymbol.outline = {
      color: event.color,
      width: 2,
    };
  } else if (typeEvent === 'text') {
    typeEvent = 'point';
    planACSVM.pointSymbol = {
      type: 'text',
      color: event.color,
      text: event.text,
      xoffset: 0,
      yoffset: 0,
      haloColor: 'white',
      haloSize: '20px',
      font: {
        size: event.size,
        family: 'Arial Unicode MS',
        weight: 'bold',
      },
    };
  }
  planACSVM.create(typeEvent);
};

export const cancelDrawingAlineaciones = () => {
  if (planACSVM) {
    planACSVM.cancel();
  }
};

export const stopAlineacionesCampbellSketchViewModel = () => {
  const layer = map.findLayerById('layer-alineaciones-campbell');
  layer.removeAll();

  store.dispatch('shareMap/stopAlineacionesCampbell');

  if (planACSVM === null) return;
  planACSVM.cancel();
  planACSVM.destroy();
};

async function createGraphicsDrawAlineacionesCampbell(graphicList) {
  try {
    const [Graphic, Point, Polygon, Polyline, SimpleFillSymbol, SimpleLineSymbol, SimpleMarkerSymbol] =
      await loadModules([
        'esri/Graphic',
        'esri/geometry/Point',
        'esri/geometry/Polygon',
        'esri/geometry/Polyline',
        'esri/symbols/SimpleFillSymbol',
        'esri/symbols/SimpleLineSymbol',
        'esri/symbols/SimpleMarkerSymbol',
      ]);

    const graphics = [];

    for (const g of graphicList) {
      let geometry;
      let symbol;
      const geomData = typeof g.GEOMETRIA === 'string' ? JSON.parse(g.GEOMETRIA) : g.GEOMETRIA;
      const simboloData = typeof g.SIMBOLO === 'string' ? JSON.parse(g.SIMBOLO) : g.SIMBOLO;

      switch (g.TIPO.toLowerCase()) {
        case 'polygon':
          geometry = new Polygon({
            hasZ: false,
            hasM: true,
            rings: geomData,
            spatialReference: { wkid: 3857 },
          });
          symbol = new SimpleFillSymbol({
            color: simboloData.color || [230, 102, 124, 0.5],
            style: simboloData.style || 'solid',
            outline: new SimpleLineSymbol({
              color: simboloData.outline?.color || [0, 0, 0, 1],
              width: simboloData.outline?.width || 2,
            }),
          });
          break;

        case 'polyline':
          geometry = new Polyline({
            paths: geomData,
            spatialReference: { wkid: 3857 },
          });
          symbol = new SimpleLineSymbol({
            color: simboloData.color || [0, 0, 0, 1],
            width: simboloData.width || 2,
          });
          break;

        case 'point':
        case 'text':
          geometry = new Point({
            x: geomData[0],
            y: geomData[1],
            spatialReference: { wkid: 3857 },
            hasZ: true,
          });
          symbol = new SimpleMarkerSymbol({
            color: simboloData.color || [0, 0, 0, 1],
            size: simboloData.size || 10,
            style: simboloData.style || 'circle',
          });
          break;

        default:
          console.warn('[ArcGIS] Tipo de gráfico desconocido:', g.TIPO);
          continue;
      }

      graphics.push(
        new Graphic({
          geometry,
          symbol,
          attributes: g.ATRIBUTOS || {
            ID_GRAFICOS_CAMPBELL: g.ID_GRAFICOS_CAMPBELL,
          },
        })
      );
    }

    return graphics;
  } catch (err) {
    VueInst.$log.error(err);
    throw err;
  }
}

export const drawGraphicsAlineacionesCampbell = async (graphicList) => {
  try {
    const layer = map.findLayerById('layer-alineaciones-campbell');
    layer.removeAll();

    await store.dispatch('shareMap/paintAlineacionesCampbell', graphicList);

    const graphics = await createGraphicsDrawAlineacionesCampbell(graphicList);
    const graphicsSorted = sortGraphics(graphics);
    layer.addMany(graphicsSorted);
  } catch (error) {
    console.error('[ArcGIS.js] Error al dibujar alineaciones campbell', error);
    throw error;
  }
};
// #endregion

// #region COMPASS WIDGET
let compass = null;
export const compassWidget = async () => {
  try {
    const [Compass] = await loadModules(['esri/widgets/Compass']);

    if (!compass) {
      // Se inicializa si no lo está ya
      compass = new Compass({
        viewModel: {
          // autocasts as new CompassViewModel()
          view: mapView,
        },
        id: 'compasss',
        container: 'compassWidget',
      });

      // mapView.ui.add(compass, 'bottom-right')
    }
  } catch (err) {
    VueInst.$log.error(err);
  }
};

let legend = null;
function updateWidgetView() {
  const is3D = store.getters['map/isMap3D'];
  if (compass) {
    compass.viewModel = { view: mapView };
  }

  if (legend) {
    legend.viewModel = { view: mapView };
    if (!is3D) {
      mapView.ui.add(legend, 'bottom-right');
    } else {
      mapView.ui.add(legend, 'bottom-right');
    }
  }

  if (planACSVM) {
    planACSVM.view = mapView;
  }

  if (searchWidget) {
    searchWidget.view = mapView;
  }
}
// #endregion

// #region LEGEND WIDGET
export const legendWidget = async () => {
  try {
    const [Legend, Expand] = await loadModules(['esri/widgets/Legend', 'esri/widgets/Expand']);

    if (!legend) {
      // Se inicializa si no lo está ya
      legend = new Expand({
        id: 'legend',
        content: new Legend({
          view: mapView,
          style: 'card', // other styles include 'classic'
          hideLayersNotInCurrentView: true,
        }),
        expandIconClass: 'esri-icon-description',
        view: mapView,
        expanded: false,
      });

      mapView.ui.add(legend, 'bottom-right');
    }
  } catch (err) {
    VueInst.$log.error(err);
  }
};
// #endregion

// #region areasIncendio
function geodesicMetrics(geometryEngine, geometry) {
  return {
    areaM2: Math.abs(geometryEngine.geodesicArea(geometry, 'square-meters')),
    lengthM: geometryEngine.geodesicLength(geometry, 'meters'),
  };
}

export const measurePerimetros = async (graficos) => {
  if (!graficos || graficos.length === 0) return { area: 0, perimetro: 0, x: [], y: [] };
  const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);

  let areaM2 = 0;
  let lengthM = 0;

  const x = [];
  const y = [];

  graficos.forEach((graphic) => {
    graphic.geometry = graphic.geometry ? graphic.geometry : JSON.parse(graphic.GEOMETRIA);
    const metrics = geodesicMetrics(geometryEngine, graphic.geometry);
    areaM2 += metrics.areaM2;
    lengthM += metrics.lengthM;
    x.push(graphic.geometry.xmin);
    x.push(graphic.geometry.xmax);
    y.push(graphic.geometry.ymin);
    y.push(graphic.geometry.ymax);
  });

  const measure = {
    area: Math.round((areaM2 / 10000) * 100) / 100,
    perimetro: Math.round((lengthM / 1000) * 100) / 100,
    x,
    y,
  };

  return measure;
};

// Recolecta recursivamente todos los layer.id del árbol de capas del panel.
// Se usa en el hitTest para identificar capas con showPanel: true sin lista hardcodeada.
function collectPanelLayerIds(tree) {
  const ids = new Set();
  const walk = (nodes) => {
    for (const n of nodes) {
      if (n.layer?.id) ids.add(n.layer.id);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(tree || []);
  return ids;
}

// #region WMS GetFeatureInfo popup

const WMS_FEATURE_INFO_TIMEOUT_MS = 8000;
let wmsFeatureInfoController = null;
let wmsClickSequence = 0;

const WMS_HIGHLIGHT_LAYER_ID = 'layer-wms-highlight';
// Mismo cian que el highlight por defecto de Esri, el que ya usan layerView.highlight() en el resto del mapa
const WMS_HIGHLIGHT_RGB = [0, 255, 255];
const WMS_HIGHLIGHT_POINT_RING_SIZE_PX = 28;

function clearWmsHighlight() {
  map?.findLayerById(WMS_HIGHLIGHT_LAYER_ID)?.removeAll();
}

// Devuelve el id del click actual: el hitTest es asíncrono y un click anterior no debe lanzar su petición tarde.
// También retira el resaltado del feature anterior.
function cancelPendingWmsFeatureInfo() {
  clearWmsHighlight();
  if (wmsFeatureInfoController) {
    wmsFeatureInfoController.abort();
    wmsFeatureInfoController = null;
  }
  wmsClickSequence += 1;
  return wmsClickSequence;
}

// Capas WMS visibles con popupEnabled: true en su config, de arriba a abajo en el mapa.
function collectWmsPopupLayers(tree) {
  const found = [];
  const walk = (nodes) => {
    for (const node of nodes || []) {
      const config = node.layer;
      if (config?.type === 'WMS' && config.popupEnabled === true && config.url && config.layers?.length) {
        const mapLayer = map.findLayerById(config.id);
        if (mapLayer?.visible) found.push({ config, order: map.layers.indexOf(mapLayer) });
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(tree);
  return found.sort((a, b) => b.order - a.order).map((item) => item.config);
}

async function fetchWmsFeatureInfo(config, event, signal) {
  const { extent, width, height, spatialReference } = mapView;
  const url = buildGetFeatureInfoUrl({
    baseUrl: config.url,
    version: config.version,
    layers: config.layers.map((l) => l.name),
    wkid: spatialReference.wkid,
    extent,
    width,
    height,
    x: event.x,
    y: event.y,
  });

  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`WMS GetFeatureInfo ${config.id} respondió HTTP ${response.status}`);
  }
  return parseFeatureInfoResponse(await response.json());
}

function buildWmsHighlightSymbol(geometryType) {
  const [red, green, blue] = WMS_HIGHLIGHT_RGB;
  const outline = { color: [red, green, blue, 1], width: 3 };

  if (geometryType === 'Point' || geometryType === 'MultiPoint') {
    return {
      type: 'simple-marker',
      style: 'circle',
      size: WMS_HIGHLIGHT_POINT_RING_SIZE_PX,
      color: [red, green, blue, 0.1],
      outline,
    };
  }
  if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
    return { type: 'simple-line', color: [red, green, blue, 1], width: 4 };
  }
  return { type: 'simple-fill', color: [red, green, blue, 0.25], outline };
}

async function ensureWmsHighlightLayer() {
  const existing = map.findLayerById(WMS_HIGHLIGHT_LAYER_ID);
  if (existing) return existing;

  const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);
  const layer = new GraphicsLayer({
    id: WMS_HIGHLIGHT_LAYER_ID,
    title: 'Resaltado WMS',
    listMode: 'hide',
    elevationInfo: { mode: 'on-the-ground' },
  });
  map.add(layer);
  return layer;
}

// No dibuja si la geometría falta/no está soportada o si un click posterior ha invalidado la petición
async function drawWmsHighlight(feature, isStale) {
  const geometryJson = geoJsonToEsriJson(feature.geometry, mapView.spatialReference.wkid);
  if (!geometryJson) return;

  const [Graphic, jsonUtils] = await loadModules(['esri/Graphic', 'esri/geometry/support/jsonUtils']);
  const layer = await ensureWmsHighlightLayer();
  if (isStale()) return;

  layer.removeAll();
  layer.add(
    new Graphic({ geometry: jsonUtils.fromJSON(geometryJson), symbol: buildWmsHighlightSymbol(feature.geometryType) })
  );
  map.reorder(layer, map.layers.length - 1);
}

async function showWmsFeatureInfoPopup(event, clickId) {
  if (clickId !== wmsClickSequence) return;
  const candidates = collectWmsPopupLayers(store.getters['layer/layers']);
  if (candidates.length === 0) return;

  const controller = new AbortController();
  wmsFeatureInfoController = controller;
  const timeoutId = setTimeout(() => controller.abort(), WMS_FEATURE_INFO_TIMEOUT_MS);

  try {
    for (const config of candidates) {
      const features = await fetchWmsFeatureInfo(config, event, controller.signal);
      if (wmsFeatureInfoController !== controller) return; // un click posterior ya lo invalidó
      if (features.length === 0) continue;

      // El popup existente muestra un solo resultado: se usa el primero devuelto por GeoServer
      const [first] = features;
      VueInst.$eventHub.$emit('showPopup', {
        layer: config.id,
        attributes: first.properties,
        // Solo puntos: se muestran las coordenadas del click; en polígonos se omiten área/perímetro
        geometry: isPointGeometryType(first.geometryType) ? event.mapPoint : undefined,
        position: { x: event.x, y: event.y + 3 },
      });
      // Un fallo al dibujar el resaltado no debe tumbar el popup ya mostrado
      await drawWmsHighlight(first, () => wmsFeatureInfoController !== controller).catch((err) => {
        VueInst.$log.error('WMS highlight falló', err);
      });
      return;
    }
  } catch (err) {
    // Un click posterior cancela la petición: no es un error. El timeout sí se registra.
    if (err.name === 'AbortError' && wmsFeatureInfoController !== controller) return;
    VueInst.$log.error('WMS GetFeatureInfo falló', err);
  } finally {
    clearTimeout(timeoutId);
    if (wmsFeatureInfoController === controller) wmsFeatureInfoController = null;
  }
}

// #endregion

// #region WEBMAP

export const loadWebMap = async (portalItemId) => {
  const [[WebMap]] = await Promise.all([loadModules(['esri/WebMap']), ensureArcGISToken()]);

  clearWebMap();

  const webmap = new WebMap({ portalItem: { id: portalItemId } });
  await webmap.load();

  // toArray() snapshots before iteration: map.add() removes each layer from
  // webmap.layers, mutating the live .items array and skipping every other layer.
  const addedLayers = [];
  webmap.layers.toArray().forEach((layer) => {
    layer._webmap = true;

    const layerRecursos = map.findLayerById('layer-recursos');
    if (layerRecursos) {
      map.add(layer, map.layers.indexOf(layerRecursos));
    } else {
      map.add(layer);
    }

    addedLayers.push({ id: layer.id, title: layer.title, visible: layer.visible });
  });

  return addedLayers;
};

export const clearWebMap = () => {
  if (!map) return;

  for (let i = map.layers.items.length - 1; i >= 0; i--) {
    if (map.layers.items[i]._webmap === true) map.remove(map.layers.items[i]);
  }
};

let webMapLayerList;

let webMapLayerListWidgets = [];

const OPACITY_PERCENT_MAX = 100;
const OPACITY_PERCENT_STEP = 5;

const roundOpacityPercentToStep = (percent) => Math.round(percent / OPACITY_PERCENT_STEP) * OPACITY_PERCENT_STEP;

// Group children and sublayers are not flagged themselves: an item belongs to the
// webmap when any ancestor was injected by loadWebMap.
const isWebMapListItem = (item) => {
  for (let node = item; node; node = node.parent) {
    if (node.layer?._webmap === true) return true;
  }
  return false;
};

// The panel is built from widget instances instead of the 'legend' shortcut so the
// opacity slider and the legend can share the single panel a ListItem allows.
const buildLayerPanelContent = async (layer) => {
  const [Legend, Slider, reactiveUtils] = await loadModules([
    'esri/widgets/Legend',
    'esri/widgets/Slider',
    'esri/core/reactiveUtils',
  ]);

  const opacitySlider = new Slider({
    min: 0,
    max: OPACITY_PERCENT_MAX,
    steps: OPACITY_PERCENT_STEP,
    values: [roundOpacityPercentToStep(Math.round(layer.opacity * OPACITY_PERCENT_MAX))],
    layout: 'horizontal',
    visibleElements: { labels: false, rangeLabels: true },
  });

  opacitySlider.on(['thumb-drag', 'thumb-change'], (event) => {
    layer.opacity = event.value / OPACITY_PERCENT_MAX;
  });

  const opacityWatchHandle = reactiveUtils.watch(
    () => layer.opacity,
    (opacity) => {
      const roundedPercent = roundOpacityPercentToStep(Math.round(opacity * OPACITY_PERCENT_MAX));
      if (opacitySlider.values[0] !== roundedPercent) {
        opacitySlider.values = [roundedPercent];
      }
    }
  );

  const layerLegend = new Legend({
    view: mapView,
    layerInfos: [{ layer }],
  });

  return { content: [opacitySlider, layerLegend], opacityWatchHandle };
};

/**
 * Builds the WebMap layer tree (groups, sublayers, legend, opacity and drag
 * reordering) inside the given container. Only layers coming from the active
 * WebMap are listed; the application operational layers stay hidden.
 */
export const createWebMapLayerList = async (container, { panelTitle } = {}) => {
  const [LayerList] = await loadModules(['esri/widgets/LayerList']);

  destroyWebMapLayerList();

  const layerList = new LayerList({
    view: mapView,
    container,
    listItemCreatedFunction: async (event) => {
      const { item } = event;

      // The SDK at this version has no filterPredicate, so non WebMap items are hidden.
      if (!isWebMapListItem(item)) {
        item.hidden = true;
        return;
      }

      // Sublayer items are not Layers: their legend and opacity belong to the parent.
      if (typeof item.layer?.when !== 'function') return;

      try {
        await item.layer.when();

        const { content, opacityWatchHandle } = await buildLayerPanelContent(item.layer);

        if (webMapLayerList === layerList) {
          webMapLayerListWidgets.push(...content, { destroy: () => opacityWatchHandle.remove() });
        }

        item.panel = {
          title: panelTitle,
          content,
          className: 'esri-icon-sliders-horizontal',
          open: false,
        };
      } catch (error) {
        VueInst.$log.error(error);
      }
    },
  });

  webMapLayerList = layerList;

  return webMapLayerList;
};

export const destroyWebMapLayerList = () => {
  if (!webMapLayerList) return;

  webMapLayerListWidgets.forEach((widget) => widget.destroy());
  webMapLayerListWidgets = [];

  webMapLayerList.destroy();
  webMapLayerList = null;
};

// #endregion
